import { t, tn, useI18n } from '../../i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FiAlertTriangle,
  FiCoffee,
  FiCopy,
  FiEdit3,
  FiHelpCircle,
  FiLock,
  FiMonitor,
  FiNavigation,
  FiPlus,
  FiRotateCcw,
  FiSearch,
  FiScissors,
  FiSettings,
  FiShield,
  FiSmartphone,
  FiTrash2,
  FiUnlock,
  FiUploadCloud,
  FiUser,
  FiUsers,
  FiX,
  FiZap,
} from 'react-icons/fi';
import {
  dutyBoardService,
  isBoundElsewhere,
  type BreakSlot,
  type BreakSlotAssignment,
  type DutyColour,
  type DutyKind,
  type DutySlot,
  type DutySlotAssignment,
  type GuardLicenceRow,
  type GuardRow,
  type GuardSiteBindingRow,
  type LicenceTypeRow,
  type PatrolRow,
  type PublishedShiftAssignmentRow,
  type PostRow,
  type RosterRuleViolation,
  type SiteRow,
} from '../../services/dutyBoardService';
import {
  MINUTES_PER_DAY,
  formatAbsoluteMinute,
  formatDateKeyLabel,
  formatWeekRange,
  getCoverageDurationMinutes,
  getCurrentDateKeyInTimeZone,
  getSiteCoverageWindow,
  getWeekDates,
  resolveSiteTimeZone,
  resolveTimeInputToAbsoluteMinute,
  shiftDateKey,
  type CoverageWindow,
} from '../../utils/siteScheduling';
import { getAvatarAccentStyle } from '../../utils/avatarAccent';
import AutoScheduleModal from './autoSchedule/AutoScheduleModal';
import ComplianceFixModal from './compliance/ComplianceFixModal';
import PayFlagsCallout from './payFlags/PayFlagsCallout';
import WeekView from './WeekView';
import './DutyBoardPage.css';

const SNAP = 15;
const DRAG_THRESH = 8;
const DEFAULT_START = 480;
const DEFAULT_END = 1020;
const MIN_DURATION = 30;
const CALENDAR_DAY_WINDOW: CoverageWindow = { startMinute: 0, endMinute: MINUTES_PER_DAY };
type GuardUnavailableReasonCode = 'site' | 'licence' | 'published_shift';

interface GuardAvailabilityState {
  isAvailable: boolean;
  reasons: GuardUnavailableReasonCode[];
  reasonText: string;
}

interface GuardAssignmentState {
  canAssign: boolean;
  reasonText?: string;
}

interface DutyCandidateWindow {
  startMinute: number;
  endMinute: number;
  requiredLicenceTypeId: string | null;
  requiredLicenceName: string | null;
}

interface LocalPublishedShiftWindow {
  startMinute: number;
  endMinute: number;
  reason: string;
}

const COLOUR_META: Record<DutyColour, { bg: string; text: string; dot: string; rgb: string }> = {
  sky:   { bg: 'linear-gradient(135deg,rgba(128,178,255,.96),rgba(96,145,255,.82))',   text: '#0a2246', dot: '#60a5fa', rgb: '96, 165, 250' },
  mint:  { bg: 'linear-gradient(135deg,rgba(103,220,188,.96),rgba(71,188,157,.84))',   text: '#082f2b', dot: '#34d399', rgb: '52, 211, 153' },
  amber: { bg: 'linear-gradient(135deg,rgba(255,207,126,.98),rgba(255,178,74,.86))',   text: '#3f2503', dot: '#fbbf24', rgb: '251, 191, 36' },
  rose:  { bg: 'linear-gradient(135deg,rgba(244,145,181,.98),rgba(224,112,153,.86))',  text: '#431127', dot: '#f472b6', rgb: '244, 114, 182' },
  slate: { bg: 'linear-gradient(135deg,rgba(170,182,205,.96),rgba(112,127,156,.86))',  text: '#10192d', dot: '#94a3b8', rgb: '148, 163, 184' },
};

const COLOURS: DutyColour[] = ['sky', 'mint', 'amber', 'rose', 'slate'];

const KIND_LABEL: Record<DutyKind, string> = {
  fixed_post: 'Post',
  patrol: 'Patrol',
  desk: 'Desk',
  mobile: 'Mobile',
  custom: 'Custom',
};

const getDefaultDutyName = (kind: DutyKind) => `Unnamed ${KIND_LABEL[kind]}`;

/** True for names the board generated (incl. the older bare "Post"-style default), so a type change may replace them. */
const isDefaultDutyName = (name: string) => (
  Object.values(KIND_LABEL).some((label) => name === label || name === `Unnamed ${label}`)
);

const KIND_ICON: Record<DutyKind, React.ReactNode> = {
  fixed_post: <FiShield aria-hidden="true" />,
  patrol: <FiNavigation aria-hidden="true" />,
  desk: <FiMonitor aria-hidden="true" />,
  mobile: <FiSmartphone aria-hidden="true" />,
  custom: <FiPlus aria-hidden="true" />,
};

const INSPECTOR_KIND_OPTIONS: DutyKind[] = ['fixed_post', 'patrol', 'desk', 'mobile'];

const getInspectorKindOptions = (currentKind: DutyKind): DutyKind[] => (
  currentKind === 'custom'
    ? [...INSPECTOR_KIND_OPTIONS, 'custom']
    : INSPECTOR_KIND_OPTIONS
);

const snap = (m: number) => Math.round(m / SNAP) * SNAP;
const clamp = (m: number, lo: number, hi: number) => Math.min(Math.max(m, lo), hi);

const snapClamp = (m: number, coverageWindow: CoverageWindow) => (
  clamp(snap(m), coverageWindow.startMinute, coverageWindow.endMinute)
);

const minuteToPercent = (m: number, coverageWindow: CoverageWindow) => (
  ((clamp(m, coverageWindow.startMinute, coverageWindow.endMinute) - coverageWindow.startMinute) / getCoverageDurationMinutes(coverageWindow)) * 100
);

const durationToPercent = (duration: number, coverageWindow: CoverageWindow) => (
  (clamp(duration, 0, getCoverageDurationMinutes(coverageWindow)) / getCoverageDurationMinutes(coverageWindow)) * 100
);

const clientXToMinute = (
  clientX: number,
  rect: DOMRect,
  coverageWindow: CoverageWindow,
): number => {
  const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
  return snapClamp(
    coverageWindow.startMinute + (ratio * getCoverageDurationMinutes(coverageWindow)),
    coverageWindow,
  );
};

const formatMinute = (minute: number, coverageWindow: CoverageWindow): string => (
  formatAbsoluteMinute(minute, {
    showMidnightAs24: coverageWindow.startMinute === 0
      && minute === coverageWindow.endMinute
      && coverageWindow.endMinute === 1440,
  })
);

const getDefaultSlotWindow = (coverageWindow: CoverageWindow): CoverageWindow => {
  const defaultDuration = DEFAULT_END - DEFAULT_START;
  const coverageDuration = getCoverageDurationMinutes(coverageWindow);

  if (coverageDuration <= defaultDuration) {
    return {
      startMinute: coverageWindow.startMinute,
      endMinute: coverageWindow.endMinute,
    };
  }

  let startMinute = Math.max(coverageWindow.startMinute, DEFAULT_START);
  if ((startMinute + defaultDuration) > coverageWindow.endMinute) {
    startMinute = coverageWindow.startMinute;
  }

  return {
    startMinute,
    endMinute: Math.min(startMinute + defaultDuration, coverageWindow.endMinute),
  };
};

/**
 * First free window in a duty row, preferring the space after its last block so the
 * row's "+" drops the new block where the eye expects it. Null when the row is full.
 */
const findFreeRowWindow = (
  rowSlots: DutySlot[],
  coverageWindow: CoverageWindow,
  desiredDuration: number,
): CoverageWindow | null => {
  const occupied = rowSlots
    .map((slot) => ({ start: slot.start_minute, end: slot.end_minute }))
    .sort((a, b) => a.start - b.start);

  const gaps: Array<{ start: number; end: number }> = [];
  let cursor = coverageWindow.startMinute;
  for (const block of occupied) {
    if (block.start > cursor) {
      gaps.push({ start: cursor, end: Math.min(block.start, coverageWindow.endMinute) });
    }
    cursor = Math.max(cursor, block.end);
  }
  if (cursor < coverageWindow.endMinute) {
    gaps.push({ start: cursor, end: coverageWindow.endMinute });
  }

  const usable = gaps.filter((gap) => (gap.end - gap.start) >= MIN_DURATION);
  if (usable.length === 0) return null;

  const lastEnd = occupied.length > 0 ? occupied[occupied.length - 1].end : coverageWindow.startMinute;
  const chosen = usable.find((gap) => gap.start >= lastEnd)
    ?? usable.reduce((widest, gap) => ((gap.end - gap.start) > (widest.end - widest.start) ? gap : widest));

  return {
    startMinute: chosen.start,
    endMinute: Math.min(chosen.start + Math.max(desiredDuration, MIN_DURATION), chosen.end),
  };
};

const isWithinCoverageWindow = (
  startMinute: number,
  endMinute: number,
  coverageWindow: CoverageWindow,
): boolean => (
  startMinute >= coverageWindow.startMinute
  && endMinute <= coverageWindow.endMinute
  && endMinute > startMinute
);

const initials = (name: string): string =>
  name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();

const summarizeUnavailableReason = (availability?: GuardAvailabilityState): string => {
  const reasonText = availability?.reasonText.trim();
  if (!availability || !reasonText) return t('Unavailable');

  const labels: string[] = [];

  if (availability.reasons.includes('site')) {
    labels.push(t('Bound to other sites'));
  }

  if (availability.reasons.includes('licence')) {
    const licenceMatch = reasonText.match(/Missing valid licence:\s*([^.]*)/i);
    const licenceName = licenceMatch?.[1]?.trim();
    labels.push(licenceName ? `Missing ${licenceName}` : 'Missing licence');
  }

  if (availability.reasons.includes('published_shift')) {
    const shiftMatch = reasonText.match(/Already assigned to another published shift:\s*([^.]*)/i);
    const shiftLabel = shiftMatch?.[1]?.trim();
    labels.push(shiftLabel ? `Shift: ${shiftLabel}` : 'Published shift conflict');
  }

  if (labels.length > 0) return labels.join(' · ');
  return reasonText.split('.').find((part) => part.trim())?.trim() ?? 'Unavailable';
};

const getDateKeyDayDelta = (baseDateKey: string, compareDateKey: string): number => {
  const [baseYear, baseMonth, baseDay] = baseDateKey.split('-').map((part) => Number.parseInt(part, 10));
  const [compareYear, compareMonth, compareDay] = compareDateKey.split('-').map((part) => Number.parseInt(part, 10));
  const baseUtc = Date.UTC(baseYear, baseMonth - 1, baseDay);
  const compareUtc = Date.UTC(compareYear, compareMonth - 1, compareDay);
  return Math.round((compareUtc - baseUtc) / (24 * 60 * 60 * 1000));
};

const overlapsWindow = (
  startA: number,
  endA: number,
  startB: number,
  endB: number,
): boolean => startA < endB && endA > startB;

const getWindowDateKey = (selectedDateKey: string, absoluteMinute: number): string => {
  const dayDelta = Math.floor(Math.max(absoluteMinute - 1, 0) / MINUTES_PER_DAY);
  return shiftDateKey(selectedDateKey, dayDelta);
};


const getNextDisplayOrder = (slotRows: DutySlot[]): number =>
  slotRows.reduce((maxOrder, slot) => Math.max(maxOrder, slot.display_order), -1) + 1;

const UNASSIGNED_CLIENT_KEY = '__unassigned_client__';

const getSiteClientKey = (site: SiteRow): string => site.client_contract_id ?? UNASSIGNED_CLIENT_KEY;

const getSiteClientName = (site: SiteRow): string => site.client_contract_name?.trim() || 'Unassigned client';



type SlotDragMode = 'move' | 'resize-start' | 'resize-end';
interface SlotInteraction {
  mode: SlotDragMode;
  slotId: string;
  pointerId: number;
  origStart: number;
  origEnd: number;
  pointerMin: number;
  dirty: boolean;
  /* Guard view only: the guard row the drag started on, and the one it is over now. */
  fromGuardId: string | null;
  overGuardId: string | null;
  pointerX: number;
  pointerY: number;
}

interface GuardDrag {
  guardId: string;
  guardName: string;
  pointerId: number;
  startX: number;
  startY: number;
  clientX: number;
  clientY: number;
  activated: boolean;
  overSlotId: string | null;
}


interface EditTimesState {
  slotId: string;
  startValue: string;
  endValue: string;
  error: string;
}

interface DutyRowGroup {
  displayOrder: number;
  primarySlot: DutySlot;
  slots: DutySlot[];
  isCarryover?: boolean;
  sourceDate?: string;
}

interface CarryoverDutySlot extends DutySlot {
  source_slot_id: string;
  source_slot_date: string;
}

interface BreakRowGroup {
  displayOrder: number;
  primaryBreak: BreakSlot;
  breaks: BreakSlot[];
}

interface ClipboardState {
  sourceDate: string;
  siteId: string;
  /* Chosen in the copy prompt — guards ride along only when the user says so. */
  includeAssignments: boolean;
}

type BoardViewMode = 'coverage' | 'guard';

interface GuardRowSegment {
  slot: DutySlot;
  isCarryover: boolean;
  sourceDate: string | null;
}

interface GuardRowConflict {
  id: string;
  leftPct: number;
  widthPct: number;
  title: string;
}

interface GuardBoardRow {
  guard: GuardRow;
  segments: GuardRowSegment[];
  /* Breaks for this guard that sit outside every duty they hold, so they need their own chip. */
  looseBreaks: BreakSlot[];
  conflicts: GuardRowConflict[];
  assignedMinutes: number;
  licenceLabel: string;
}

interface DutyBreakOverlay {
  id: string;
  breakSlotId: string;
  startMinute: number;
  endMinute: number;
  /* The break's own length, which can run wider than the slice visible on this duty. */
  durationMinutes: number;
  isPaid: boolean;
  guardNames: string[];
}

/* The break bar shrinks its label rather than its height, so a short break stays as tall
   and as clickable as the duty it sits on. Widths are measured, not guessed from minutes,
   because the timeline stretches with the window. */
function breakBarLabel(widthPx: number, durationMinutes: number, isPaid: boolean): string {
  /* Rung widths come from measuring the rendered label, plus the bar's 4px padding and
     2px border. A digit is ~5.4px and the "B " prefix ~8.8px at this type size. */
  const minutes = String(durationMinutes);
  const minutesWidth = 6 + (minutes.length * 5.4);
  if (isPaid && widthPx >= 62) return 'PAID BREAK';
  if (widthPx >= 36) return 'BREAK';
  if (widthPx >= minutesWidth + 8.8) return `B ${minutes}`;
  if (widthPx >= minutesWidth) return minutes;
  return 'B';
}

/**
 * What the board is telling the user about this day. 'locked' is the important one:
 * the roster is live for guards, so nothing on the board can be touched until it is unlocked.
 */
type PublicationTone = 'past' | 'empty' | 'draft' | 'update' | 'locked' | 'editing';

interface PublishedRosterSnapshot {
  slots: DutySlot[];
  breakSlots: BreakSlot[];
}

interface PublishWarningState {
  dateKey: string;
  isUpdate: boolean;
  violations: RosterRuleViolation[];
}

function groupRosterViolationsByDate(violations: RosterRuleViolation[]): Record<string, RosterRuleViolation[]> {
  return violations.reduce<Record<string, RosterRuleViolation[]>>((acc, violation) => {
    if (!acc[violation.slot_date]) acc[violation.slot_date] = [];
    acc[violation.slot_date].push(violation);
    return acc;
  }, {});
}

function buildViolationSourceSlotMap(violations: RosterRuleViolation[]): Map<string, RosterRuleViolation[]> {
  const map = new Map<string, RosterRuleViolation[]>();
  violations.forEach((violation) => {
    violation.source_slot_ids.forEach((slotId) => {
      const current = map.get(slotId);
      if (current) current.push(violation);
      else map.set(slotId, [violation]);
    });
  });
  return map;
}

function uniqueViolations(violations: RosterRuleViolation[]): RosterRuleViolation[] {
  const seen = new Set<string>();
  return violations.filter((violation) => {
    const key = violation.id ?? `${violation.rule_code}:${violation.guard_id ?? ''}:${violation.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function violationTitle(violations: RosterRuleViolation[]): string {
  return uniqueViolations(violations).map((violation) => violation.message).join('\n');
}

function buildDutyBreakOverlays(
  slot: DutySlot,
  breakSlots: BreakSlot[],
  focusGuardId?: string | null,
): DutyBreakOverlay[] {
  const dutyGuardIds = new Set(
    slot.assignments
      .filter((assignment) => !focusGuardId || assignment.guard_id === focusGuardId)
      .map((assignment) => assignment.guard_id),
  );
  if (dutyGuardIds.size === 0) return [];

  return breakSlots.flatMap((breakSlot) => {
    if (breakSlot.assignments.length === 0) return [];
    if (!overlapsWindow(slot.start_minute, slot.end_minute, breakSlot.start_minute, breakSlot.end_minute)) return [];

    const matchingGuardNames = breakSlot.assignments
      .filter((assignment) => dutyGuardIds.has(assignment.guard_id))
      .map((assignment) => assignment.guard_name);

    if (matchingGuardNames.length === 0) return [];

    const startMinute = Math.max(slot.start_minute, breakSlot.start_minute);
    const endMinute = Math.min(slot.end_minute, breakSlot.end_minute);
    if (endMinute <= startMinute) return [];

    return [{
      id: `${slot.id}-${breakSlot.id}`,
      breakSlotId: breakSlot.id,
      startMinute,
      endMinute,
      durationMinutes: breakSlot.duration_minutes,
      isPaid: breakSlot.is_paid,
      guardNames: matchingGuardNames,
    }];
  });
}

interface EditTimesModalProps {
  slot: DutySlot;
  startValue: string;
  endValue: string;
  error: string;
  isBusy: boolean;
  onChangeStart: (value: string) => void;
  onChangeEnd: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
}

function EditTimesModal({
  slot,
  startValue,
  endValue,
  error,
  isBusy,
  onChangeStart,
  onChangeEnd,
  onClose,
  onSave,
}: EditTimesModalProps) {
  return (
    <div className="db-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="db-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Edit times for ${slot.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="db-modal__header">
          <div>
            <div className="db-modal__eyebrow">{t('Edit duty times')}</div>
            <h2 className="db-modal__title">{slot.name}</h2>
          </div>
          <button className="db-modal__close" onClick={onClose} aria-label={t('Close')}>x</button>
        </div>

        <div className="db-modal__body">
          <div className="db-modal__summary">
            <span>{t(KIND_LABEL[slot.duty_kind])}</span>
            <span>{t('{assigned}/{required} assigned', {
              assigned: slot.assignments.length,
              required: slot.guards_required,
            })}</span>
          </div>

          <div className="db-modal__fields">
            <label className="db-modal__field">
              <span>{t('Start time')}</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder="08:00"
                value={startValue}
                onChange={(e) => onChangeStart(e.target.value)}
              />
            </label>
            <label className="db-modal__field">
              <span>{t('End time')}</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder="17:00"
                value={endValue}
                onChange={(e) => onChangeEnd(e.target.value)}
              />
            </label>
          </div>

          <p className="db-modal__hint">{t('Use 15-minute increments. The end time can be set to 24:00.')}</p>
          {error && <p className="db-modal__error">{error}</p>}
        </div>

        <div className="db-modal__actions">
          <button type="button" className="db-modal__secondary" onClick={onClose}>
            {t('Cancel')}
          </button>
          <button type="button" className="db-modal__primary" onClick={onSave} disabled={isBusy}>
            {t('Save times')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface AddBreakModalProps {
  onSave: (durationMinutes: number, isPaid: boolean) => void;
  onClose: () => void;
  isBusy: boolean;
}

function AddBreakModal({ onSave, onClose, isBusy }: AddBreakModalProps) {
  const [duration, setDuration] = useState('30');
  const [isPaid, setIsPaid] = useState(false);
  const [error, setError] = useState('');

  const handleSave = () => {
    const mins = parseInt(duration, 10);
    if (isNaN(mins) || mins < 5 || mins > 480) {
      setError(t('Duration must be between 5 and 480 minutes.'));
      return;
    }
    if (mins % 5 !== 0) {
      setError(t('Duration must be in 5-minute increments.'));
      return;
    }
    onSave(mins, isPaid);
  };

  return (
    <div className="db-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="db-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('Add break')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="db-modal__header">
          <div>
            <div className="db-modal__eyebrow">{t('Break')}</div>
            <h2 className="db-modal__title">{t('Add break')}</h2>
          </div>
          <button className="db-modal__close" onClick={onClose} aria-label={t('Close')}>x</button>
        </div>

        <div className="db-modal__body">
          <div className="db-modal__fields">
            <label className="db-modal__field">
              <span>{t('Duration (minutes)')}</span>
              <input
                type="number"
                min={5}
                max={480}
                step={5}
                value={duration}
                autoFocus
                onChange={(e) => { setDuration(e.target.value); setError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSave(); } }}
              />
            </label>
            <label className="db-modal__field db-modal__field--toggle">
              <span>{t('Paid break')}</span>
              <button
                type="button"
                className={`db-toggle ${isPaid ? 'db-toggle--on' : ''}`}
                onClick={() => setIsPaid((p) => !p)}
                role="switch"
                aria-checked={isPaid}
              >
                <span className="db-toggle__knob" />
              </button>
            </label>
          </div>

          <p className="db-modal__hint">{t('Set the break length. You can drag and reposition the break block on the timeline after creating it.')}</p>
          {error && <p className="db-modal__error">{error}</p>}
        </div>

        <div className="db-modal__actions">
          <button type="button" className="db-modal__secondary" onClick={onClose}>
            {t('Cancel')}
          </button>
          <button type="button" className="db-modal__primary" onClick={handleSave} disabled={isBusy}>
            {t('Add break')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface CopyDayModalProps {
  dateLabel: string;
  dutyCount: number;
  blockCount: number;
  assignmentCount: number;
  onCopy: (includeAssignments: boolean) => void;
  onClose: () => void;
}

/** Copying a day is cheap; copying the wrong guards onto it is not — so we ask first. */
function CopyDayModal({
  dateLabel,
  dutyCount,
  blockCount,
  assignmentCount,
  onCopy,
  onClose,
}: CopyDayModalProps) {
  const hasAssignments = assignmentCount > 0;

  return (
    <div className="db-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="db-modal db-modal--copy"
        role="dialog"
        aria-modal="true"
        aria-label={t('Also copy guard assignments?')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="db-modal__header">
          <div>
            <div className="db-modal__eyebrow">{t('Copy {date}', { date: dateLabel })}</div>
            <h2 className="db-modal__title">{t('Also copy guard assignments?')}</h2>
          </div>
          <button className="db-modal__close" onClick={onClose} aria-label={t('Close')}>x</button>
        </div>

        <div className="db-modal__body">
          <div className="db-modal__summary">
            {tn('{count} duty', '{count} duties', dutyCount)}
            {' · '}
            {tn('{count} block', '{count} blocks', blockCount)}
            {' · '}
            {tn('{count} guard assignment', '{count} guard assignments', assignmentCount)}
          </div>

          <div className="db-copychoice">
            <button
              type="button"
              className="db-copychoice__option"
              onClick={() => onCopy(false)}
              autoFocus
            >
              <span className="db-copychoice__icon"><FiCopy aria-hidden="true" /></span>
              <span className="db-copychoice__text">
                <strong>{t('Duties only')}</strong>
                <small>{t('Copies the shape of the day - every block starts unassigned.')}</small>
              </span>
            </button>

            <button
              type="button"
              className="db-copychoice__option db-copychoice__option--primary"
              onClick={() => onCopy(true)}
              disabled={!hasAssignments}
              title={hasAssignments ? undefined : t('This day has no guards assigned yet.')}
            >
              <span className="db-copychoice__icon"><FiUsers aria-hidden="true" /></span>
              <span className="db-copychoice__text">
                <strong>{t('Duties and guards')}</strong>
                <small>
                  {hasAssignments
                    ? tn(
                        'Carries {count} guard assignment over to each day you paste into.',
                        'Carries {count} guard assignments over to each day you paste into.',
                        assignmentCount,
                      )
                    : t('This day has no guards assigned yet.')}
                </small>
              </span>
            </button>
          </div>

          <p className="db-modal__hint">
            {t('Pasted days always arrive as drafts, so nothing reaches guards until you publish.')}
          </p>
        </div>

        <div className="db-modal__actions">
          <button type="button" className="db-modal__secondary" onClick={onClose}>
            {t('Cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface RosterWarningsModalProps {
  dateKey: string;
  violations: RosterRuleViolation[];
  isBusy: boolean;
  onClose: () => void;
  onOverride: () => void;
  onSuggestFixes: () => void;
}

function RosterWarningsModal({
  dateKey,
  violations,
  isBusy,
  onClose,
  onOverride,
  onSuggestFixes,
}: RosterWarningsModalProps) {
  const displayedViolations = uniqueViolations(violations);
  const hasFixableViolations = displayedViolations.some((violation) => violation.rule_code !== 'ero_roster_notice');
  const countryCodes = Array.from(new Set(displayedViolations.map((violation) => violation.country_code).filter(Boolean)));
  const countryLabel = countryCodes.length > 0 ? countryCodes.join(', ') : t('this region');

  return (
    <div className="db-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="db-modal db-modal--warnings"
        role="dialog"
        aria-modal="true"
        aria-label={t('Roster rule warnings')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="db-modal__header">
          <div>
            <div className="db-modal__eyebrow">{t('Roster rules')}</div>
            <h2 className="db-modal__title">{t('Publication blocked')}</h2>
          </div>
          <button className="db-modal__close" onClick={onClose} aria-label={t('Close')}>x</button>
        </div>

        <div className="db-modal__body">
          <p className="db-modal__hint">
            {t('{date} breaches the active {country} roster rules. Review the suggested fixes, or override and keep the warning flagged for admins.', {
              date: formatDateKeyLabel(dateKey),
              country: countryLabel,
            })}
          </p>

          <div className="db-warning-list">
            {displayedViolations.map((violation, index) => (
              <div key={violation.id ?? `${violation.rule_code}-${index}`} className="db-warning-list__item">
                <div className="db-warning-list__head">
                  <span className="db-warning-list__rule">{violation.rule_name}</span>
                  <span className="db-warning-list__code">{violation.rule_code.replace(/_/g, ' ')}</span>
                </div>
                <p>{violation.message}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Fixing is the main way out; overriding the rules is the deliberate exception. */}
        <div className="db-modal__actions db-modal__actions--spread">
          <button type="button" className="db-modal__secondary" onClick={onClose} disabled={isBusy}>
            {t('Back to roster')}
          </button>
          <button type="button" className="db-modal__secondary db-modal__secondary--warning" onClick={onOverride} disabled={isBusy}>
            {t('Override and publish')}
          </button>
          {hasFixableViolations && (
            <button type="button" className="db-modal__primary" onClick={onSuggestFixes} disabled={isBusy}>
              {t('Suggest fixes')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface BreakBlockProps {
  breakSlot: BreakSlot;
  coverageWindow: CoverageWindow;
  interaction: SlotInteraction | null;
  guardDragOver: boolean;
  ruleViolations: RosterRuleViolation[];
  /* Guard view only: the row is already the guard, so the chips are dropped. */
  focusGuardId?: string | null;
  onPointerDown: (mode: SlotDragMode, e: React.PointerEvent) => void;
  onOpenSettings: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

function BreakBlock({
  breakSlot,
  coverageWindow,
  interaction,
  guardDragOver,
  ruleViolations,
  focusGuardId,
  onPointerDown,
  onOpenSettings,
}: BreakBlockProps) {
  const live = interaction?.slotId === breakSlot.id ? interaction : null;
  const visibleStart = Math.max(breakSlot.start_minute, coverageWindow.startMinute);
  const visibleEnd = Math.min(breakSlot.end_minute, coverageWindow.endMinute);
  const startPct = minuteToPercent(visibleStart, coverageWindow);
  const widthPct = durationToPercent(Math.max(visibleEnd - visibleStart, MIN_DURATION), coverageWindow);
  const duration = visibleEnd - visibleStart;
  const isNarrow = duration < 60;
  const guardCount = breakSlot.assignments.length;
  const visibleGuards = breakSlot.assignments.slice(0, 2);
  const extraGuards = Math.max(guardCount - 2, 0);
  const warningTitle = violationTitle(ruleViolations);

  return (
    <div
      className={[
        'db-block db-block--break',
        !isNarrow ? 'db-block--has-actions' : '',
        live ? 'db-block--dragging' : '',
        guardDragOver ? 'db-block--drop-target' : '',
        breakSlot.is_published ? 'db-block--published' : '',
        ruleViolations.length > 0 ? 'db-block--rule-warning' : '',
      ].filter(Boolean).join(' ')}
      style={{
        left: `${startPct}%`,
        width: `${widthPct}%`,
      }}
      data-slot-id={breakSlot.id}
      data-break="true"
    >
      {ruleViolations.length > 0 && (
        <span className="db-block__rule-warning" title={warningTitle}>!</span>
      )}

      <div
        className="db-block__body"
        onPointerDown={(e) => onPointerDown('move', e)}
      >
        {!isNarrow && (
          <span className="db-block__name">
            {breakSlot.is_paid ? 'Paid' : 'Unpaid'} break
          </span>
        )}
        {!focusGuardId && (
        <div className="db-block__guards">
          {visibleGuards.map((a) => (
            <span key={a.id} className="db-block__guard" title={a.guard_name}>
              <span
                className="db-block__avatar"
                style={a.avatar_url ? undefined : getAvatarAccentStyle(a.guard_id || a.guard_name)}
              >
                {a.avatar_url
                  ? <img src={a.avatar_url} alt={a.guard_name} />
                  : initials(a.guard_name)}
              </span>
              {!isNarrow && <span className="db-block__guard-name">{a.guard_name.split(' ')[0]}</span>}
            </span>
          ))}
          {extraGuards > 0 && (
            <span className="db-block__avatar db-block__avatar--more">+{extraGuards}</span>
          )}
          {guardCount === 0 && !isNarrow && (
            <span className="db-block__unassigned">{t('no guard')}</span>
          )}
        </div>
        )}
      </div>

      {!isNarrow && (
        <button
          type="button"
          className="db-block__settings"
          onClick={(e) => { e.stopPropagation(); onOpenSettings(e); }}
          onPointerDown={(e) => e.stopPropagation()}
          title={t('Break actions')}
        >
          <FiSettings aria-hidden="true" />
        </button>
      )}

      {live && (
        <div className="db-block__tooltip">
          {formatMinute(breakSlot.start_minute, coverageWindow)} - {formatMinute(breakSlot.end_minute, coverageWindow)}
        </div>
      )}
    </div>
  );
}

interface DutyBlockProps {
  slot: DutySlot;
  coverageWindow: CoverageWindow;
  interaction: SlotInteraction | null;
  isMenuOpen: boolean;
  guardDragOver: boolean;
  /* Published day that has not been unlocked — the block is read-only and says so. */
  isLocked: boolean;
  routeLabel: string | null;
  breakOverlays: DutyBreakOverlay[];
  ruleViolations: RosterRuleViolation[];
  guardAvatarById: Map<string, string | null>;
  /* Guard view: render the block from one guard's perspective — just their chip, no vacancies. */
  focusGuardId?: string | null;
  /* Measured timeline scale, used to pick how much of the break label fits. */
  pixelsPerMinute: number;
  selectedBreakId: string | null;
  /* Break the rail drag is currently hovering, so the bar can light up rather than the duty. */
  guardDragBreakId: string | null;
  onPointerDown: (mode: SlotDragMode, e: React.PointerEvent) => void;
  onOpenMenu: (e: React.MouseEvent<HTMLElement>) => void;
  onSetRoute?: (e: React.MouseEvent<HTMLElement>) => void;
  onBreakPointerDown: (breakSlotId: string, e: React.PointerEvent) => void;
  onSelectBreak: (breakSlotId: string, e: React.MouseEvent) => void;
}

function DutyBlock({
  slot,
  coverageWindow,
  interaction,
  isMenuOpen,
  guardDragOver,
  isLocked,
  routeLabel,
  breakOverlays,
  ruleViolations,
  guardAvatarById,
  focusGuardId,
  pixelsPerMinute,
  selectedBreakId,
  guardDragBreakId,
  onPointerDown,
  onOpenMenu,
  onSetRoute,
  onBreakPointerDown,
  onSelectBreak,
}: DutyBlockProps) {
  const c = COLOUR_META[slot.colour];
  const live = interaction?.slotId === slot.id ? interaction : null;
  const visibleStart = Math.max(slot.start_minute, coverageWindow.startMinute);
  const visibleEnd = Math.min(slot.end_minute, coverageWindow.endMinute);
  const startPct = minuteToPercent(visibleStart, coverageWindow);
  const widthPct = durationToPercent(Math.max(visibleEnd - visibleStart, MIN_DURATION), coverageWindow);
  const duration = visibleEnd - visibleStart;
  const isNarrow = duration < 60;
  const guardCount = slot.assignments.length;
  const shownAssignments = focusGuardId
    ? slot.assignments.filter((a) => a.guard_id === focusGuardId)
    : slot.assignments;
  const emptySlots = focusGuardId ? 0 : Math.max(0, slot.guards_required - guardCount);
  const visibleGuards = shownAssignments.slice(0, 3);
  const extraGuards = focusGuardId ? 0 : Math.max(guardCount - 3, 0);
  const showGuardName = duration >= 90;
  const hasRouteLabel = (slot.duty_kind === 'patrol' || slot.duty_kind === 'fixed_post') && !!routeLabel && !isNarrow;
  const warningTitle = violationTitle(ruleViolations);
  const noRoute = (slot.duty_kind === 'patrol' || slot.duty_kind === 'fixed_post') && !routeLabel && !!onSetRoute && !isNarrow;

  return (
    <div
      className={[
        'db-dblock',
        live ? 'db-dblock--dragging' : '',
        isLocked ? 'db-dblock--locked' : '',
        guardDragOver ? 'db-dblock--drop-target' : '',
        isMenuOpen ? 'db-dblock--selected' : '',
        ruleViolations.length > 0 ? 'db-dblock--rule-warning' : '',
        focusGuardId ? 'db-dblock--focused' : '',
      ].filter(Boolean).join(' ')}
      style={{
        left: `${startPct}%`,
        width: `${widthPct}%`,
        '--db-block-stripe': c.dot,
        '--db-block-fill': `rgba(${c.rgb}, 0.24)`,
        '--db-block-border': `rgba(${c.rgb}, 0.42)`,
      } as React.CSSProperties}
      data-slot-id={slot.id}
      onClick={(e) => { if (!live) { e.stopPropagation(); onOpenMenu(e); } }}
      onPointerDown={focusGuardId ? (e) => onPointerDown('move', e) : undefined}
    >
      {ruleViolations.length > 0 && (
        <span className="db-block__rule-warning" title={warningTitle}>!</span>
      )}

      {isLocked && (
        <span
          className="db-block__lock"
          title={t('Published and locked - unlock the day to change this block')}
        >
          <FiLock aria-hidden="true" />
        </span>
      )}

      <div className="db-dblock__stripe" style={{ background: c.dot }} />

      <div
        className="db-dblock__handle db-dblock__handle--start"
        onPointerDown={(e) => { e.stopPropagation(); onPointerDown('resize-start', e); }}
      />

      {/* A short block normally hides its head to leave room for the guard chips.
          Guard view has no chips, so the title stays and only the times drop. */}
      {(!isNarrow || focusGuardId) && (
        <div className="db-dblock__head">
          <span className="db-dblock__title">
            {slot.name}
            {hasRouteLabel && <span className="db-dblock__sub"> · {routeLabel}</span>}
          </span>
          {!isNarrow && (
            <span className="db-dblock__time">
              {formatMinute(slot.start_minute, coverageWindow)}–{formatMinute(slot.end_minute, coverageWindow)}
            </span>
          )}
        </div>
      )}

      {!focusGuardId && (
      <div
        className="db-dblock__body"
        onPointerDown={(e) => onPointerDown('move', e)}
      >
        {noRoute && guardCount === 0 ? (
          <button
            type="button"
            className="db-dblock__route-prompt"
            onClick={(e) => { e.stopPropagation(); onSetRoute!(e); }}
          >
            Set {slot.duty_kind === 'patrol' ? 'route' : 'post'}
          </button>
        ) : (
          <div className="db-dblock__slots">
            {visibleGuards.map((a) => {
              const avatarUrl = a.avatar_url ?? guardAvatarById.get(a.guard_id) ?? null;
              return (
                <span key={a.id} className="db-dblock__slot" title={a.guard_name}>
                  <span
                    className="db-dblock__slot-avatar"
                    style={avatarUrl ? undefined : getAvatarAccentStyle(a.guard_id || a.guard_name)}
                  >
                    {avatarUrl ? <img src={avatarUrl} alt={a.guard_name} /> : initials(a.guard_name)}
                  </span>
                  {showGuardName && <span className="db-dblock__slot-name">{a.guard_name.split(' ')[0]}</span>}
                </span>
              );
            })}
            {extraGuards > 0 && <span className="db-dblock__overflow">+{extraGuards}</span>}
            {Array.from({ length: Math.min(emptySlots, 2) }).map((_, i) => (
              <span
                key={`empty-${i}`}
                className={`db-dblock__slot db-dblock__slot--empty${guardCount === 0 ? ' db-dblock__slot--empty-flash' : ''}`}
              >
                <span className="db-dblock__slot-avatar">?</span>
                {showGuardName && <span className="db-dblock__slot-name">{t('Drop guard')}</span>}
              </span>
            ))}
          </div>
        )}
      </div>
      )}

      {breakOverlays.map((overlay) => {
        const left = ((overlay.startMinute - slot.start_minute) / duration) * 100;
        const width = ((overlay.endMinute - overlay.startMinute) / duration) * 100;
        const widthPx = (overlay.endMinute - overlay.startMinute) * pixelsPerMinute;
        return (
          <div
            key={overlay.id}
            className={[
              'db-dblock__break',
              overlay.isPaid ? 'db-dblock__break--paid' : '',
              selectedBreakId === overlay.breakSlotId ? 'db-dblock__break--selected' : '',
              guardDragBreakId === overlay.breakSlotId ? 'db-dblock__break--drop-target' : '',
            ].filter(Boolean).join(' ')}
            style={{ left: `${left}%`, width: `${width}%` }}
            data-slot-id={overlay.breakSlotId}
            data-break="true"
            title={`${overlay.isPaid ? 'Paid break' : 'Break'}: ${overlay.guardNames.join(', ')} ${formatMinute(overlay.startMinute, coverageWindow)}–${formatMinute(overlay.endMinute, coverageWindow)}`}
            onPointerDown={(e) => { e.stopPropagation(); onBreakPointerDown(overlay.breakSlotId, e); }}
            onClick={(e) => { e.stopPropagation(); onSelectBreak(overlay.breakSlotId, e); }}
          >
            <span className="db-dblock__break-label">
              {breakBarLabel(widthPx, overlay.durationMinutes, overlay.isPaid)}
            </span>
          </div>
        );
      })}

      <div
        className="db-dblock__handle db-dblock__handle--end"
        onPointerDown={(e) => { e.stopPropagation(); onPointerDown('resize-end', e); }}
      />

      {live && (
        <div className="db-dblock__tooltip">
          {formatMinute(slot.start_minute, coverageWindow)}–{formatMinute(slot.end_minute, coverageWindow)}
        </div>
      )}
    </div>
  );
}

interface UnavailableGuardsModalProps {
  guards: Array<{ guard: GuardRow; availability: GuardAvailabilityState }>;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onClose: () => void;
}

function UnavailableGuardsModal({
  guards,
  searchValue,
  onSearchChange,
  onClose,
}: UnavailableGuardsModalProps) {
  return (
    <div className="db-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="db-modal db-modal--unavailable"
        role="dialog"
        aria-modal="true"
        aria-label={t('Unavailable guards')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="db-modal__header">
          <div>
            <div className="db-modal__eyebrow">{t('Duty board staffing')}</div>
            <h2 className="db-modal__title">{t('Unavailable guards')}</h2>
          </div>
          <button className="db-modal__close" onClick={onClose} aria-label={t('Close')}>x</button>
        </div>

        <div className="db-modal__body">
          <input
            type="search"
            className="db-modal__search"
            placeholder={t('Search guards')}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            autoFocus
          />

          <p className="db-modal__hint">{t('Hover a guard to see why they are unavailable.')}</p>

          {guards.length === 0 ? (
            <div className="db-modal__empty">{t('No unavailable guards for this view.')}</div>
          ) : (
            <div className="db-unavailable-list">
              {guards.map(({ guard, availability }) => (
                <div
                  key={guard.id}
                  className="db-unavailable-list__row"
                  title={availability.reasonText}
                >
                  <span
                    className="db-unavailable-list__avatar"
                    style={guard.avatar_url ? undefined : getAvatarAccentStyle(guard.id || guard.full_name)}
                  >
                    {guard.avatar_url
                      ? <img src={guard.avatar_url} alt={guard.full_name} />
                      : initials(guard.full_name)}
                  </span>
                  <span className="db-unavailable-list__name">{guard.full_name}</span>
                  <span className="db-unavailable-list__status">{t('Unavailable')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type ViewMode = 'day' | 'week';

interface DutyBoardPageProps {
  /** Holds the payroll permission, so pay flags may show what a premium costs. */
  canSeePay?: boolean;
  onReplayTour?: () => void;
  onNavigateToCreatePatrol?: () => void;
  onNavigateToCreatePost?: () => void;
  navigationTarget?: {
    siteId: string;
    date: string;
    dutyId?: string | null;
    nonce: number;
  } | null;
  onNavigationHandled?: () => void;
}

export default function DutyBoardPage({
  canSeePay = false,
  onReplayTour,
  onNavigateToCreatePatrol,
  onNavigateToCreatePost,
  navigationTarget,
  onNavigationHandled,
}: DutyBoardPageProps = {}) {
  useI18n();
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [guards, setGuards] = useState<GuardRow[]>([]);
  const [guardLicences, setGuardLicences] = useState<GuardLicenceRow[]>([]);
  const [guardSiteBindings, setGuardSiteBindings] = useState<GuardSiteBindingRow[]>([]);
  const [siteLicenceTypes, setSiteLicenceTypes] = useState<LicenceTypeRow[]>([]);
  const [publishedShiftAssignments, setPublishedShiftAssignments] = useState<PublishedShiftAssignmentRow[]>([]);
  const [slots, setSlots] = useState<DutySlot[]>([]);
  const [carryoverSlots, setCarryoverSlots] = useState<CarryoverDutySlot[]>([]);
  const [rosterViolations, setRosterViolations] = useState<RosterRuleViolation[]>([]);
  const [sitePatrols, setSitePatrols] = useState<PatrolRow[]>([]);
  const [sitePosts, setSitePosts] = useState<PostRow[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => getCurrentDateKeyInTimeZone(resolveSiteTimeZone(undefined)));
  const [isLoading, setIsLoading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [inspectorSlotId, setInspectorSlotId] = useState<string | null>(null);
  const [guardRailQuery, setGuardRailQuery] = useState('');
  const [guardRailFilter, setGuardRailFilter] = useState<'all' | 'available' | 'unavailable'>('all');
  const [isGuardsPanelOpen, setIsGuardsPanelOpen] = useState(false);
  /* Trails inspectorSlotId: it keeps the last selection on screen while the panel
     slides out, so closing animates instead of blinking to an empty card. */
  const [inspectorContentId, setInspectorContentId] = useState<string | null>(null);
  const [editTimesState, setEditTimesState] = useState<EditTimesState | null>(null);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [hasPublishedRosterChanges, setHasPublishedRosterChanges] = useState(false);
  const [isConfirmingPublish, setIsConfirmingPublish] = useState(false);
  const [isPublishedEditMode, setIsPublishedEditMode] = useState(false);
  const [publishedEditSnapshot, setPublishedEditSnapshot] = useState<PublishedRosterSnapshot | null>(null);
  const [slotInteraction, setSlotInteraction] = useState<SlotInteraction | null>(null);
  const [guardDrag, setGuardDrag] = useState<GuardDrag | null>(null);

  const [breakSlots, setBreakSlots] = useState<BreakSlot[]>([]);
  const [showAddBreakModal, setShowAddBreakModal] = useState(false);
  const [timelineWidthPx, setTimelineWidthPx] = useState(0);
  const [staffBreakMenuGuardId, setStaffBreakMenuGuardId] = useState<string | null>(null);
  const [newBreakIsPaid, setNewBreakIsPaid] = useState(false);
  const [boardViewMode, setBoardViewMode] = useState<BoardViewMode>('coverage');
  const [manualBoardGuardIds, setManualBoardGuardIds] = useState<string[]>([]);
  const [openGuardRowPickerId, setOpenGuardRowPickerId] = useState<string | null>(null);
  const [isBoardGuardPickerOpen, setIsBoardGuardPickerOpen] = useState(false);

  const [viewMode, setViewMode] = useState<ViewMode>('day');
  const [weekSlots, setWeekSlots] = useState<Record<string, DutySlot[]>>({});
  const [weekRosterViolations, setWeekRosterViolations] = useState<Record<string, RosterRuleViolation[]>>({});
  const [weekLoading, setWeekLoading] = useState(false);
  const [clipboard, setClipboard] = useState<ClipboardState | null>(null);
  const [copyPromptDate, setCopyPromptDate] = useState<string | null>(null);
  const [selectedClientKey, setSelectedClientKey] = useState('');
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [unavailableGuardSearch, setUnavailableGuardSearch] = useState('');
  const [publishWarningState, setPublishWarningState] = useState<PublishWarningState | null>(null);
  /* The day whose rule breaches are being fixed; while set, it stands in for the warnings popup. */
  const [fixModalDateKey, setFixModalDateKey] = useState<string | null>(null);
  const [isAutoScheduleOpen, setIsAutoScheduleOpen] = useState(false);

  const guardDragRef = useRef<GuardDrag | null>(null);
  const hasInitialisedSiteDateRef = useRef(false);
  const trackRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  /* In the guard view one duty can sit on several rows, so per-slot refs are ambiguous.
     Every lane track has identical geometry, so keep one live track to measure against. */
  const laneTrackRef = useRef<HTMLDivElement | null>(null);
  const axisTrackRef = useRef<HTMLDivElement | null>(null);
  const toastTimerRef = useRef<number | null>(null);

  const selectedSite = useMemo(
    () => sites.find((site) => site.id === selectedSiteId) ?? null,
    [selectedSiteId, sites],
  );

  /* Guards bound to other clients or sites: on this board they are unavailable, like a guard without the licence. */
  const guardsBoundElsewhere = useMemo(() => {
    const boundElsewhere = new Set<string>();
    if (!selectedSite) return boundElsewhere;
    guardSiteBindings.forEach((binding) => {
      if (isBoundElsewhere(binding, selectedSite)) boundElsewhere.add(binding.guard_id);
    });
    return boundElsewhere;
  }, [guardSiteBindings, selectedSite]);

  const guardAvatarById = useMemo(() => {
    const map = new Map<string, string | null>();
    guards.forEach((guard) => {
      map.set(guard.id, guard.avatar_url);
    });
    return map;
  }, [guards]);

  const clientOptions = useMemo(() => {
    const clients = new Map<string, string>();
    sites.forEach((site) => {
      const clientKey = getSiteClientKey(site);
      if (!clients.has(clientKey)) {
        clients.set(clientKey, getSiteClientName(site));
      }
    });
    return Array.from(clients.entries())
      .map(([key, name]) => ({ key, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [sites]);

  const filteredSites = useMemo(
    () => sites.filter((site) => getSiteClientKey(site) === selectedClientKey),
    [selectedClientKey, sites],
  );

  const selectedSiteTimeZone = useMemo(
    () => resolveSiteTimeZone(selectedSite),
    [selectedSite],
  );

  const coverageWindow = useMemo(
    () => getSiteCoverageWindow(selectedSite),
    [selectedSite],
  );

  const displayCoverageWindow = CALENDAR_DAY_WINDOW;

  const weekDates = useMemo(() => getWeekDates(selectedDate), [selectedDate]);

  const weekRangeLabel = useMemo(() => formatWeekRange(weekDates), [weekDates]);

  const todayKey = useMemo(
    () => getCurrentDateKeyInTimeZone(selectedSiteTimeZone),
    [selectedSiteTimeZone],
  );

  const isPastDay = selectedDate < todayKey;
  const rosterItemCount = slots.length + breakSlots.length;
  const hasPublishedRosterItems = slots.some((slot) => slot.is_published)
    || breakSlots.some((breakSlot) => breakSlot.is_published);
  const hasDraftRosterItems = slots.some((slot) => !slot.is_published)
    || breakSlots.some((breakSlot) => !breakSlot.is_published);
  const hasPublishedRosterUpdate = hasPublishedRosterChanges || (hasPublishedRosterItems && hasDraftRosterItems);
  const allPublished = rosterItemCount > 0 && !hasDraftRosterItems;
  const isPublishedRosterLocked = !isPastDay && hasPublishedRosterItems && !isPublishedEditMode;
  const isDayEditable = !isPastDay && !isPublishedRosterLocked;
  const pendingWarningsForSelectedDate = publishWarningState?.dateKey === selectedDate
    ? publishWarningState.violations
    : null;
  const displayedRosterViolations = pendingWarningsForSelectedDate ?? rosterViolations;
  const displayedRosterViolationCount = uniqueViolations(displayedRosterViolations).length;
  const overriddenRosterViolationCount = displayedRosterViolations.filter((violation) => violation.is_overridden).length;
  const displayedViolationBySourceSlotId = useMemo(
    () => buildViolationSourceSlotMap(displayedRosterViolations),
    [displayedRosterViolations],
  );

  const selectedSiteCountryCode = selectedSite?.country_code?.trim().toUpperCase() ?? '';
  const selectedSiteRequiredLicenceTypeId = selectedSite?.required_licence_type_id ?? null;

  const publishedShiftQueryRange = useMemo(() => {
    return {
      startDate: shiftDateKey(selectedDate, -1),
      endDate: shiftDateKey(selectedDate, 1),
    };
  }, [selectedDate]);

  const loadWeekSlots = useCallback(async () => {
    if (!selectedSiteId || viewMode !== 'week') return;
    setWeekLoading(true);
    try {
      const [data, violationRows] = await Promise.all([
        dutyBoardService.fetchSlotsRange(
          selectedSiteId,
          weekDates[0],
          weekDates[6],
        ),
        dutyBoardService.fetchRosterRuleViolations(
          selectedSiteId,
          weekDates[0],
          weekDates[6],
        ),
      ]);
      setWeekSlots(data);
      setWeekRosterViolations(groupRosterViolationsByDate(violationRows));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWeekLoading(false);
    }
  }, [selectedSiteId, viewMode, weekDates]);

  const loadBase = useCallback(async () => {
    try {
      const [siteRows, guardRows, guardLicenceRows, guardBindingRows] = await Promise.all([
        dutyBoardService.fetchSites(),
        dutyBoardService.fetchGuards(),
        dutyBoardService.fetchCompanyGuardLicences(),
        // Never the reason the board fails to open: without it every guard reads as bound to nothing.
        dutyBoardService.fetchGuardSiteBindings().catch((bindingError) => {
          console.error('Error loading guard site bindings:', (bindingError as Error).message);
          return [] as GuardSiteBindingRow[];
        }),
      ]);
      setSites(siteRows);
      setGuards(guardRows);
      setGuardLicences(guardLicenceRows);
      setGuardSiteBindings(guardBindingRows);
      if (siteRows.length > 0) {
        const nextSite = siteRows.find((site) => site.id === selectedSiteId) ?? siteRows[0];
        if (!selectedClientKey) {
          setSelectedClientKey(getSiteClientKey(nextSite));
        }
        if (!selectedSiteId) {
          setSelectedSiteId(nextSite.id);
        }
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }, [selectedClientKey, selectedSiteId]);

  const loadSlots = useCallback(async () => {
    if (!selectedSiteId) return;
    setIsLoading(true);
    setError('');
    try {
      const previousDate = shiftDateKey(selectedDate, -1);
      const [rows, previousRows, breakRows, violationRows] = await Promise.all([
        dutyBoardService.fetchSlots(selectedSiteId, selectedDate),
        dutyBoardService.fetchSlots(selectedSiteId, previousDate),
        dutyBoardService.fetchBreakSlots(selectedSiteId, selectedDate),
        dutyBoardService.fetchRosterRuleViolations(selectedSiteId, selectedDate, selectedDate),
      ]);
      setSlots(rows);
      setCarryoverSlots(previousRows
        .filter((slot) => slot.end_minute > MINUTES_PER_DAY)
        .map((slot) => ({
          ...slot,
          id: `carryover:${slot.id}`,
          slot_date: selectedDate,
          start_minute: Math.max(0, slot.start_minute - MINUTES_PER_DAY),
          end_minute: Math.min(MINUTES_PER_DAY, slot.end_minute - MINUTES_PER_DAY),
          source_slot_id: slot.id,
          source_slot_date: previousDate,
        }))
        .filter((slot) => slot.end_minute > slot.start_minute));
      setBreakSlots(breakRows);
      setRosterViolations(violationRows);
      setHasPublishedRosterChanges(false);
      setIsConfirmingPublish(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSiteId, selectedDate]);

  useEffect(() => {
    setManualBoardGuardIds([]);
    setOpenGuardRowPickerId(null);
    setIsBoardGuardPickerOpen(false);
  }, [selectedSiteId, selectedDate]);

  const loadRouteOptions = useCallback(async () => {
    if (!selectedSiteId) return;
    try {
      const [patrolRows, postRows] = await Promise.all([
        dutyBoardService.fetchPatrols(selectedSiteId),
        dutyBoardService.fetchPosts(selectedSiteId),
      ]);
      setSitePatrols(patrolRows);
      setSitePosts(postRows);
    } catch {
      // non-critical — silently ignore
    }
  }, [selectedSiteId]);

  const loadSiteLicenceTypes = useCallback(async () => {
    if (!selectedSiteCountryCode) {
      setSiteLicenceTypes([]);
      return;
    }

    try {
      const rows = await dutyBoardService.fetchLicenceTypes(selectedSiteCountryCode);
      setSiteLicenceTypes(rows);
    } catch {
      setSiteLicenceTypes([]);
    }
  }, [selectedSiteCountryCode]);

  const loadPublishedShiftAssignments = useCallback(async () => {
    try {
      const rows = await dutyBoardService.fetchPublishedShiftAssignments(
        publishedShiftQueryRange.startDate,
        publishedShiftQueryRange.endDate,
      );
      setPublishedShiftAssignments(rows);
    } catch {
      setPublishedShiftAssignments([]);
    }
  }, [publishedShiftQueryRange.endDate, publishedShiftQueryRange.startDate]);

  useEffect(() => {
    void loadBase();
  }, [loadBase]);

  useEffect(() => {
    if (!selectedSite) return;
    const clientKey = getSiteClientKey(selectedSite);
    if (clientKey !== selectedClientKey) {
      setSelectedClientKey(clientKey);
    }
  }, [selectedClientKey, selectedSite]);

  useEffect(() => {
    void loadSlots();
  }, [loadSlots]);

  useEffect(() => {
    void loadRouteOptions();
  }, [loadRouteOptions]);

  useEffect(() => {
    void loadSiteLicenceTypes();
  }, [loadSiteLicenceTypes]);

  useEffect(() => {
    void loadPublishedShiftAssignments();
  }, [loadPublishedShiftAssignments]);

  useEffect(() => {
    void loadWeekSlots();
  }, [loadWeekSlots]);

  useEffect(() => {
    if (!selectedSiteId || navigationTarget || hasInitialisedSiteDateRef.current) return;
    setSelectedDate(getCurrentDateKeyInTimeZone(selectedSiteTimeZone));
    hasInitialisedSiteDateRef.current = true;
  }, [navigationTarget, selectedSiteId, selectedSiteTimeZone]);

  useEffect(() => {
    if (!navigationTarget) return;
    setViewMode('day');
    hasInitialisedSiteDateRef.current = true;
    setSelectedSiteId(navigationTarget.siteId);
    setSelectedDate(navigationTarget.date);
    setHasPublishedRosterChanges(false);
    setIsConfirmingPublish(false);
    setIsPublishedEditMode(false);
    setPublishedEditSnapshot(null);
    setShowAddBreakModal(false);
    setInspectorSlotId(navigationTarget.dutyId ?? null);
    setEditTimesState(null);
    setEditingNameId(null);
    onNavigationHandled?.();
  }, [navigationTarget, onNavigationHandled]);

  useEffect(() => () => {
    if (toastTimerRef.current !== null) {
      window.clearTimeout(toastTimerRef.current);
    }
  }, []);

  const captureRosterSnapshot = useCallback((): PublishedRosterSnapshot => ({
    slots: slots.map((slot) => ({
      ...slot,
      required_licence: slot.required_licence ? { ...slot.required_licence } : null,
      assignments: slot.assignments.map((assignment) => ({ ...assignment })),
    })),
    breakSlots: breakSlots.map((breakSlot) => ({
      ...breakSlot,
      assignments: breakSlot.assignments.map((assignment) => ({ ...assignment })),
    })),
  }), [breakSlots, slots]);

  useEffect(() => {
    setInspectorSlotId(null);
    setEditTimesState(null);
    setEditingNameId(null);
    setShowUnavailableModal(false);
    setUnavailableGuardSearch('');
    setIsGuardsPanelOpen(false);
    setHasPublishedRosterChanges(false);
    setIsConfirmingPublish(false);
    setIsPublishedEditMode(false);
    setPublishedEditSnapshot(null);
    setPublishWarningState(null);
    setRosterViolations([]);
  }, [selectedSiteId, selectedDate]);

  useEffect(() => {
    if (!isGuardsPanelOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsGuardsPanelOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isGuardsPanelOpen]);

  useEffect(() => {
    setStaffBreakMenuGuardId(null);
  }, [inspectorSlotId]);

  useEffect(() => {
    if (inspectorSlotId) {
      setInspectorContentId(inspectorSlotId);
      return undefined;
    }
    /* Outlast the slide-out in DutyBoardPage.css before emptying the card. */
    const timer = window.setTimeout(() => setInspectorContentId(null), 280);
    return () => window.clearTimeout(timer);
  }, [inspectorSlotId]);

  /* Clicking off the duty dismisses the panel, which is what uncovers the timeline
     controls sitting underneath it. Anything that owns its own click is left alone. */
  useEffect(() => {
    if (!inspectorSlotId) return undefined;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest([
        '.db-inspector',
        '.db-dblock',
        '.db-block',
        '.db-modal-overlay',
        '.db-rowmenu',
        '.db-guardpicker',
        '.db-actionbar__pop',
        '.db-staff-card__break-wrap',
        '.db-rail',
        '.db-topbar',
      ].join(', '))) return;
      setInspectorSlotId(null);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [inspectorSlotId]);

  useEffect(() => {
    if (!inspectorSlotId) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setInspectorSlotId(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [inspectorSlotId]);

  useEffect(() => {
    if (!editTimesState) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setEditTimesState(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editTimesState]);

  const isAnyGuardPickerOpen = isBoardGuardPickerOpen
    || openGuardRowPickerId !== null
    || staffBreakMenuGuardId !== null;
  useEffect(() => {
    if (!isAnyGuardPickerOpen) return undefined;

    const closeAll = () => {
      setIsBoardGuardPickerOpen(false);
      setOpenGuardRowPickerId(null);
      setStaffBreakMenuGuardId(null);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeAll();
    };
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.db-rowmenu, .db-lane__addduty, .db-guardpicker, .db-actionbar__pop, .db-staff-card__break-wrap')) return;
      closeAll();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isAnyGuardPickerOpen]);

  const showToast = useCallback((msg: string) => {
    if (toastTimerRef.current !== null) {
      window.clearTimeout(toastTimerRef.current);
    }
    setToast(msg);
    toastTimerRef.current = window.setTimeout(() => {
      setToast('');
      toastTimerRef.current = null;
    }, 2500);
  }, []);

  // ── Copy / Paste / Clear / Week actions ──────────────────────────────

  const restoreRosterSnapshot = useCallback(async (snapshot: PublishedRosterSnapshot) => {
    if (!selectedSiteId) return;

    setIsBusy(true);
    setInspectorSlotId(null);
    setEditTimesState(null);
    setEditingNameId(null);
    setShowAddBreakModal(false);

    try {
      await dutyBoardService.clearDay(selectedSiteId, selectedDate);

      const restoredSlots: DutySlot[] = [];
      for (const slot of snapshot.slots) {
        const restored = await dutyBoardService.createSlot({
          siteId: selectedSiteId,
          slotDate: selectedDate,
          name: slot.name,
          dutyKind: slot.duty_kind,
          colour: slot.colour,
          startMinute: slot.start_minute,
          endMinute: slot.end_minute,
          guardsRequired: slot.guards_required,
          displayOrder: slot.display_order,
          requiredLicenceTypeId: slot.required_licence_type_id,
          isPublished: slot.is_published,
          notes: slot.notes,
        });

        if (slot.patrol_id || slot.post_id) {
          await dutyBoardService.updateSlot(restored.id, {
            patrol_id: slot.patrol_id,
            post_id: slot.post_id,
            is_published: slot.is_published,
          });
        }

        const assignments: DutySlotAssignment[] = [];
        for (const assignment of slot.assignments) {
          assignments.push(await dutyBoardService.assignGuard(restored.id, assignment.guard_id));
        }

        restoredSlots.push({
          ...restored,
          patrol_id: slot.patrol_id,
          post_id: slot.post_id,
          is_published: slot.is_published,
          assignments,
        });
      }

      const restoredBreakSlots: BreakSlot[] = [];
      for (const breakSlot of snapshot.breakSlots) {
        const restored = await dutyBoardService.createBreakSlot({
          siteId: selectedSiteId,
          slotDate: selectedDate,
          durationMinutes: breakSlot.duration_minutes,
          isPaid: breakSlot.is_paid,
          startMinute: breakSlot.start_minute,
          endMinute: breakSlot.end_minute,
          displayOrder: breakSlot.display_order,
        });

        const assignments: BreakSlotAssignment[] = [];
        for (const assignment of breakSlot.assignments) {
          assignments.push(await dutyBoardService.assignGuardToBreak(restored.id, assignment.guard_id));
        }

        await dutyBoardService.updateBreakSlot(restored.id, {
          duration_minutes: breakSlot.duration_minutes,
          is_paid: breakSlot.is_paid,
          is_published: breakSlot.is_published,
          notes: breakSlot.notes,
        });

        restoredBreakSlots.push({
          ...restored,
          duration_minutes: breakSlot.duration_minutes,
          is_paid: breakSlot.is_paid,
          is_published: breakSlot.is_published,
          notes: breakSlot.notes,
          assignments,
        });
      }

      const snapshotItemCount = snapshot.slots.length + snapshot.breakSlots.length;
      const snapshotAllPublished = snapshotItemCount > 0
        && snapshot.slots.every((slot) => slot.is_published)
        && snapshot.breakSlots.every((breakSlot) => breakSlot.is_published);

      if (snapshotAllPublished) {
        const publishResult = await dutyBoardService.publishDay(selectedSiteId, selectedDate, { overrideWarnings: true });
        setRosterViolations(publishResult.violations);
      }

      setSlots(restoredSlots.map((slot) => (
        snapshotAllPublished ? { ...slot, is_published: true } : slot
      )));
      setBreakSlots(restoredBreakSlots.map((breakSlot) => (
        snapshotAllPublished ? { ...breakSlot, is_published: true } : breakSlot
      )));
      setHasPublishedRosterChanges(false);
      setIsConfirmingPublish(false);
      setIsPublishedEditMode(false);
      setPublishedEditSnapshot(null);
      showToast(t('Edits undone - published roster restored'));
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    } finally {
      setIsBusy(false);
    }
  }, [loadSlots, selectedDate, selectedSiteId, showToast]);

  const isHistoricDateKey = useCallback((dateKey: string) => (
    dateKey < todayKey
  ), [todayKey]);

  const ensureEditableDay = useCallback((dateKey = selectedDate): boolean => {
    if (isHistoricDateKey(dateKey)) {
      showToast(t('Historic roster is read-only'));
      return false;
    }

    if (dateKey === selectedDate && isPublishedRosterLocked) {
      showToast(t('Published roster is locked. Press Edit to make changes.'));
      return false;
    }

    return true;
  }, [isHistoricDateKey, isPublishedRosterLocked, selectedDate, showToast]);

  const handleStartPublishedEdit = useCallback(() => {
    if (isPastDay || !hasPublishedRosterItems || isBusy) return;
    setPublishedEditSnapshot(captureRosterSnapshot());
    setIsPublishedEditMode(true);
    setIsConfirmingPublish(false);
    setInspectorSlotId(null);
    setEditTimesState(null);
    setEditingNameId(null);
    showToast(t('Published roster unlocked for editing'));
  }, [captureRosterSnapshot, hasPublishedRosterItems, isBusy, isPastDay, showToast]);

  const handleUndoPublishedEdit = useCallback(async () => {
    if (!publishedEditSnapshot || isBusy) return;
    if (!hasPublishedRosterUpdate) {
      setIsPublishedEditMode(false);
      setPublishedEditSnapshot(null);
      showToast(t('Edit cancelled'));
      return;
    }
    await restoreRosterSnapshot(publishedEditSnapshot);
  }, [hasPublishedRosterUpdate, isBusy, publishedEditSnapshot, restoreRosterSnapshot, showToast]);

  const notePublishedRosterChange = useCallback(() => {
    const hasPublishedItems = slots.some((slot) => slot.is_published)
      || breakSlots.some((slot) => slot.is_published);

    if (hasPublishedItems) {
      setHasPublishedRosterChanges(true);
    }
    setIsConfirmingPublish(false);
    setPublishWarningState(null);
  }, [breakSlots, slots]);

  const handleCopyDay = useCallback((dateKey: string) => {
    if (!selectedSiteId) return;
    setCopyPromptDate(dateKey);
  }, [selectedSiteId]);

  const handleConfirmCopyDay = useCallback((includeAssignments: boolean) => {
    if (!copyPromptDate || !selectedSiteId) return;
    setClipboard({ sourceDate: copyPromptDate, siteId: selectedSiteId, includeAssignments });
    setCopyPromptDate(null);
    showToast(includeAssignments
      ? t('Copied with guard assignments - pick the day(s) to paste into')
      : t('Copied without guard assignments - pick the day(s) to paste into'));
  }, [copyPromptDate, selectedSiteId, showToast]);

  const handlePasteDay = useCallback(async (targetDates: string[]) => {
    if (!clipboard || !selectedSiteId) return;
    if (clipboard.siteId !== selectedSiteId) {
      setError(t('Clipboard is from a different site. Copy again from the current site.'));
      return;
    }
    const editableTargetDates = targetDates.filter((dateKey) => !isHistoricDateKey(dateKey));
    if (editableTargetDates.length === 0) {
      showToast(t('Historic roster is read-only'));
      return;
    }

    setIsBusy(true);
    try {
      const created = await dutyBoardService.copyDayTo(
        selectedSiteId,
        clipboard.sourceDate,
        editableTargetDates,
        { includeAssignments: clipboard.includeAssignments },
      );
      // If we're on day view and pasted to the current day, refresh slots
      if (viewMode === 'day' && editableTargetDates.includes(selectedDate)) {
        await loadSlots();
      }
      // If we're on week view, merge new data into weekSlots
      if (viewMode === 'week') {
        setWeekSlots((prev) => {
          const next = { ...prev };
          for (const [date, newSlots] of Object.entries(created)) {
            next[date] = [...(next[date] ?? []), ...newSlots];
          }
          return next;
        });
      }
      const dayLabel = tn('{count} day', '{count} days', editableTargetDates.length);
      const skippedCount = targetDates.length - editableTargetDates.length;
      const pasteMessage = clipboard.includeAssignments
        ? t('Pasted duties and guard assignments to {days}', { days: dayLabel })
        : t('Pasted duties to {days} - no guards assigned', { days: dayLabel });
      showToast(skippedCount > 0
        ? `${pasteMessage}. ${tn('Skipped {count} historic day', 'Skipped {count} historic days', skippedCount)}`
        : pasteMessage);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  }, [clipboard, selectedSiteId, viewMode, selectedDate, loadSlots, showToast, isHistoricDateKey]);

  const handleClearDay = useCallback(async (dateKey: string) => {
    if (!selectedSiteId) return;
    if (!ensureEditableDay(dateKey)) return;
    setIsBusy(true);
    try {
      await dutyBoardService.clearDay(selectedSiteId, dateKey);
      if (viewMode === 'day' && dateKey === selectedDate) {
        setSlots([]);
        setBreakSlots([]);
        setHasPublishedRosterChanges(false);
        setIsConfirmingPublish(false);
        setIsPublishedEditMode(false);
        setPublishedEditSnapshot(null);
        setRosterViolations([]);
        setPublishWarningState(null);
      }
      if (viewMode === 'week') {
        setWeekSlots((prev) => {
          const next = { ...prev };
          delete next[dateKey];
          return next;
        });
        setWeekRosterViolations((prev) => {
          const next = { ...prev };
          delete next[dateKey];
          return next;
        });
      }
      showToast(t('Day cleared'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  }, [selectedSiteId, viewMode, selectedDate, showToast, ensureEditableDay]);

  const handlePublishDayFromWeek = useCallback(async (dateKey: string, overrideWarnings = false) => {
    if (!selectedSiteId) return;
    if (!ensureEditableDay(dateKey)) return;
    const daySlots = weekSlots[dateKey] ?? [];
    const isUpdate = daySlots.some((slot) => slot.is_published) && daySlots.some((slot) => !slot.is_published);
    setIsBusy(true);
    try {
      const publishResult = await dutyBoardService.publishDay(selectedSiteId, dateKey, { overrideWarnings });
      if (publishResult.status === 'blocked') {
        setPublishWarningState({ dateKey, isUpdate, violations: publishResult.violations });
        setWeekRosterViolations((prev) => ({
          ...prev,
          [dateKey]: publishResult.violations,
        }));
        showToast(t('Publication blocked by roster rule warnings'));
        return;
      }

      setWeekSlots((prev) => {
        const next = { ...prev };
        if (next[dateKey]) {
          next[dateKey] = next[dateKey].map((s) => ({ ...s, is_published: true }));
        }
        return next;
      });
      setWeekRosterViolations((prev) => ({
        ...prev,
        [dateKey]: publishResult.violations,
      }));
      setPublishWarningState(null);
      showToast(
        publishResult.overridden
          ? t('Day published with roster warnings flagged')
          : isUpdate
            ? t('Published roster updated - guards can see the latest shifts')
            : t('Day published - guards can now see assigned shifts'),
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  }, [selectedSiteId, showToast, ensureEditableDay, weekSlots]);

  const handleFillWeek = useCallback(async () => {
    if (!clipboard || !selectedSiteId) return;
    const emptyDates = weekDates.filter((dk) =>
      dk !== clipboard.sourceDate && (weekSlots[dk] ?? []).length === 0,
    );
    if (emptyDates.length === 0) {
      showToast(t('No empty days to fill'));
      return;
    }
    await handlePasteDay(emptyDates);
  }, [clipboard, selectedSiteId, weekDates, weekSlots, showToast, handlePasteDay]);

  const handleSwitchToDay = useCallback((dateKey: string) => {
    setSelectedDate(dateKey);
    setViewMode('day');
  }, []);

  // ── Existing day-view handlers ──────────────────────────────────────

  const handleAddDuty = async () => {
    if (!selectedSiteId || isBusy) return;
    if (!ensureEditableDay()) return;
    setBoardViewMode('coverage');
    const defaultKind: DutyKind = 'fixed_post';
    const defaultWindow = getDefaultSlotWindow(coverageWindow);

    if (!isWithinCoverageWindow(defaultWindow.startMinute, defaultWindow.endMinute, coverageWindow)) {
      setError(t('Duty times must stay inside the site coverage window.'));
      return;
    }

    notePublishedRosterChange();
    setInspectorSlotId(null);
    setEditTimesState(null);
    setIsBusy(true);
    try {
      const created = await dutyBoardService.createSlot({
        siteId: selectedSiteId,
        slotDate: selectedDate,
        name: getDefaultDutyName(defaultKind),
        dutyKind: defaultKind,
        colour: COLOURS[slots.length % COLOURS.length],
        startMinute: defaultWindow.startMinute,
        endMinute: defaultWindow.endMinute,
        guardsRequired: 1,
        displayOrder: getNextDisplayOrder(slots),
        requiredLicenceTypeId: selectedSiteRequiredLicenceTypeId,
      });
      setSlots((prev) => [...prev, created]);
      setInspectorSlotId(created.id);
      showToast(t('Duty added'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  /** Second (third, …) block on the same duty row — same post, different hours, different guard. */
  const handleAddRowBlock = async (row: DutyRowGroup) => {
    if (!selectedSiteId || isBusy) return;
    if (!ensureEditableDay()) return;

    const anchor = row.slots[row.slots.length - 1];
    const nextWindow = findFreeRowWindow(
      row.slots,
      coverageWindow,
      anchor.end_minute - anchor.start_minute,
    );

    if (!nextWindow) {
      setError(t('No free time left on this duty row. Shorten a block to make room.'));
      return;
    }

    notePublishedRosterChange();
    setIsBusy(true);
    try {
      const created = await dutyBoardService.createSlot({
        siteId: selectedSiteId,
        slotDate: selectedDate,
        name: row.primarySlot.name,
        dutyKind: row.primarySlot.duty_kind,
        colour: row.primarySlot.colour,
        startMinute: nextWindow.startMinute,
        endMinute: nextWindow.endMinute,
        guardsRequired: row.primarySlot.guards_required,
        displayOrder: row.displayOrder,
        requiredLicenceTypeId: row.primarySlot.required_licence_type_id,
        patrolId: anchor.patrol_id,
        postId: anchor.post_id,
      });
      setSlots((prev) => [...prev, created]);
      setInspectorSlotId(created.id);
      showToast(t('Block added to {name} - assign a guard for this window', { name: row.primarySlot.name }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  const handleDeleteDutyRow = async (rowSlots: DutySlot[]) => {
    if (!ensureEditableDay()) return;
    const slotIds = new Set(rowSlots.map((slot) => slot.id));

    notePublishedRosterChange();
    setSlots((prev) => prev.filter((slot) => !slotIds.has(slot.id)));
    setInspectorSlotId((prev) => (prev && slotIds.has(prev) ? null : prev));
    setEditTimesState((prev) => (prev && slotIds.has(prev.slotId) ? null : prev));

    try {
      await Promise.all(rowSlots.map((slot) => dutyBoardService.deleteSlot(slot.id)));
      showToast(t('Duty removed'));
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    }
  };


  const handlePublish = async (overrideWarnings = false) => {
    if (!selectedSiteId) return;
    if (!ensureEditableDay()) return;

    const isUpdate = hasPublishedRosterUpdate;
    setIsBusy(true);
    try {
      const publishResult = await dutyBoardService.publishDay(selectedSiteId, selectedDate, { overrideWarnings });
      if (publishResult.status === 'blocked') {
        setRosterViolations(publishResult.violations);
        setPublishWarningState({ dateKey: selectedDate, isUpdate, violations: publishResult.violations });
        setIsConfirmingPublish(false);
        showToast(t('Publication blocked by roster rule warnings'));
        return;
      }

      setSlots((prev) => prev.map((s) => ({ ...s, is_published: true })));
      setBreakSlots((prev) => prev.map((s) => ({ ...s, is_published: true })));
      setRosterViolations(publishResult.violations);
      setHasPublishedRosterChanges(false);
      setIsConfirmingPublish(false);
      setIsPublishedEditMode(false);
      setPublishedEditSnapshot(null);
      setPublishWarningState(null);
      showToast(
        publishResult.overridden
          ? t('Roster published with warnings flagged for admins')
          : isUpdate
            ? t('Published roster updated - guards can see the latest shifts')
            : t('Roster published - guards can now see assigned shifts'),
      );
    } catch (err) {
      setError((err as Error).message);
      setIsConfirmingPublish(false);
    } finally {
      setIsBusy(false);
    }
  };

  // ── Break handlers ──────────────────────────────────────────────────

  const getNextBreakDisplayOrder = (): number =>
    breakSlots.reduce((max, s) => Math.max(max, s.display_order), -1) + 1;

  const handleAddBreak = () => {
    if (!ensureEditableDay()) return;
    setBoardViewMode('coverage');
    setInspectorSlotId(null);
    setEditTimesState(null);
    setShowAddBreakModal(true);
  };

  const handleSaveNewBreak = async (durationMinutes: number, isPaid: boolean) => {
    if (!selectedSiteId) return;
    if (!ensureEditableDay()) return;

    const defaultWindow = getDefaultSlotWindow(coverageWindow);
    const endMinute = Math.min(defaultWindow.startMinute + durationMinutes, coverageWindow.endMinute);
    const startMinute = endMinute - durationMinutes;

    if (!isWithinCoverageWindow(startMinute, endMinute, coverageWindow)) {
      setError(t('Break does not fit within the site coverage window.'));
      setShowAddBreakModal(false);
      return;
    }

    notePublishedRosterChange();
    setIsBusy(true);
    setShowAddBreakModal(false);
    try {
      const created = await dutyBoardService.createBreakSlot({
        siteId: selectedSiteId,
        slotDate: selectedDate,
        durationMinutes,
        isPaid,
        startMinute,
        endMinute,
        displayOrder: getNextBreakDisplayOrder(),
      });
      setBreakSlots((prev) => [...prev, created]);
      showToast(t('Break added'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  /* Deleting a break should not slam the inspector shut when it is showing something else. */
  const handleDeleteBreak = async (breakSlotId: string) => {
    if (!ensureEditableDay()) return;
    notePublishedRosterChange();
    setBreakSlots((prev) => prev.filter((s) => s.id !== breakSlotId));
    setInspectorSlotId((prev) => (prev === breakSlotId ? null : prev));
    try {
      await dutyBoardService.deleteBreakSlot(breakSlotId);
      showToast(t('Break removed'));
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    }
  };

  /* Place a new break inside the duty: centred if the guard has nothing else booked,
     otherwise the first clear 15-minute step that fits. */
  const findGuardBreakStart = (
    slot: DutySlot,
    guardId: string,
    durationMinutes: number,
  ): number | null => {
    const lower = Math.max(slot.start_minute, coverageWindow.startMinute);
    const upper = Math.min(slot.end_minute, coverageWindow.endMinute);
    if (upper - lower < durationMinutes) return null;

    const guardBreaks = breakSlots.filter((b) => b.assignments.some((a) => a.guard_id === guardId));
    const isClear = (start: number) => !guardBreaks.some((b) => (
      overlapsWindow(b.start_minute, b.end_minute, start, start + durationMinutes)
    ));

    const latestStart = upper - durationMinutes;
    const centred = clamp(snap(lower + Math.round((upper - lower - durationMinutes) / 2)), lower, latestStart);
    if (isClear(centred)) return centred;

    for (let start = lower; start <= latestStart; start += SNAP) {
      if (isClear(start)) return start;
    }
    return null;
  };

  const handleCreateGuardBreak = async (
    slot: DutySlot,
    guardId: string,
    durationMinutes: number,
    isPaid: boolean,
  ) => {
    if (!selectedSiteId) return;
    if (!ensureEditableDay()) return;

    const startMinute = findGuardBreakStart(slot, guardId, durationMinutes);
    if (startMinute === null) {
      setError(t('No free window inside this duty for a {count} minute break.', { count: durationMinutes }));
      return;
    }

    notePublishedRosterChange();
    setIsBusy(true);
    try {
      const created = await dutyBoardService.createBreakSlot({
        siteId: selectedSiteId,
        slotDate: selectedDate,
        durationMinutes,
        isPaid,
        startMinute,
        endMinute: startMinute + durationMinutes,
        displayOrder: getNextBreakDisplayOrder(),
      });
      const assignment = await dutyBoardService.assignGuardToBreak(created.id, guardId);
      setBreakSlots((prev) => [...prev, {
        ...created,
        assignments: [...created.assignments, assignment],
        is_published: false,
      }]);
      showToast(t('Break added'));
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    } finally {
      setIsBusy(false);
    }
  };

  /* Taking a guard off a break they were alone on leaves an orphan, so delete it instead. */
  const handleRemoveGuardBreak = async (breakSlot: BreakSlot, guardId: string) => {
    const assignment = breakSlot.assignments.find((a) => a.guard_id === guardId);
    if (!assignment) return;
    if (breakSlot.assignments.length === 1) {
      await handleDeleteBreak(breakSlot.id);
      return;
    }
    await handleUnassignGuardFromBreak(breakSlot.id, assignment.id);
  };

  const handleAssignGuardToBreak = useCallback(async (breakSlotId: string, guardId: string) => {
    if (!ensureEditableDay()) return;
    notePublishedRosterChange();
    setIsBusy(true);
    try {
      const assignment = await dutyBoardService.assignGuardToBreak(breakSlotId, guardId);
      await dutyBoardService.updateBreakSlot(breakSlotId, { is_published: false });
      setBreakSlots((prev) => prev.map((s) => (
        s.id === breakSlotId ? { ...s, assignments: [...s.assignments, assignment], is_published: false } : s
      )));
      showToast(t('Guard assigned to break'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  }, [showToast, ensureEditableDay, notePublishedRosterChange]);

  const handleUnassignGuardFromBreak = async (breakSlotId: string, assignmentId: string) => {
    if (!ensureEditableDay()) return;
    notePublishedRosterChange();
    setIsBusy(true);
    setBreakSlots((prev) => prev.map((s) => (
      s.id === breakSlotId
        ? { ...s, assignments: s.assignments.filter((a) => a.id !== assignmentId), is_published: false }
        : s
    )));
    try {
      await dutyBoardService.unassignGuardFromBreak(assignmentId);
      await dutyBoardService.updateBreakSlot(breakSlotId, { is_published: false });
      showToast(t('Guard removed from break'));
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    } finally {
      setIsBusy(false);
    }
  };

  const handleDuplicateBreak = async (source: BreakSlot) => {
    if (!selectedSiteId) return;
    if (!ensureEditableDay()) return;

    // Find all breaks in the same row and place after the last one
    const rowBreaks = breakSlots.filter((s) => s.display_order === source.display_order);
    const lastEnd = Math.max(...rowBreaks.map((s) => s.end_minute));

    const startMinute = lastEnd;
    const endMinute = startMinute + source.duration_minutes;

    if (!isWithinCoverageWindow(startMinute, endMinute, coverageWindow)) {
      setError(t('No space to add another break in this coverage window.'));
      return;
    }

    notePublishedRosterChange();
    setIsBusy(true);
    try {
      const created = await dutyBoardService.createBreakSlot({
        siteId: selectedSiteId,
        slotDate: selectedDate,
        durationMinutes: source.duration_minutes,
        isPaid: source.is_paid,
        startMinute,
        endMinute,
        displayOrder: source.display_order,
      });
      setBreakSlots((prev) =>
        [...prev, created].sort((a, b) => (a.display_order - b.display_order) || (a.start_minute - b.start_minute)),
      );
      showToast(t('Break added'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  const startEditName = (row: DutyRowGroup) => {
    setEditingNameId(`row-${row.displayOrder}`);
    setEditingNameValue(row.primarySlot.name);
  };

  const commitEditName = async (displayOrder: number) => {
    if (!ensureEditableDay()) return;
    const value = editingNameValue.trim();
    if (!value) {
      setEditingNameId(null);
      return;
    }

    const rowSlots = slots.filter((slot) => slot.display_order === displayOrder);
    if (rowSlots.every((slot) => slot.name === value)) {
      setEditingNameId(null);
      return;
    }

    notePublishedRosterChange();
    setSlots((prev) => prev.map((slot) => (
      slot.display_order === displayOrder ? { ...slot, name: value, is_published: false } : slot
    )));
    setEditingNameId(null);

    try {
      await Promise.all(rowSlots.map((slot) => dutyBoardService.updateSlot(slot.id, { name: value, is_published: false })));
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    }
  };

  const handleRenameRow = async (displayOrder: number, name: string) => {
    if (!ensureEditableDay()) return;
    const rowSlots = slots.filter((s) => s.display_order === displayOrder);
    if (rowSlots.every((slot) => slot.name === name)) return;

    notePublishedRosterChange();
    setSlots((prev) => prev.map((s) => (
      s.display_order === displayOrder ? { ...s, name, is_published: false } : s
    )));
    try {
      await Promise.all(rowSlots.map((s) => dutyBoardService.updateSlot(s.id, { name, is_published: false })));
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    }
  };

  const approvedLicencesByGuard = useMemo(() => {
    const guardMap = new Map<string, Map<string, string>>();

    guardLicences.forEach((licence) => {
      if (!licence.licence_type_id || licence.status !== 'approved') return;
      const licenceMap = guardMap.get(licence.guard_id) ?? new Map<string, string>();
      const previousExpiry = licenceMap.get(licence.licence_type_id);
      if (!previousExpiry || licence.expiry_date > previousExpiry) {
        licenceMap.set(licence.licence_type_id, licence.expiry_date);
      }
      guardMap.set(licence.guard_id, licenceMap);
    });

    return guardMap;
  }, [guardLicences]);

  const publishedShiftWindowsByGuard = useMemo(() => {
    const guardMap = new Map<string, LocalPublishedShiftWindow[]>();

    publishedShiftAssignments.forEach((assignment) => {
      const dayDelta = getDateKeyDayDelta(selectedDate, assignment.shift_date);
      const startMinute = (dayDelta * MINUTES_PER_DAY) + assignment.shift_start_minute;
      const endMinute = (dayDelta * MINUTES_PER_DAY) + assignment.shift_end_minute;

      if (endMinute <= startMinute) return;

      const shiftLabel = assignment.shift_title ?? 'Untitled shift';
      const siteLabel = assignment.shift_site_name ? ` at ${assignment.shift_site_name}` : '';
      const windows = guardMap.get(assignment.guard_id) ?? [];
      windows.push({
        startMinute,
        endMinute,
        reason: `Already assigned to another published shift: ${shiftLabel}${siteLabel}.`,
      });
      guardMap.set(assignment.guard_id, windows);
    });

    return guardMap;
  }, [publishedShiftAssignments, selectedDate]);

  const resolveLicenceName = useCallback((licenceTypeId: string | null, explicitName?: string | null): string => {
    if (explicitName?.trim()) return explicitName.trim();
    if (!licenceTypeId) return 'required duty licence';
    return siteLicenceTypes.find((licenceType) => licenceType.id === licenceTypeId)?.name ?? 'required duty licence';
  }, [siteLicenceTypes]);

  const hasValidLicenceForWindow = useCallback((guardId: string, window: DutyCandidateWindow): boolean => {
    if (!window.requiredLicenceTypeId) return true;

    const guardLicencesForType = approvedLicencesByGuard.get(guardId);
    const latestExpiry = guardLicencesForType?.get(window.requiredLicenceTypeId);
    if (!latestExpiry) return false;

    return latestExpiry >= getWindowDateKey(selectedDate, window.endMinute);
  }, [approvedLicencesByGuard, selectedDate]);

  const getPublishedShiftConflictReason = useCallback((
    guardId: string,
    startMinute: number,
    endMinute: number,
  ): string | null => {
    const conflict = publishedShiftWindowsByGuard.get(guardId)?.find((shiftWindow) => (
      overlapsWindow(shiftWindow.startMinute, shiftWindow.endMinute, startMinute, endMinute)
    ));
    return conflict?.reason ?? null;
  }, [publishedShiftWindowsByGuard]);

  const getDutyAssignmentConflictReason = useCallback((slotId: string, guardId: string): string | null => {
    const targetSlot = slots.find((slot) => slot.id === slotId);
    if (!targetSlot) return null;

    const hasDutyConflict = [...slots, ...carryoverSlots].some((slot) => {
      if (slot.id === slotId) return false;
      if (!slot.assignments.some((assignment) => assignment.guard_id === guardId)) return false;
      return overlapsWindow(slot.start_minute, slot.end_minute, targetSlot.start_minute, targetSlot.end_minute);
    });

    if (hasDutyConflict) {
      return t('Already on another duty at this time.');
    }

    if (guardsBoundElsewhere.has(guardId)) {
      return t('Bound to other sites. Change where they work on their Staff profile.');
    }

    if (!hasValidLicenceForWindow(guardId, {
      startMinute: targetSlot.start_minute,
      endMinute: targetSlot.end_minute,
      requiredLicenceTypeId: targetSlot.required_licence_type_id,
      requiredLicenceName: targetSlot.required_licence?.name ?? null,
    })) {
      return `Missing valid licence: ${resolveLicenceName(
        targetSlot.required_licence_type_id,
        targetSlot.required_licence?.name,
      )}.`;
    }

    return getPublishedShiftConflictReason(guardId, targetSlot.start_minute, targetSlot.end_minute);
  }, [carryoverSlots, getPublishedShiftConflictReason, guardsBoundElsewhere, hasValidLicenceForWindow, resolveLicenceName, slots]);

  const handleAssignGuard = useCallback(async (slotId: string, guardId: string) => {
    if (!ensureEditableDay()) return;
    const conflictReason = getDutyAssignmentConflictReason(slotId, guardId);
    if (conflictReason) {
      const guardName = guards.find((guard) => guard.id === guardId)?.full_name ?? 'This guard';
      setError(`${guardName}: ${conflictReason}`);
      return;
    }

    notePublishedRosterChange();
    setIsBusy(true);
    try {
      const assignment = await dutyBoardService.assignGuard(slotId, guardId);
      const guardMeta = guards.find((guard) => guard.id === guardId);
      const enrichedAssignment = {
        ...assignment,
        guard_name: assignment.guard_name === 'Unknown'
          ? guardMeta?.full_name ?? assignment.guard_name
          : assignment.guard_name,
        avatar_url: assignment.avatar_url ?? guardMeta?.avatar_url ?? null,
      };
      await dutyBoardService.updateSlot(slotId, { is_published: false });
      setSlots((prev) => prev.map((s) => (
        s.id === slotId ? { ...s, assignments: [...s.assignments, enrichedAssignment], is_published: false } : s
      )));
      showToast(t('Guard assigned'));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  }, [getDutyAssignmentConflictReason, guards, showToast, ensureEditableDay, notePublishedRosterChange]);

  const handleUnassignGuard = async (slotId: string, assignmentId: string) => {
    if (!ensureEditableDay()) return;
    notePublishedRosterChange();
    setIsBusy(true);
    setSlots((prev) => prev.map((s) => (
      s.id === slotId
        ? { ...s, assignments: s.assignments.filter((a) => a.id !== assignmentId), is_published: false }
        : s
    )));

    try {
      await dutyBoardService.unassignGuard(assignmentId);
      await dutyBoardService.updateSlot(slotId, { is_published: false });
      showToast(t('Guard removed'));
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    } finally {
      setIsBusy(false);
    }
  };

  /* Guard view: dragging a duty block from one guard's row onto another's swaps who holds it. */
  const handleMoveDutyToGuard = useCallback(async (
    slotId: string,
    fromGuardId: string,
    toGuardId: string,
  ) => {
    if (!ensureEditableDay()) return;

    const slot = slots.find((s) => s.id === slotId);
    const assignment = slot?.assignments.find((a) => a.guard_id === fromGuardId);
    if (!slot || !assignment) return;

    if (slot.assignments.some((a) => a.guard_id === toGuardId)) {
      showToast(t('Already on this duty.'));
      return;
    }

    const guardMeta = guards.find((guard) => guard.id === toGuardId);
    const conflictReason = getDutyAssignmentConflictReason(slotId, toGuardId);
    if (conflictReason) {
      setError(`${guardMeta?.full_name ?? 'This guard'}: ${conflictReason}`);
      return;
    }

    notePublishedRosterChange();
    setIsBusy(true);
    try {
      await dutyBoardService.unassignGuard(assignment.id);
      const created = await dutyBoardService.assignGuard(slotId, toGuardId);
      await dutyBoardService.updateSlot(slotId, { is_published: false });
      const enriched = {
        ...created,
        guard_name: created.guard_name === 'Unknown'
          ? guardMeta?.full_name ?? created.guard_name
          : created.guard_name,
        avatar_url: created.avatar_url ?? guardMeta?.avatar_url ?? null,
      };
      setSlots((prev) => prev.map((s) => (
        s.id === slotId
          ? {
            ...s,
            assignments: [...s.assignments.filter((a) => a.id !== assignment.id), enriched],
            is_published: false,
          }
          : s
      )));
      showToast(t('Reassigned to {name}', { name: guardMeta?.full_name ?? '' }));
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    } finally {
      setIsBusy(false);
    }
  }, [
    ensureEditableDay,
    getDutyAssignmentConflictReason,
    guards,
    loadSlots,
    notePublishedRosterChange,
    showToast,
    slots,
  ]);

  const handleMoveBreakToGuard = useCallback(async (
    breakSlotId: string,
    fromGuardId: string,
    toGuardId: string,
  ) => {
    if (!ensureEditableDay()) return;

    const breakSlot = breakSlots.find((s) => s.id === breakSlotId);
    const assignment = breakSlot?.assignments.find((a) => a.guard_id === fromGuardId);
    if (!breakSlot || !assignment) return;
    if (breakSlot.assignments.some((a) => a.guard_id === toGuardId)) return;

    const guardMeta = guards.find((guard) => guard.id === toGuardId);
    notePublishedRosterChange();
    setIsBusy(true);
    try {
      await dutyBoardService.unassignGuardFromBreak(assignment.id);
      const created = await dutyBoardService.assignGuardToBreak(breakSlotId, toGuardId);
      await dutyBoardService.updateBreakSlot(breakSlotId, { is_published: false });
      setBreakSlots((prev) => prev.map((s) => (
        s.id === breakSlotId
          ? {
            ...s,
            assignments: [...s.assignments.filter((a) => a.id !== assignment.id), created],
            is_published: false,
          }
          : s
      )));
      showToast(t('Break moved to {name}', { name: guardMeta?.full_name ?? '' }));
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    } finally {
      setIsBusy(false);
    }
  }, [breakSlots, ensureEditableDay, guards, loadSlots, notePublishedRosterChange, showToast]);

  const getTrackRect = (slotId: string): DOMRect | null => {
    const direct = trackRefs.current.get(slotId);
    if (direct?.isConnected) return direct.getBoundingClientRect();
    const fallback = laneTrackRef.current;
    return fallback?.isConnected ? fallback.getBoundingClientRect() : null;
  };

  const handleBlockPointerDown = useCallback((
    slotId: string,
    mode: SlotDragMode,
    e: React.PointerEvent,
    fromGuardId: string | null = null,
  ) => {
    if (isBusy || !isDayEditable || e.button !== 0) return;

    e.preventDefault();
    e.stopPropagation();

    const slot = slots.find((s) => s.id === slotId) ?? breakSlots.find((s) => s.id === slotId);
    if (!slot) return;

    /* Select on grab rather than clearing. The inspector column is always reserved, so
       nothing reflows under the pointer, and the panel tracks the block being moved. */
    setInspectorSlotId(slotId);

    const rect = getTrackRect(slotId);
    const pointerMin = rect ? clientXToMinute(e.clientX, rect, displayCoverageWindow) : slot.start_minute;

    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setOpenGuardRowPickerId(null);
    setIsBoardGuardPickerOpen(false);
    setSlotInteraction({
      mode,
      slotId,
      pointerId: e.pointerId,
      origStart: slot.start_minute,
      origEnd: slot.end_minute,
      pointerMin,
      dirty: false,
      fromGuardId,
      overGuardId: fromGuardId,
      pointerX: e.clientX,
      pointerY: e.clientY,
    });
  }, [displayCoverageWindow, isBusy, isDayEditable, slots, breakSlots]);

  const handleCanvasPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!slotInteraction || e.pointerId !== slotInteraction.pointerId) return;

    const rect = getTrackRect(slotInteraction.slotId);
    if (!rect) return;

    const { clientX, clientY } = e;
    const currentMin = clientXToMinute(clientX, rect, displayCoverageWindow);
    const delta = currentMin - slotInteraction.pointerMin;
    const { origStart, origEnd, mode } = slotInteraction;
    const duration = origEnd - origStart;

    let nextStart = origStart;
    let nextEnd = origEnd;

    const liveBreak = breakSlots.find((s) => s.id === slotInteraction.slotId) ?? null;
    const isBreak = liveBreak !== null;

    if (isBreak) {
      // Breaks only support move — duration is fixed
      nextStart = snapClamp(origStart + delta, coverageWindow);
      nextEnd = nextStart + duration;
      if (nextEnd > coverageWindow.endMinute) {
        nextEnd = coverageWindow.endMinute;
        nextStart = nextEnd - duration;
      }
    } else if (mode === 'move') {
      nextStart = snapClamp(origStart + delta, coverageWindow);
      nextEnd = nextStart + duration;
      if (nextEnd > coverageWindow.endMinute) {
        nextEnd = coverageWindow.endMinute;
        nextStart = nextEnd - duration;
      }
    } else if (mode === 'resize-start') {
      nextStart = snapClamp(Math.min(origStart + delta, origEnd - MIN_DURATION), coverageWindow);
    } else {
      nextEnd = snapClamp(Math.max(origEnd + delta, origStart + MIN_DURATION), coverageWindow);
    }

    const liveSlot = isBreak ? liveBreak : slots.find((s) => s.id === slotInteraction.slotId) ?? null;
    const moved = !liveSlot || liveSlot.start_minute !== nextStart || liveSlot.end_minute !== nextEnd;

    if (moved) {
      if (isBreak) {
        setBreakSlots((prev) => prev.map((s) => (
          s.id === slotInteraction.slotId
            ? { ...s, start_minute: nextStart, end_minute: nextEnd }
            : s
        )));
      } else {
        setSlots((prev) => prev.map((s) => (
          s.id === slotInteraction.slotId
            ? { ...s, start_minute: nextStart, end_minute: nextEnd }
            : s
        )));
      }
    }

    const tracksGuardRow = boardViewMode === 'guard'
      && slotInteraction.mode === 'move'
      && !!slotInteraction.fromGuardId;

    let overGuardId = slotInteraction.overGuardId;
    if (tracksGuardRow) {
      const rowEl = document.elementFromPoint(clientX, clientY)?.closest('[data-guard-row-id]');
      overGuardId = (rowEl as HTMLElement | null)?.dataset.guardRowId ?? null;
    }

    /* Pointer moves arrive far faster than the 15-minute snap grid, so most of them
       land on the step the block already occupies. Re-rendering every lane for those
       is what made dragging feel jittery, and treating them as edits meant a nudged
       click could quietly unpublish the duty. */
    if (!moved && !tracksGuardRow) return;

    setSlotInteraction((prev) => (prev
      ? { ...prev, dirty: prev.dirty || moved, overGuardId, pointerX: clientX, pointerY: clientY }
      : null));
  }, [boardViewMode, coverageWindow, displayCoverageWindow, slotInteraction, breakSlots, slots]);

  const handleCanvasPointerUp = useCallback(async (e: React.PointerEvent<HTMLDivElement>) => {
    if (!slotInteraction || e.pointerId !== slotInteraction.pointerId) return;
    if (!ensureEditableDay()) {
      setSlotInteraction(null);
      await loadSlots();
      return;
    }

    const interaction = slotInteraction;
    setSlotInteraction(null);

    const droppedOnGuardId = boardViewMode === 'guard'
      && interaction.mode === 'move'
      && interaction.fromGuardId
      && interaction.overGuardId
      && interaction.overGuardId !== interaction.fromGuardId
      ? interaction.overGuardId
      : null;

    if (droppedOnGuardId && interaction.fromGuardId) {
      if (breakSlots.some((s) => s.id === interaction.slotId)) {
        await handleMoveBreakToGuard(interaction.slotId, interaction.fromGuardId, droppedOnGuardId);
      } else {
        await handleMoveDutyToGuard(interaction.slotId, interaction.fromGuardId, droppedOnGuardId);
      }
    }

    if (!interaction.dirty) return;

    // Handle break slot drag/resize
    const bSlot = breakSlots.find((s) => s.id === interaction.slotId);
    if (bSlot) {
      let finalStart = bSlot.start_minute;
      let finalEnd = bSlot.end_minute;

      if (!isWithinCoverageWindow(finalStart, finalEnd, coverageWindow)) {
        setBreakSlots((prev) => prev.map((s) => (
          s.id === bSlot.id
            ? { ...s, start_minute: interaction.origStart, end_minute: interaction.origEnd }
            : s
        )));
        setError(t('Break times must stay inside the site coverage window.'));
        return;
      }

      try {
        notePublishedRosterChange();
        setBreakSlots((prev) => prev.map((s) => (
          s.id === interaction.slotId ? { ...s, is_published: false } : s
        )));
        await dutyBoardService.updateBreakSlot(interaction.slotId, {
          start_minute: finalStart,
          end_minute: finalEnd,
          duration_minutes: finalEnd - finalStart,
          is_published: false,
        });
      } catch (err) {
        setError((err as Error).message);
        await loadSlots();
      }
      return;
    }

    // Handle duty slot drag/resize
    const slot = slots.find((s) => s.id === interaction.slotId);
    if (!slot) return;

    const siblings = slots.filter(
      (s) => s.id !== slot.id && s.display_order === slot.display_order,
    );

    let finalStart = slot.start_minute;
    let finalEnd = slot.end_minute;
    const duration = interaction.origEnd - interaction.origStart;

    const overlapping = siblings.filter(
      (s) => s.start_minute < finalEnd && s.end_minute > finalStart,
    );

    if (overlapping.length > 0) {
      if (interaction.mode === 'move') {
        if (finalStart >= interaction.origStart) {
          // moved right — butt end up against leftmost blocker's start
          const blocker = overlapping.reduce((min, s) => (s.start_minute < min.start_minute ? s : min));
          finalEnd = blocker.start_minute;
          finalStart = Math.max(coverageWindow.startMinute, finalEnd - duration);
        } else {
          // moved left — butt start up against rightmost blocker's end
          const blocker = overlapping.reduce((max, s) => (s.end_minute > max.end_minute ? s : max));
          finalStart = blocker.end_minute;
          finalEnd = Math.min(coverageWindow.endMinute, finalStart + duration);
        }
      } else if (interaction.mode === 'resize-start') {
        // left-edge drag — clamp start to rightmost blocker's end
        const blocker = overlapping.reduce((max, s) => (s.end_minute > max.end_minute ? s : max));
        finalStart = Math.min(blocker.end_minute, finalEnd - MIN_DURATION);
      } else {
        // right-edge drag — clamp end to leftmost blocker's start
        const blocker = overlapping.reduce((min, s) => (s.start_minute < min.start_minute ? s : min));
        finalEnd = Math.max(blocker.start_minute, finalStart + MIN_DURATION);
      }

      // Final guard — if still overlapping (e.g. squeezed between two blocks), revert
      const stillOverlapping = siblings.some(
        (s) => s.start_minute < finalEnd && s.end_minute > finalStart,
      );

      if (stillOverlapping) {
        setSlots((prev) => prev.map((s) => (
          s.id === slot.id
            ? { ...s, start_minute: interaction.origStart, end_minute: interaction.origEnd }
            : s
        )));
        setError(t('Not enough space to place the block there.'));
        return;
      }

      // Apply the snapped position in UI before saving
      setSlots((prev) => prev.map((s) => (
        s.id === slot.id ? { ...s, start_minute: finalStart, end_minute: finalEnd } : s
      )));
    }

    if (!isWithinCoverageWindow(finalStart, finalEnd, coverageWindow)) {
      setSlots((prev) => prev.map((s) => (
        s.id === slot.id
          ? { ...s, start_minute: interaction.origStart, end_minute: interaction.origEnd }
          : s
      )));
      setError(t('Duty times must stay inside the site coverage window.'));
      return;
    }

    try {
      notePublishedRosterChange();
      setSlots((prev) => prev.map((s) => (
        s.id === interaction.slotId ? { ...s, is_published: false } : s
      )));
      await dutyBoardService.updateSlot(interaction.slotId, {
        start_minute: finalStart,
        end_minute: finalEnd,
        is_published: false,
      });
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    }
  }, [
    boardViewMode,
    coverageWindow,
    slotInteraction,
    slots,
    breakSlots,
    loadSlots,
    ensureEditableDay,
    notePublishedRosterChange,
    handleMoveBreakToGuard,
    handleMoveDutyToGuard,
  ]);

  const handleGuardPointerDown = useCallback((
    guardId: string,
    guardName: string,
    e: React.PointerEvent<HTMLButtonElement>,
  ) => {
    if (e.button !== 0 || !ensureEditableDay()) return;

    e.preventDefault();
    (e.currentTarget as HTMLButtonElement).setPointerCapture(e.pointerId);

    const initial: GuardDrag = {
      guardId,
      guardName,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      clientX: e.clientX,
      clientY: e.clientY,
      activated: false,
      overSlotId: null,
    };

    guardDragRef.current = initial;
    setGuardDrag(initial);
  }, [ensureEditableDay]);

  const handleGuardPointerMove = useCallback((e: PointerEvent) => {
    const prev = guardDragRef.current;
    if (!prev || e.pointerId !== prev.pointerId) return;

    const dx = e.clientX - prev.startX;
    const dy = e.clientY - prev.startY;
    const activated = prev.activated || Math.hypot(dx, dy) > DRAG_THRESH;

    let overSlotId: string | null = null;
    if (activated) {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const slotEl = el?.closest('[data-slot-id]');
      if (slotEl) overSlotId = (slotEl as HTMLElement).dataset.slotId ?? null;
    }

    const next = { ...prev, clientX: e.clientX, clientY: e.clientY, activated, overSlotId };
    guardDragRef.current = next;
    setGuardDrag(next);
  }, []);

  const handleGuardPointerUp = useCallback((e: PointerEvent) => {
    const drag = guardDragRef.current;
    if (!drag || e.pointerId !== drag.pointerId) return;

    guardDragRef.current = null;
    setGuardDrag(null);

    if (drag.activated && drag.overSlotId) {
      // Check if dropped on a break block
      const bSlot = breakSlots.find((s) => s.id === drag.overSlotId);
      if (bSlot) {
        const alreadyAssigned = bSlot.assignments.some((a) => a.guard_id === drag.guardId);
        if (!alreadyAssigned) {
          void handleAssignGuardToBreak(drag.overSlotId, drag.guardId);
        }
        return;
      }

      // Otherwise duty slot
      const slot = slots.find((s) => s.id === drag.overSlotId);
      const alreadyAssigned = slot?.assignments.some((a) => a.guard_id === drag.guardId);
      if (!alreadyAssigned && slot) {
        const conflictReason = getDutyAssignmentConflictReason(slot.id, drag.guardId);
        if (conflictReason) {
          showToast(`${drag.guardName}: ${conflictReason}`);
        } else {
          void handleAssignGuard(drag.overSlotId, drag.guardId);
        }
      }
    }
  }, [slots, breakSlots, getDutyAssignmentConflictReason, handleAssignGuard, handleAssignGuardToBreak, showToast]);

  const isGuardDragging = guardDrag !== null;
  useEffect(() => {
    if (!isGuardDragging) return undefined;

    document.addEventListener('pointermove', handleGuardPointerMove);
    document.addEventListener('pointerup', handleGuardPointerUp);
    return () => {
      document.removeEventListener('pointermove', handleGuardPointerMove);
      document.removeEventListener('pointerup', handleGuardPointerUp);
    };
  }, [isGuardDragging, handleGuardPointerMove, handleGuardPointerUp]);

  const handleColourChange = async (displayOrder: number, colour: DutyColour) => {
    if (!ensureEditableDay()) return;
    const rowSlots = slots.filter((slot) => slot.display_order === displayOrder);
    if (rowSlots.every((slot) => slot.colour === colour)) return;

    notePublishedRosterChange();
    setSlots((prev) => prev.map((slot) => (
      slot.display_order === displayOrder ? { ...slot, colour, is_published: false } : slot
    )));
    try {
      await Promise.all(rowSlots.map((slot) => dutyBoardService.updateSlot(slot.id, { colour, is_published: false })));
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    }
  };

  const handleKindChange = async (displayOrder: number, duty_kind: DutyKind) => {
    if (!ensureEditableDay()) return;
    const rowSlots = slots.filter((slot) => slot.display_order === displayOrder);
    if (rowSlots.every((slot) => slot.duty_kind === duty_kind)) return;

    const updates = rowSlots.every((slot) => isDefaultDutyName(slot.name))
      ? { duty_kind, name: getDefaultDutyName(duty_kind), is_published: false }
      : { duty_kind, is_published: false };

    notePublishedRosterChange();
    setSlots((prev) => prev.map((slot) => (
      slot.display_order === displayOrder ? { ...slot, ...updates } : slot
    )));
    try {
      await Promise.all(rowSlots.map((slot) => dutyBoardService.updateSlot(slot.id, updates)));
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    }
  };

  const handleRequiredLicenceChange = async (displayOrder: number, requiredLicenceTypeId: string | null) => {
    if (!ensureEditableDay()) return;
    const nextRequiredLicence = requiredLicenceTypeId
      ? siteLicenceTypes.find((licenceType) => licenceType.id === requiredLicenceTypeId) ?? null
      : null;
    const rowSlots = slots.filter((slot) => slot.display_order === displayOrder);
    if (rowSlots.every((slot) => slot.required_licence_type_id === requiredLicenceTypeId)) return;

    notePublishedRosterChange();
    setSlots((prev) => prev.map((slot) => (
      slot.display_order === displayOrder
        ? {
          ...slot,
          required_licence_type_id: requiredLicenceTypeId,
          required_licence: nextRequiredLicence
            ? {
              id: nextRequiredLicence.id,
              name: nextRequiredLicence.name,
              country_code: nextRequiredLicence.country_code,
            }
            : null,
          is_published: false,
        }
        : slot
    )));

    try {
      await Promise.all(rowSlots.map((slot) => dutyBoardService.updateSlot(slot.id, {
        required_licence_type_id: requiredLicenceTypeId,
        is_published: false,
      })));
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    }
  };

  const handleOpenActionMenu = (slot: DutySlot, _rect?: DOMRect) => {
    if (!ensureEditableDay()) return;
    setInspectorSlotId(slot.id);
  };

  const handleOpenRoutePicker = (slotId: string, _rect?: DOMRect) => {
    if (!ensureEditableDay()) return;
    setInspectorSlotId(slotId);
  };

  const handleSelectRoute = async (slotId: string, patrolId: string | null, postId: string | null) => {
    if (!ensureEditableDay()) return;
    const slot = slots.find((s) => s.id === slotId);
    if (slot?.patrol_id === patrolId && slot.post_id === postId) return;

    notePublishedRosterChange();
    setSlots((prev) => prev.map((s) => (
      s.id === slotId ? { ...s, patrol_id: patrolId, post_id: postId, is_published: false } : s
    )));
    try {
      await dutyBoardService.updateSlot(slotId, { patrol_id: patrolId, post_id: postId, is_published: false });
      showToast(t('Route assigned'));
    } catch (err) {
      setError((err as Error).message);
      await loadSlots();
    }
  };

  const handleSplitSlot = async (slot: DutySlot) => {
    if (!selectedSiteId || isBusy) return;
    if (!ensureEditableDay()) return;

    const duration = slot.end_minute - slot.start_minute;
    if (duration < MIN_DURATION * 2) {
      setError(t('Duty must be at least 60 minutes to split.'));
      return;
    }

    const midpoint = clamp(
      snap(slot.start_minute + (duration / 2)),
      slot.start_minute + MIN_DURATION,
      slot.end_minute - MIN_DURATION,
    );

    if ((midpoint - slot.start_minute) < MIN_DURATION || (slot.end_minute - midpoint) < MIN_DURATION) {
      setError(t('Split would create a segment shorter than 30 minutes.'));
      return;
    }

    notePublishedRosterChange();
    setIsBusy(true);

    let createdSlotId: string | null = null;
    let originalUpdated = false;

    try {
      await dutyBoardService.updateSlot(slot.id, {
        end_minute: midpoint,
        is_published: false,
      });
      originalUpdated = true;

      const created = await dutyBoardService.createSlot({
        siteId: selectedSiteId,
        slotDate: selectedDate,
        name: slot.name,
        dutyKind: slot.duty_kind,
        colour: slot.colour,
        startMinute: midpoint,
        endMinute: slot.end_minute,
        guardsRequired: slot.guards_required,
        displayOrder: slot.display_order,
        requiredLicenceTypeId: slot.required_licence_type_id,
        isPublished: false,
        notes: slot.notes,
      });
      createdSlotId = created.id;

      if (slot.patrol_id || slot.post_id) {
        await dutyBoardService.updateSlot(created.id, {
          patrol_id: slot.patrol_id,
          post_id: slot.post_id,
          is_published: false,
        });
      }

      const splitSlot: DutySlot = {
        ...created,
        patrol_id: slot.patrol_id,
        post_id: slot.post_id,
        required_licence: slot.required_licence,
        assignments: [],
      };

      setSlots((prev) => (
        prev
          .map((s) => (
            s.id === slot.id
              ? { ...s, end_minute: midpoint, is_published: false }
              : s
          ))
          .concat(splitSlot)
          .sort((a, b) => (a.display_order - b.display_order) || (a.start_minute - b.start_minute))
      ));
      setInspectorSlotId(slot.id);
      showToast(t('Duty split'));
    } catch (err) {
      if (createdSlotId) {
        try { await dutyBoardService.deleteSlot(createdSlotId); } catch { /* best-effort */ }
      }
      if (originalUpdated) {
        try {
          await dutyBoardService.updateSlot(slot.id, {
            start_minute: slot.start_minute,
            end_minute: slot.end_minute,
            is_published: slot.is_published,
          });
        } catch {
          // The reload below reconciles any partial rollback failure.
        }
      }
      setError((err as Error).message);
      await loadSlots();
    } finally {
      setIsBusy(false);
    }
  };

  const handleOpenEditTimes = (slot: DutySlot) => {
    if (!ensureEditableDay()) return;
    setEditTimesState({
      slotId: slot.id,
      startValue: formatMinute(slot.start_minute, coverageWindow),
      endValue: formatMinute(slot.end_minute, coverageWindow),
      error: '',
    });
  };

  const handleSaveEditTimes = async () => {
    if (!editTimesState) return;
    if (!ensureEditableDay()) return;

    const slot = slots.find((s) => s.id === editTimesState.slotId);
    if (!slot) return;

    const nextStart = resolveTimeInputToAbsoluteMinute(editTimesState.startValue, coverageWindow);
    const nextEnd = resolveTimeInputToAbsoluteMinute(editTimesState.endValue, coverageWindow);

    if (nextStart === null || nextEnd === null) {
      setEditTimesState((prev) => (prev ? { ...prev, error: 'Enter valid HH:MM times inside the site coverage window.' } : prev));
      return;
    }

    if ((nextStart % SNAP) !== 0 || (nextEnd % SNAP) !== 0) {
      setEditTimesState((prev) => (prev ? { ...prev, error: 'Times must be in 15-minute increments.' } : prev));
      return;
    }

    if (nextStart >= nextEnd) {
      setEditTimesState((prev) => (prev ? { ...prev, error: 'End time must be later than the start time.' } : prev));
      return;
    }

    if ((nextEnd - nextStart) < MIN_DURATION) {
      setEditTimesState((prev) => (prev ? { ...prev, error: 'A duty block must be at least 30 minutes.' } : prev));
      return;
    }

    if (!isWithinCoverageWindow(nextStart, nextEnd, coverageWindow)) {
      setEditTimesState((prev) => (prev ? { ...prev, error: 'Duty times must stay inside the site coverage window.' } : prev));
      return;
    }

    const siblingsOverlap = slots.some((s) => {
      if (s.id === slot.id) return false;
      if (s.display_order !== slot.display_order) return false;
      return s.start_minute < nextEnd && s.end_minute > nextStart;
    });

    if (siblingsOverlap) {
      setEditTimesState((prev) => (prev ? { ...prev, error: 'These times overlap with another block in the same duty row.' } : prev));
      return;
    }

    if (nextStart === slot.start_minute && nextEnd === slot.end_minute) {
      setEditTimesState(null);
      return;
    }

    const previousStart = slot.start_minute;
    const previousEnd = slot.end_minute;

    notePublishedRosterChange();
    setIsBusy(true);
    setSlots((prev) => prev.map((s) => (
      s.id === slot.id ? { ...s, start_minute: nextStart, end_minute: nextEnd, is_published: false } : s
    )));

    try {
      await dutyBoardService.updateSlot(slot.id, {
        start_minute: nextStart,
        end_minute: nextEnd,
        is_published: false,
      });
      setEditTimesState(null);
      showToast(t('Duty times updated'));
    } catch (err) {
      setSlots((prev) => prev.map((s) => (
        s.id === slot.id ? { ...s, start_minute: previousStart, end_minute: previousEnd } : s
      )));
      setError((err as Error).message);
      await loadSlots();
    } finally {
      setIsBusy(false);
    }
  };

  const dutyRows = useMemo<DutyRowGroup[]>(() => {
    const grouped = new Map<number, DutySlot[]>();

    slots.forEach((slot) => {
      const current = grouped.get(slot.display_order);
      if (current) current.push(slot);
      else grouped.set(slot.display_order, [slot]);
    });

    return Array.from(grouped.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([displayOrder, rowSlots]) => {
        const sortedSlots = [...rowSlots].sort((a, b) => a.start_minute - b.start_minute);
        return {
          displayOrder,
          primarySlot: sortedSlots[0],
          slots: sortedSlots,
        };
      });
  }, [slots]);

  const displayDutyRows = useMemo<DutyRowGroup[]>(() => {
    const groupedContinuations = new Map<number, CarryoverDutySlot[]>();
    carryoverSlots.forEach((slot) => {
      const current = groupedContinuations.get(slot.display_order);
      if (current) current.push(slot);
      else groupedContinuations.set(slot.display_order, [slot]);
    });
    const continuationRows = Array.from(groupedContinuations.values()).map((rowSlots, index) => {
      const sortedSlots = [...rowSlots].sort((a, b) => a.start_minute - b.start_minute);
      return {
        displayOrder: -(index + 1),
        primarySlot: sortedSlots[0],
        slots: sortedSlots,
        isCarryover: true,
        sourceDate: sortedSlots[0].source_slot_date,
      };
    });
    return [...continuationRows, ...dutyRows];
  }, [carryoverSlots, dutyRows]);

  const dailyDutySlots = useMemo<DutySlot[]>(() => [
    ...carryoverSlots,
    ...slots.filter((slot) => overlapsWindow(
      slot.start_minute,
      slot.end_minute,
      CALENDAR_DAY_WINDOW.startMinute,
      CALENDAR_DAY_WINDOW.endMinute,
    )),
  ], [carryoverSlots, slots]);

  const breakRows = useMemo<BreakRowGroup[]>(() => {
    const grouped = new Map<number, BreakSlot[]>();
    breakSlots.forEach((bs) => {
      const current = grouped.get(bs.display_order);
      if (current) current.push(bs);
      else grouped.set(bs.display_order, [bs]);
    });
    return Array.from(grouped.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([displayOrder, rowBreaks]) => {
        const sorted = [...rowBreaks].sort((a, b) => a.start_minute - b.start_minute);
        return { displayOrder, primaryBreak: sorted[0], breaks: sorted };
      });
  }, [breakSlots]);

  const guardLicenceLabelById = useMemo(() => {
    const licenceTypeNameById = new Map(siteLicenceTypes.map((type) => [type.id, type.name]));
    const labelByGuardId = new Map<string, string>();
    approvedLicencesByGuard.forEach((licencesByType, guardId) => {
      const names = Array.from(licencesByType.keys())
        .map((typeId) => licenceTypeNameById.get(typeId))
        .filter((name): name is string => Boolean(name))
        .sort((a, b) => a.localeCompare(b));
      if (names.length > 0) labelByGuardId.set(guardId, names.join(' · '));
    });
    return labelByGuardId;
  }, [approvedLicencesByGuard, siteLicenceTypes]);

  /* Guard view rows: every guard who holds a duty or break today, plus any added by hand. */
  const boardGuardRows = useMemo<GuardBoardRow[]>(() => {
    const carryoverIds = new Set(carryoverSlots.map((slot) => slot.id));
    const onBoardGuardIds = new Set(manualBoardGuardIds);
    dailyDutySlots.forEach((slot) => slot.assignments.forEach((a) => onBoardGuardIds.add(a.guard_id)));
    breakSlots.forEach((breakSlot) => breakSlot.assignments.forEach((a) => onBoardGuardIds.add(a.guard_id)));

    return guards
      .filter((guard) => onBoardGuardIds.has(guard.id))
      .sort((a, b) => a.full_name.localeCompare(b.full_name))
      .map((guard) => {
        const segments: GuardRowSegment[] = dailyDutySlots
          .filter((slot) => slot.assignments.some((a) => a.guard_id === guard.id))
          .sort((a, b) => a.start_minute - b.start_minute)
          .map((slot) => ({
            slot,
            isCarryover: carryoverIds.has(slot.id),
            sourceDate: carryoverIds.has(slot.id) ? (slot as CarryoverDutySlot).source_slot_date : null,
          }));

        /* A break inside one of their duties is drawn on the duty block itself, so only the
           ones that fall outside every duty need a chip of their own. */
        const looseBreaks = breakSlots
          .filter((breakSlot) => breakSlot.assignments.some((a) => a.guard_id === guard.id))
          .filter((breakSlot) => !segments.some(({ slot }) => overlapsWindow(
            slot.start_minute,
            slot.end_minute,
            breakSlot.start_minute,
            breakSlot.end_minute,
          )))
          .sort((a, b) => a.start_minute - b.start_minute);

        const conflicts: GuardRowConflict[] = [];
        for (let i = 0; i < segments.length; i += 1) {
          for (let j = i + 1; j < segments.length; j += 1) {
            const first = segments[i].slot;
            const second = segments[j].slot;
            if (!overlapsWindow(first.start_minute, first.end_minute, second.start_minute, second.end_minute)) continue;
            const startMinute = Math.max(first.start_minute, second.start_minute);
            const endMinute = Math.min(first.end_minute, second.end_minute);
            conflicts.push({
              id: `${first.id}~${second.id}`,
              leftPct: minuteToPercent(startMinute, displayCoverageWindow),
              widthPct: durationToPercent(endMinute - startMinute, displayCoverageWindow),
              title: t('Double-booked: {first} and {second}', { first: first.name, second: second.name }),
            });
          }
        }

        return {
          guard,
          segments,
          looseBreaks,
          conflicts,
          assignedMinutes: segments.reduce((total, { slot }) => total + (slot.end_minute - slot.start_minute), 0),
          licenceLabel: guardLicenceLabelById.get(guard.id) ?? t('Guard'),
        };
      });
  }, [
    breakSlots,
    carryoverSlots,
    dailyDutySlots,
    displayCoverageWindow,
    guardLicenceLabelById,
    guards,
    manualBoardGuardIds,
  ]);

  const reassignTargetGuardName = useMemo(() => {
    if (boardViewMode !== 'guard' || !slotInteraction?.fromGuardId) return null;
    const { overGuardId, fromGuardId } = slotInteraction;
    if (!overGuardId || overGuardId === fromGuardId) return null;
    return guards.find((guard) => guard.id === overGuardId)?.full_name ?? null;
  }, [boardViewMode, guards, slotInteraction]);

  const guardsOffBoard = useMemo(() => {
    const onBoardIds = new Set(boardGuardRows.map((row) => row.guard.id));
    return guards.filter((guard) => !onBoardIds.has(guard.id));
  }, [boardGuardRows, guards]);

  const boardCandidateWindows = useMemo<DutyCandidateWindow[]>(() => {
    if (dailyDutySlots.length > 0) {
      return dailyDutySlots.map((slot) => ({
        startMinute: slot.start_minute,
        endMinute: slot.end_minute,
        requiredLicenceTypeId: slot.required_licence_type_id,
        requiredLicenceName: slot.required_licence?.name ?? null,
      }));
    }

    return [{
      startMinute: coverageWindow.startMinute,
      endMinute: coverageWindow.endMinute,
      requiredLicenceTypeId: selectedSiteRequiredLicenceTypeId,
      requiredLicenceName: selectedSite?.required_licence?.name ?? null,
    }];
  }, [coverageWindow.endMinute, coverageWindow.startMinute, dailyDutySlots, selectedSite, selectedSiteRequiredLicenceTypeId]);

  const inspectorSlot = useMemo(
    () => slots.find((s) => s.id === inspectorContentId) ?? null,
    [inspectorContentId, slots],
  );

  const inspectorBreakSlot = useMemo(
    () => breakSlots.find((s) => s.id === inspectorContentId) ?? null,
    [inspectorContentId, breakSlots],
  );

  /* Board highlighting follows the live selection, not the trailing panel content. */
  const selectedBreakId = useMemo(
    () => (inspectorSlotId && breakSlots.some((s) => s.id === inspectorSlotId) ? inspectorSlotId : null),
    [inspectorSlotId, breakSlots],
  );

  const boardGuardAvailabilityById = useMemo(() => {
    const availabilityMap = new Map<string, GuardAvailabilityState>();

    guards.forEach((guard) => {
      if (guardsBoundElsewhere.has(guard.id)) {
        availabilityMap.set(guard.id, {
          isAvailable: false,
          reasons: ['site'],
          reasonText: t('Bound to other sites. Change where they work on their Staff profile.'),
        });
        return;
      }

      const hasLicenceEligibleWindow = boardCandidateWindows.some((window) => (
        hasValidLicenceForWindow(guard.id, window)
      ));
      const hasShiftEligibleWindow = boardCandidateWindows.some((window) => (
        !getPublishedShiftConflictReason(guard.id, window.startMinute, window.endMinute)
      ));
      const hasAvailableWindow = boardCandidateWindows.some((window) => (
        hasValidLicenceForWindow(guard.id, window)
        && !getPublishedShiftConflictReason(guard.id, window.startMinute, window.endMinute)
      ));

      const reasons: GuardUnavailableReasonCode[] = [];
      const reasonParts: string[] = [];

      if (!hasAvailableWindow) {
        if (!hasLicenceEligibleWindow) {
          reasons.push('licence');
          const missingLicenceNames = Array.from(new Set(
            boardCandidateWindows
              .filter((window) => window.requiredLicenceTypeId)
              .map((window) => resolveLicenceName(window.requiredLicenceTypeId, window.requiredLicenceName)),
          ));
          reasonParts.push(
            missingLicenceNames.length > 0
              ? `Missing valid licence: ${missingLicenceNames.join(', ')}.`
              : 'Missing valid licence for current duties.',
          );
        }

        if (!hasShiftEligibleWindow) {
          reasons.push('published_shift');
          const shiftReason = boardCandidateWindows
            .map((window) => getPublishedShiftConflictReason(guard.id, window.startMinute, window.endMinute))
            .find(Boolean);
          reasonParts.push(shiftReason ?? 'Already assigned to another published shift.');
        }
      }

      availabilityMap.set(guard.id, {
        isAvailable: hasAvailableWindow,
        reasons,
        reasonText: reasonParts.join(' '),
      });
    });

    return availabilityMap;
  }, [
    boardCandidateWindows,
    getPublishedShiftConflictReason,
    guards,
    guardsBoundElsewhere,
    hasValidLicenceForWindow,
    resolveLicenceName,
  ]);

  const availableGuards = useMemo(() => (
    guards.filter((guard) => boardGuardAvailabilityById.get(guard.id)?.isAvailable !== false)
  ), [boardGuardAvailabilityById, guards]);

  const unavailableGuards = useMemo(() => (
    guards
      .filter((guard) => boardGuardAvailabilityById.get(guard.id)?.isAvailable === false)
      .map((guard) => ({
        guard,
        availability: boardGuardAvailabilityById.get(guard.id)!,
      }))
  ), [boardGuardAvailabilityById, guards]);

  const filteredUnavailableGuards = useMemo(() => {
    const searchValue = unavailableGuardSearch.trim().toLowerCase();
    if (!searchValue) return unavailableGuards;
    return unavailableGuards.filter(({ guard }) => (
      guard.full_name.toLowerCase().includes(searchValue)
    ));
  }, [unavailableGuardSearch, unavailableGuards]);

  const inspectorGuardAssignmentStateById = useMemo(() => {
    if (!inspectorSlot) return new Map<string, GuardAssignmentState>();

    const assignedToSlot = new Set(inspectorSlot.assignments.map((assignment) => assignment.guard_id));
    const stateMap = new Map<string, GuardAssignmentState>();

    guards.forEach((guard) => {
      if (assignedToSlot.has(guard.id)) return;

      const currentDutyConflict = slots.some((slot) => {
        if (slot.id === inspectorSlot.id) return false;
        if (!slot.assignments.some((assignment) => assignment.guard_id === guard.id)) return false;
        return overlapsWindow(slot.start_minute, slot.end_minute, inspectorSlot.start_minute, inspectorSlot.end_minute);
      });

      const licenceConflict = !hasValidLicenceForWindow(guard.id, {
        startMinute: inspectorSlot.start_minute,
        endMinute: inspectorSlot.end_minute,
        requiredLicenceTypeId: inspectorSlot.required_licence_type_id,
        requiredLicenceName: inspectorSlot.required_licence?.name ?? null,
      })
        ? `Missing valid licence: ${resolveLicenceName(
          inspectorSlot.required_licence_type_id,
          inspectorSlot.required_licence?.name,
        )}.`
        : null;

      const publishedShiftConflict = getPublishedShiftConflictReason(
        guard.id,
        inspectorSlot.start_minute,
        inspectorSlot.end_minute,
      );

      const siteConflict = guardsBoundElsewhere.has(guard.id)
        ? t('Bound to other sites. Change where they work on their Staff profile.')
        : null;

      const reasonText = currentDutyConflict
        ? t('Already on another duty at this time.')
        : siteConflict ?? licenceConflict ?? publishedShiftConflict ?? undefined;

      stateMap.set(guard.id, {
        canAssign: !reasonText,
        reasonText,
      });
    });

    return stateMap;
  }, [
    inspectorSlot,
    getPublishedShiftConflictReason,
    guards,
    guardsBoundElsewhere,
    hasValidLicenceForWindow,
    resolveLicenceName,
    slots,
  ]);

  const inspectorAvailableGuards = useMemo(() => {
    if (!inspectorSlot) return [];

    const assignedIds = new Set(inspectorSlot.assignments.map((assignment) => assignment.guard_id));
    return guards.filter((guard) => !assignedIds.has(guard.id));
  }, [guards, inspectorSlot]);

  const copyPromptSummary = useMemo(() => {
    if (!copyPromptDate) return null;
    const daySlots = viewMode === 'week' ? (weekSlots[copyPromptDate] ?? []) : slots;
    // Week view only holds duties, so break assignments count only on the day being viewed.
    const dayBreaks = viewMode === 'week' || copyPromptDate !== selectedDate ? [] : breakSlots;
    return {
      dutyCount: new Set(daySlots.map((slot) => slot.display_order)).size,
      blockCount: daySlots.length,
      assignmentCount: daySlots.reduce((total, slot) => total + slot.assignments.length, 0)
        + dayBreaks.reduce((total, breakSlot) => total + breakSlot.assignments.length, 0),
    };
  }, [breakSlots, copyPromptDate, selectedDate, slots, viewMode, weekSlots]);

  const editTimesSlot = useMemo(
    () => slots.find((s) => s.id === editTimesState?.slotId) ?? null,
    [editTimesState, slots],
  );

  const handleClientSelect = (clientKey: string) => {
    setSelectedClientKey(clientKey);
    const firstClientSite = sites.find((site) => getSiteClientKey(site) === clientKey);
    setSelectedSiteId(firstClientSite?.id ?? '');
  };

  const handleSiteSelect = (siteId: string) => {
    const nextSite = sites.find((site) => site.id === siteId);
    if (nextSite) {
      setSelectedClientKey(getSiteClientKey(nextSite));
    }
    setSelectedSiteId(siteId);
  };

  const publicationTone: PublicationTone = isPastDay
    ? 'past'
    : rosterItemCount === 0
      ? 'empty'
      : isPublishedRosterLocked
        ? 'locked'
        : isPublishedEditMode
          ? 'editing'
          : hasPublishedRosterUpdate
            ? 'update'
            : 'draft';

  const publicationChipLabel: Record<PublicationTone, string> = {
    past: t('Read only'),
    empty: t('No roster'),
    draft: t('Draft - not live'),
    update: t('Changes not live'),
    locked: t('Published - locked'),
    editing: t('Unlocked - not live'),
  };

  const publicationHeadline: Record<PublicationTone, string> = {
    past: t('Past day - read only'),
    empty: t('Nothing scheduled yet'),
    draft: t('Draft - guards cannot see this day'),
    update: t('Unpublished changes - guards still see the old roster'),
    locked: hasPublishedRosterUpdate
      ? t('Published and locked - with unpublished changes')
      : t('Published and live - the board is locked'),
    editing: hasPublishedRosterUpdate
      ? t('Unlocked - your changes are not live yet')
      : t('Unlocked for editing'),
  };

  const publicationDetail: Record<PublicationTone, string> = {
    past: t('Historic rosters cannot be changed. Open today or a future day to make edits.'),
    empty: t('Add duties and breaks, then publish the day before guards can see assigned shifts.'),
    draft: t('Nothing here reaches the guard app until you publish this day.'),
    update: t('Guards still see the last published version. Publish to push your changes.'),
    locked: hasPublishedRosterUpdate
      ? t('Guards see the published version. Unlock the board to keep editing, then publish again.')
      : t('Guards can already see these shifts, so the board is read-only. Unlock it to change duties, breaks or guards.'),
    editing: hasPublishedRosterUpdate
      ? t('Guards still see the published roster. Publish to push your changes, or undo to restore it.')
      : t('Make your changes, then publish again. Undo restores the published roster at any time.'),
  };

  const publicationIcon: Record<PublicationTone, React.ReactNode> = {
    past: <FiLock aria-hidden="true" />,
    empty: <FiEdit3 aria-hidden="true" />,
    draft: <FiUploadCloud aria-hidden="true" />,
    update: <FiUploadCloud aria-hidden="true" />,
    locked: <FiLock aria-hidden="true" />,
    editing: <FiUnlock aria-hidden="true" />,
  };
  const publishButtonLabel = isConfirmingPublish
    ? t('Confirm publish')
    : hasPublishedRosterUpdate
      ? t('Update published')
      : allPublished
        ? t('Published')
        : t('Publish day');
  const isPublishButtonDisabled = isBusy || rosterItemCount === 0 || (allPublished && !hasPublishedRosterUpdate);
  const handlePublishButtonClick = () => {
    if (isPublishButtonDisabled) return;
    if (!isConfirmingPublish) {
      setIsConfirmingPublish(true);
      return;
    }
    void handlePublish();
  };
  const handleOverridePublish = () => {
    if (!publishWarningState) return;
    if (publishWarningState.dateKey === selectedDate && viewMode === 'day') {
      void handlePublish(true);
      return;
    }
    void handlePublishDayFromWeek(publishWarningState.dateKey, true);
  };

  /* Fixes are written by one RPC, so the board is simply re-read rather than patched. */
  const handleFixesApplied = async (dateKey: string, remaining: RosterRuleViolation[]) => {
    setPublishWarningState((prev) => (
      remaining.length > 0
        ? { dateKey, isUpdate: prev?.isUpdate ?? false, violations: remaining }
        : null
    ));
    if (viewMode === 'week') {
      setWeekRosterViolations((prev) => ({ ...prev, [dateKey]: remaining }));
      await loadWeekSlots();
    }
    if (dateKey === selectedDate) await loadSlots();
    showToast(t('Roster fixes applied - changed duties are drafts until published'));
  };

  /* The scheduler writes through one RPC too; the board re-reads whatever it touched. */
  const handleAutoScheduleApplied = async (
    siteIds: string[],
    range: { startDate: string; endDate: string },
    createdAssignments: number,
  ) => {
    if (siteIds.includes(selectedSiteId)) {
      if (viewMode === 'week') await loadWeekSlots();
      if (selectedDate >= range.startDate && selectedDate <= range.endDate) await loadSlots();
    }
    showToast(tn(
      'Auto schedule added {count} shift as a draft',
      'Auto schedule added {count} shifts as drafts',
      createdAssignments,
    ));
  };

  const handlePublishAfterFixes = (dateKey: string) => {
    setFixModalDateKey(null);
    if (dateKey === selectedDate && viewMode === 'day') {
      void handlePublish();
      return;
    }
    void handlePublishDayFromWeek(dateKey);
  };
  const totalAssigned = dailyDutySlots.reduce((n, s) => n + s.assignments.length, 0);
  const totalRequired = dailyDutySlots.reduce((n, s) => n + s.guards_required, 0);
  const totalScheduledHours = (dailyDutySlots.reduce((total, slot) => (
    total + Math.max(0, Math.min(slot.end_minute, MINUTES_PER_DAY) - Math.max(slot.start_minute, 0))
  ), 0) / 60).toFixed(1);
  const uncoveredSlots = dailyDutySlots.filter((slot) => slot.assignments.length < slot.guards_required).length;
  const isWeek = viewMode === 'week';
  /* Open only while a duty is actually selected; the week grid has nothing to inspect. */
  const isInspectorPanelOpen = !isWeek && Boolean(inspectorSlotId) && Boolean(inspectorSlot || inspectorBreakSlot);
  const navDelta = isWeek ? 7 : 1;
  const navLabel = isWeek ? 'week' : 'day';

  const nowLinePercent = useMemo(() => {
    if (selectedDate !== todayKey) return null;
    const d = new Date();
    const minuteOfDay = d.getHours() * 60 + d.getMinutes();
    if (minuteOfDay <= displayCoverageWindow.startMinute || minuteOfDay >= displayCoverageWindow.endMinute) return null;
    return minuteToPercent(minuteOfDay, displayCoverageWindow);
  }, [selectedDate, todayKey, displayCoverageWindow]);

  const guardScheduledMinutes = useMemo(() => {
    const map = new Map<string, number>();
    dailyDutySlots.forEach((slot) => {
      slot.assignments.forEach((a) => {
        const visibleMinutes = Math.max(
          0,
          Math.min(slot.end_minute, MINUTES_PER_DAY) - Math.max(slot.start_minute, 0),
        );
        map.set(a.guard_id, (map.get(a.guard_id) ?? 0) + visibleMinutes);
      });
    });
    return map;
  }, [dailyDutySlots]);

  const guardDutyCount = useMemo(() => {
    const map = new Map<string, number>();
    dailyDutySlots.forEach((slot) => {
      slot.assignments.forEach((a) => {
        map.set(a.guard_id, (map.get(a.guard_id) ?? 0) + 1);
      });
    });
    return map;
  }, [dailyDutySlots]);

  const guardMatchesRailQuery = useCallback((guard: GuardRow) => {
    const query = guardRailQuery.toLowerCase().trim();
    return !query || guard.full_name.toLowerCase().includes(query);
  }, [guardRailQuery]);

  const filteredRailAvailableGuards = useMemo(() => (
    availableGuards.filter(guardMatchesRailQuery)
  ), [availableGuards, guardMatchesRailQuery]);

  const filteredRailUnavailableGuards = useMemo(() => (
    unavailableGuards.filter(({ guard }) => guardMatchesRailQuery(guard))
  ), [guardMatchesRailQuery, unavailableGuards]);

  const filteredRailGuards = useMemo(() => {
    if (guardRailFilter === 'available') return filteredRailAvailableGuards;
    if (guardRailFilter === 'unavailable') return filteredRailUnavailableGuards.map(({ guard }) => guard);
    return [
      ...filteredRailAvailableGuards,
      ...filteredRailUnavailableGuards.map(({ guard }) => guard),
    ];
  }, [filteredRailAvailableGuards, filteredRailUnavailableGuards, guardRailFilter]);

  const timeAxisTicks = useMemo(() => {
    const windowMins = displayCoverageWindow.endMinute - displayCoverageWindow.startMinute;
    const ticks: { label: string; leftPct: number; major: boolean }[] = [];
    const startH = Math.ceil(displayCoverageWindow.startMinute / 60);
    const endH = Math.floor(displayCoverageWindow.endMinute / 60);
    for (let h = startH; h <= endH; h++) {
      const m = h * 60;
      if (m < displayCoverageWindow.startMinute || m > displayCoverageWindow.endMinute) continue;
      ticks.push({ label: `${String(h).padStart(2, '0')}:00`, leftPct: ((m - displayCoverageWindow.startMinute) / windowMins) * 100, major: h % 2 === 0 });
    }
    return ticks;
  }, [displayCoverageWindow]);

  /* The axis track spans the same columns as every lane track, so one observer on it
     gives the pixel scale for the whole board. */
  const measureTimeline = useCallback((el: HTMLDivElement | null) => {
    axisTrackRef.current = el;
    if (el) setTimelineWidthPx(Math.round(el.getBoundingClientRect().width));
  }, []);

  useEffect(() => {
    const el = axisTrackRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const width = Math.round(entries[0].contentRect.width);
      setTimelineWidthPx((prev) => (prev === width ? prev : width));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [boardViewMode, isLoading, viewMode]);

  const pixelsPerMinute = timelineWidthPx > 0
    ? timelineWidthPx / getCoverageDurationMinutes(displayCoverageWindow)
    : 0;

  const timelineTrackMinWidth = useMemo(() => {
    const coverageHours = getCoverageDurationMinutes(displayCoverageWindow) / 60;
    const pxPerHour = coverageHours >= 18 ? 72 : coverageHours >= 12 ? 84 : 96;
    return Math.max(720, Math.ceil(coverageHours * pxPerHour));
  }, [displayCoverageWindow]);

  const renderRailGuardCard = (g: GuardRow, availability?: GuardAvailabilityState) => {
    const mins = guardScheduledMinutes.get(g.id) ?? 0;
    const hours = (mins / 60).toFixed(1);
    const maxMins = coverageWindow.endMinute - coverageWindow.startMinute;
    const barPct = maxMins > 0 ? Math.min(100, (mins / maxMins) * 100) : 0;
    const isUnavail = availability?.isAvailable === false;
    const dutyCount = guardDutyCount.get(g.id) ?? 0;
    const unavailableReason = summarizeUnavailableReason(availability);
    const dragTitle = isUnavail
      ? availability?.reasonText || unavailableReason
      : isDayEditable
        ? `Drag to assign ${g.full_name}`
        : g.full_name;

    return (
      <button
        key={g.id}
        type="button"
        className={[
          'db-gc',
          guardDrag?.guardId === g.id ? 'db-gc--dragging' : '',
          isUnavail ? 'db-gc--unavailable' : '',
        ].filter(Boolean).join(' ')}
        onPointerDown={isUnavail ? undefined : (e) => handleGuardPointerDown(g.id, g.full_name, e)}
        title={dragTitle}
        disabled={!isDayEditable || isUnavail}
        style={{ touchAction: 'none' }}
      >
        <span
          className="db-gc__avatar"
          style={g.avatar_url ? undefined : getAvatarAccentStyle(g.id || g.full_name)}
        >
          {g.avatar_url ? <img src={g.avatar_url} alt={g.full_name} /> : initials(g.full_name)}
          {!isUnavail && <span className="db-gc__status-dot" />}
        </span>
        <span className="db-gc__body">
          <span className="db-gc__name">{g.full_name}</span>
          <span className="db-gc__meta">
            {dutyCount > 0 ? `On ${dutyCount} ${dutyCount === 1 ? 'duty' : 'duties'}` : `${hours}h scheduled`}
          </span>
          {isUnavail && (
            <span className="db-gc__reason" title={availability?.reasonText}>
              {unavailableReason}
            </span>
          )}
        </span>
        <span className="db-gc__right">
          <span className="db-gc__hours">{hours}h</span>
          <span className="db-gc__hours-bar"><span style={{ width: `${barPct}%` }} /></span>
        </span>
      </button>
    );
  };

  return (
    <div className="duty-board" onPointerMove={handleCanvasPointerMove} onPointerUp={handleCanvasPointerUp}>

      {/* ─── Topbar ─────────────────────────────────────────────── */}
      <div className="db-topbar">
        <div className="db-topbar__center" data-tour="schedule-filters">
          <div className="db-topbar__intro">
            <span>{t('Operations')}</span>
            <strong>{t('Duties board')}</strong>
          </div>

          <div className="db-topbar__divider" />

          <div className="db-site-pick">
            <select
              className="form__select form__select--sm db-site-pick__select"
              value={selectedClientKey}
              onChange={(e) => handleClientSelect(e.target.value)}
              disabled={clientOptions.length === 0}
            >
              {clientOptions.length === 0 && <option value="">{t('No clients')}</option>}
              {clientOptions.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
            </select>
            <span className="db-site-pick__sep">/</span>
            <select
              className="form__select form__select--sm db-site-pick__select"
              value={selectedSiteId}
              onChange={(e) => handleSiteSelect(e.target.value)}
              disabled={filteredSites.length === 0}
            >
              {filteredSites.length === 0 && <option value="">{t('No sites')}</option>}
              {filteredSites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <div className="db-topbar__divider" />

          <div className="db-date-step">
            <button
              className="db-date-step__btn"
              onClick={() => setSelectedDate((d) => shiftDateKey(d, -navDelta))}
              aria-label={`Previous ${navLabel}`}
            >&#8249;</button>
            <span className="db-date-step__label">
              {isWeek ? weekRangeLabel : formatDateKeyLabel(selectedDate)}
            </span>
            <button
              className="db-date-step__btn"
              onClick={() => setSelectedDate((d) => shiftDateKey(d, navDelta))}
              aria-label={`Next ${navLabel}`}
            >&#8250;</button>
          </div>
          <button
            className="db-date-step__today"
            onClick={() => setSelectedDate(getCurrentDateKeyInTimeZone(selectedSiteTimeZone))}
          >{t('Today')}</button>

          <div className="db-topbar__divider" />

          <div className="db-view-toggle">
            <button
              className={`db-view-toggle__btn${!isWeek ? ' db-view-toggle__btn--active' : ''}`}
              onClick={() => setViewMode('day')}
            >{t('Day')}</button>
            <button
              className={`db-view-toggle__btn${isWeek ? ' db-view-toggle__btn--active' : ''}`}
              onClick={() => setViewMode('week')}
            >{t('Week')}</button>
          </div>

          {!isWeek && (
            <>
              <div className="db-topbar__divider" />
              <span className={`db-status-chip db-status-chip--${publicationTone}`}>
                {publicationTone === 'locked'
                  ? <FiLock aria-hidden="true" />
                  : <span className="db-status-chip__dot" />}
                {publicationChipLabel[publicationTone]}
              </span>
            </>
          )}
        </div>

        <div className="db-topbar__right" data-tour="schedule-publish">
          {onReplayTour && (
            <button type="button" className="db-btn-secondary" onClick={onReplayTour}>
              <FiHelpCircle aria-hidden="true" /> {t('Help')}
            </button>
          )}
          {!isWeek && (
            <button
              type="button"
              className={`db-guards-trigger${isGuardsPanelOpen ? ' db-guards-trigger--active' : ''}`}
              onClick={() => setIsGuardsPanelOpen((open) => !open)}
              aria-expanded={isGuardsPanelOpen}
              aria-controls="db-guards-panel"
              data-tour="schedule-guards"
            >
              <FiUsers aria-hidden="true" />
              <span>{t('Guards')}</span>
              <strong>{availableGuards.length}</strong>
            </button>
          )}
          <button
            type="button"
            className="db-btn-secondary"
            onClick={() => setIsAutoScheduleOpen(true)}
            disabled={!selectedSite || isBusy}
            title={t('Draft a fully staffed, rule-checked roster for one site or many, for a day, week or month')}
          >
            <FiZap aria-hidden="true" /> {t('Auto schedule')}
          </button>
          {isWeek && clipboard && (
            <button
              className="db-btn-secondary"
              onClick={() => { void handleFillWeek(); }}
              disabled={isBusy}
              title={clipboard.includeAssignments
                ? t('Paste the copied duties and their guards into every empty day this week')
                : t('Paste the copied duties into every empty day this week, unassigned')}
            >{t('Fill empty days')}</button>
          )}
          {!isWeek && !isPastDay && (
            <button
              className={`db-btn-primary${allPublished && !hasPublishedRosterUpdate ? ' db-btn-primary--done' : ''}`}
              disabled={isPublishButtonDisabled}
              onClick={handlePublishButtonClick}
            >{publishButtonLabel}</button>
          )}
        </div>
      </div>

      {/* ─── Publication state ──────────────────────────────────── */}
      {!isWeek && (
        <div className={`db-pubbar db-pubbar--${publicationTone}`} role="status" data-tour="schedule-status">
          <span className="db-pubbar__icon" aria-hidden="true">{publicationIcon[publicationTone]}</span>
          <span className="db-pubbar__text">
            <strong className="db-pubbar__title">{publicationHeadline[publicationTone]}</strong>
            <span className="db-pubbar__copy">
              {isConfirmingPublish
                ? t('Press Confirm publish once more to make these shifts visible to guards.')
                : publicationDetail[publicationTone]}
            </span>
          </span>
          {publicationTone === 'locked' && (
            <button
              type="button"
              className="db-pubbar__btn db-pubbar__btn--primary"
              onClick={handleStartPublishedEdit}
              disabled={isBusy}
            >
              <FiUnlock aria-hidden="true" /> {t('Unlock to edit')}
            </button>
          )}
          {publicationTone === 'editing' && publishedEditSnapshot && (
            <button
              type="button"
              className="db-pubbar__btn"
              onClick={() => { void handleUndoPublishedEdit(); }}
              disabled={isBusy}
            >
              <FiRotateCcw aria-hidden="true" /> {t('Undo edits')}
            </button>
          )}
        </div>
      )}

      {!isWeek && displayedRosterViolationCount > 0 && (
        <div className="db-callout db-callout--rule" role={pendingWarningsForSelectedDate ? 'alert' : 'status'}>
          <span className="db-callout__label">
            {pendingWarningsForSelectedDate ? t('Publication blocked') : t('Roster rule warnings')}
          </span>
          <span className="db-callout__copy">
            {pendingWarningsForSelectedDate
              ? tn(
                  '{count} warning must be fixed or overridden before publishing.',
                  '{count} warnings must be fixed or overridden before publishing.',
                  displayedRosterViolationCount,
                )
              : overriddenRosterViolationCount > 0
                ? tn(
                    '{count} warning flagged on this published roster after override.',
                    '{count} warnings flagged on this published roster after override.',
                    displayedRosterViolationCount,
                  )
                : tn(
                    '{count} warning flagged on this published roster.',
                    '{count} warnings flagged on this published roster.',
                    displayedRosterViolationCount,
                  )}
          </span>
          {/* Fixes edit the roster, so they follow the same lock as every other edit. */}
          {isDayEditable && selectedSite && (
            <button
              type="button"
              className="db-callout__action"
              onClick={() => setFixModalDateKey(selectedDate)}
              disabled={isBusy}
            >
              {t('Suggest fixes')}
            </button>
          )}
        </div>
      )}

      {!isWeek && (
        <PayFlagsCallout
          siteId={selectedSiteId || null}
          slotDate={selectedDate}
          slots={slots}
          breakSlots={breakSlots}
          canSeePay={canSeePay}
          isDraft={isDayEditable && !allPublished}
        />
      )}

      {error && (
        <div className="db-error-bar" role="alert">
          {error}
          <button onClick={() => setError('')} aria-label={t('Dismiss')}>x</button>
        </div>
      )}

      {/* ─── Three-pane body ──────────────────────────────────────── */}
      <div
        className={[
          'db-body',
          isInspectorPanelOpen ? 'db-body--inspecting' : '',
          isWeek ? 'db-body--week' : '',
        ].filter(Boolean).join(' ')}
        data-tour="schedule-board"
      >

        {/* LEFT RAIL ─── guards roster */}
        {!isWeek && (
        <aside
          id="db-guards-panel"
          className={`db-rail db-rail--drawer${isGuardsPanelOpen ? ' db-rail--open' : ''}`}
          aria-label={t('Guards')}
          aria-hidden={!isGuardsPanelOpen}
          inert={!isGuardsPanelOpen}
        >
          <div className="db-rail__head">
            <div className="db-rail__title-row">
              <div>
                <span className="db-rail__eyebrow">{t('Staffing')}</span>
                <span className="db-rail__title">
                  {t('Guards')}
                  <span className="db-rail__count">{guards.length}</span>
                </span>
              </div>
              <div className="db-rail__head-actions">
                <button
                  type="button"
                  className="db-icon-btn db-icon-btn--warning"
                  onClick={() => setShowUnavailableModal(true)}
                  title={`${unavailableGuards.length} unavailable`}
                  aria-label={`${unavailableGuards.length} unavailable guards`}
                >
                  <FiAlertTriangle aria-hidden="true" />
                  {unavailableGuards.length > 0 && <span>{unavailableGuards.length}</span>}
                </button>
                <button
                  type="button"
                  className="db-icon-btn"
                  onClick={() => setIsGuardsPanelOpen(false)}
                  aria-label={t('Close guards panel')}
                ><FiX aria-hidden="true" /></button>
              </div>
            </div>
            <p className="db-rail__hint">{t('Drag an available guard onto a duty or break.')}</p>
            <div className="db-rail__search">
              <FiSearch className="db-rail__search-icon" aria-hidden="true" />
              <input
                placeholder={t('Search guards...')}
                value={guardRailQuery}
                onChange={(e) => setGuardRailQuery(e.target.value)}
              />
            </div>
            <div className="db-rail__filters">
              {(['all', 'available', 'unavailable'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`db-rail__filter${guardRailFilter === f ? ' db-rail__filter--on' : ''}`}
                  onClick={() => setGuardRailFilter(f)}
                >
                  <span>{f === 'all' ? 'All' : f === 'available' ? 'Available' : 'Unavailable'}</span>
                  <strong>
                    {f === 'all'
                      ? guards.length
                      : f === 'available'
                        ? availableGuards.length
                        : unavailableGuards.length}
                  </strong>
                </button>
              ))}
            </div>
          </div>

          <div className="db-rail__list">
            {filteredRailGuards.length === 0 && (
              <span className="db-rail__group-label" style={{ paddingTop: 20 }}>
                {guards.length === 0 ? 'No guards yet' : 'No match'}
              </span>
            )}
            {guardRailFilter === 'all' ? (
              <>
                {filteredRailAvailableGuards.length > 0 && (
                  <div className="db-rail__section">
                    <span className="db-rail__group-label">
                      Available
                      <strong>{filteredRailAvailableGuards.length}</strong>
                    </span>
                    {filteredRailAvailableGuards.map((guard) => renderRailGuardCard(guard))}
                  </div>
                )}

                {filteredRailUnavailableGuards.length > 0 && (
                  <div className="db-rail__section">
                    <span className="db-rail__group-label">
                      Unavailable
                      <strong>{filteredRailUnavailableGuards.length}</strong>
                    </span>
                    {filteredRailUnavailableGuards.map(({ guard, availability }) => (
                      renderRailGuardCard(guard, availability)
                    ))}
                  </div>
                )}
              </>
            ) : guardRailFilter === 'available' ? (
              <div className="db-rail__section">
                {filteredRailAvailableGuards.map((guard) => renderRailGuardCard(guard))}
              </div>
            ) : (
              <div className="db-rail__section">
                {filteredRailUnavailableGuards.map(({ guard, availability }) => (
                  renderRailGuardCard(guard, availability)
                ))}
              </div>
            )}
          </div>
        </aside>
        )}

        {/* CENTER ─── canvas or week view */}
        {isWeek ? (
          <div className="db-week-wrap">
            {weekLoading ? (
              <div className="db-loading" style={{ minHeight: 200 }}>{t('Loading week...')}</div>
            ) : (
              <WeekView
                weekDates={weekDates}
                weekSlots={weekSlots}
                rosterViolations={weekRosterViolations}
                coverageWindow={coverageWindow}
                copiedDate={clipboard?.sourceDate ?? null}
                copiedWithAssignments={clipboard?.includeAssignments ?? false}
                todayKey={todayKey}
                isBusy={isBusy}
                onDayClick={handleSwitchToDay}
                onCopyDay={handleCopyDay}
                onPasteDay={(targets) => { void handlePasteDay(targets); }}
                onClearDay={(dk) => { void handleClearDay(dk); }}
                onPublishDay={(dk) => { void handlePublishDayFromWeek(dk); }}
              />
            )}
          </div>
        ) : (
          <div className={`db-canvas-wrap${isPublishedRosterLocked ? ' db-canvas-wrap--locked' : ''}`}>

            {/* Stats strip */}
            {!isLoading && (displayDutyRows.length > 0 || breakSlots.length > 0) && (
              <div className="db-canvas-topstrip">
                <span className={`db-coverage-pill${uncoveredSlots > 0 ? ' db-coverage-pill--gap' : totalAssigned >= totalRequired && totalRequired > 0 ? ' db-coverage-pill--full' : ''}`}>
                  {t('{assigned}/{required} assigned', { assigned: totalAssigned, required: totalRequired })}
                </span>
                <span className="db-coverage-pill">
                  {t('{count} duties · {hours}h', { count: displayDutyRows.length, hours: totalScheduledHours })}
                </span>
                {uncoveredSlots > 0 && (
                  <span className="db-coverage-pill db-coverage-pill--gap">
                    {tn('{count} gap', '{count} gaps', uncoveredSlots)}
                  </span>
                )}
                <span className="db-coverage-pill" style={{ marginLeft: 'auto', color: 'var(--db-g300)' }}>
                  {boardViewMode === 'guard'
                    ? tn('{count} guard on board', '{count} guards on board', boardGuardRows.length)
                    : t('Rows are duties')}
                </span>
                {isPublishedRosterLocked && (
                  <span className="db-coverage-pill db-coverage-pill--locked">
                    <FiLock aria-hidden="true" />
                    {t('Locked - published to guards')}
                  </span>
                )}
                {isPastDay && (
                  <span className="db-coverage-pill" style={{ color: 'var(--db-g300)' }}>
                    {t('Past day &#8212; view only')}
                  </span>
                )}
              </div>
            )}

            <div
              className="db-canvas-scroll"
              style={{ '--db-timeline-track-min': `${timelineTrackMinWidth}px` } as React.CSSProperties}
            >
              {isLoading ? (
                <div className="db-loading">{t('Loading roster...')}</div>
              ) : (
                <>
                  {/* Time axis */}
                  <div className="db-taxis">
                    <div className="db-taxis__corner">
                      {boardViewMode === 'guard' ? t('Guards') : t('Duties')}
                    </div>
                    <div className="db-taxis__track" ref={measureTimeline}>
                      {timeAxisTicks.map((tick) => (
                        <div
                          key={tick.label}
                          className={`db-taxis__tick${tick.major ? ' db-taxis__tick--major' : ''}`}
                          style={{ left: `${tick.leftPct}%` }}
                        >{tick.label}</div>
                      ))}
                      <div className="db-taxis__rule" />
                      {timeAxisTicks.map((tick) => (
                        <div
                          key={`gl-${tick.label}`}
                          className="db-taxis__gridline"
                          style={{ left: `${tick.leftPct}%` }}
                        />
                      ))}
                      {nowLinePercent !== null && (
                        <div className="db-nowline" style={{ left: `${nowLinePercent}%` }} />
                      )}
                    </div>
                  </div>


                  {boardViewMode === 'coverage'
                    && displayDutyRows.length === 0 && breakSlots.length === 0 && isDayEditable && (
                    <div className="db-empty">
                      <p>{t('No duties for this day yet.')}</p>
                      <span>{t('Use the action bar below to add your first duty or break.')}</span>
                    </div>
                  )}

                  {/* Duty lanes — coverage view */}
                  {boardViewMode === 'coverage' && displayDutyRows.map((row) => {
                    const slot = row.primarySlot;
                    const isInspectorOpen = row.slots.some((segment) => segment.id === inspectorSlotId);
                    const rowAssignedCount = row.slots.reduce((total, segment) => total + segment.assignments.length, 0);
                    const rowRequiredCount = row.slots.reduce((total, segment) => total + segment.guards_required, 0);
                    const rowViolations = uniqueViolations(row.slots.flatMap((segment) => displayedViolationBySourceSlotId.get(segment.id) ?? []));
                    const c = COLOUR_META[slot.colour];
                    const coverageState: 'full' | 'partial' | 'empty' =
                      rowRequiredCount === 0 ? 'full'
                      : rowAssignedCount >= rowRequiredCount ? 'full'
                      : rowAssignedCount === 0 ? 'empty' : 'partial';
                    const visibleSlots = row.slots.filter((segment) => overlapsWindow(
                      segment.start_minute,
                      segment.end_minute,
                      displayCoverageWindow.startMinute,
                      displayCoverageWindow.endMinute,
                    ));
                    const lastSlot = row.slots[row.slots.length - 1];
                    const addBlockWindow = isDayEditable && !row.isCarryover
                      ? findFreeRowWindow(
                          row.slots,
                          coverageWindow,
                          lastSlot.end_minute - lastSlot.start_minute,
                        )
                      : null;
                    // Near the right edge there is no room to sit after the gap, so hug the edge instead.
                    const addBlockLeftPct = addBlockWindow
                      ? minuteToPercent(addBlockWindow.startMinute, displayCoverageWindow)
                      : 0;
                    const addBlockStyle: React.CSSProperties = addBlockLeftPct > 95
                      ? { right: '6px' }
                      : { left: `${addBlockLeftPct}%`, marginLeft: '6px' };

                    return (
                      <div
                        key={row.isCarryover ? `carryover-row-${slot.id}` : `row-${row.displayOrder}`}
                        className={`db-lane${isInspectorOpen ? ' db-lane--selected' : ''}${row.isCarryover ? ' db-lane--carryover' : ''}`}
                      >
                        <div className="db-lane__head">
                          <div className="db-lane__head-top">
                            <span className="db-lane__swatch" style={{ background: c.dot }} />
                            {editingNameId === `row-${row.displayOrder}` ? (
                              <input
                                className="db-lane__name-input"
                                value={editingNameValue}
                                autoFocus
                                onChange={(e) => setEditingNameValue(e.target.value)}
                                onBlur={() => { void commitEditName(row.displayOrder); }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') void commitEditName(row.displayOrder);
                                  if (e.key === 'Escape') setEditingNameId(null);
                                }}
                                maxLength={60}
                              />
                            ) : (
                              <button
                                className="db-lane__name"
                                onClick={() => {
                                  if (row.isCarryover && row.sourceDate) {
                                    setSelectedDate(row.sourceDate);
                                    return;
                                  }
                                  if (isDayEditable) startEditName(row);
                                }}
                                disabled={!row.isCarryover && !isDayEditable}
                                title={row.isCarryover ? t('Open originating day') : undefined}
                              >{slot.name}</button>
                            )}
                          </div>
                          <div className="db-lane__meta">
                            <span className="db-lane__kind">{t(KIND_LABEL[slot.duty_kind])}</span>
                            {row.isCarryover && (
                              <span className="db-lane__continuation">{t('From previous day')}</span>
                            )}
                            <span className={`db-lane__coverage db-lane__coverage--${coverageState}`}>
                              <span className="db-lane__coverage-dot" />
                              {rowAssignedCount}/{rowRequiredCount}
                            </span>
                            {rowViolations.length > 0 && (
                              <span className="db-lane__warning" title={violationTitle(rowViolations)}>
                                {rowViolations.length} warn
                              </span>
                            )}
                            {isDayEditable && !row.isCarryover && (
                              <button
                                className="db-btn-secondary"
                                style={{ height: 18, fontSize: 9, padding: '0 5px', marginLeft: 'auto' }}
                                onClick={() => { void handleDeleteDutyRow(row.slots); }}
                                title={t('Delete duty')}
                              >{t('Delete')}</button>
                            )}
                          </div>
                        </div>

                        <div
                          className={`db-lane__track${!row.isCarryover && visibleSlots.some((segment) => guardDrag?.overSlotId === segment.id) ? ' db-lane__track--drop-ok' : ''}`}
                          ref={(el) => {
                            if (el) laneTrackRef.current = el;
                            if (row.isCarryover) return;
                            visibleSlots.forEach((segment) => {
                              if (el) trackRefs.current.set(segment.id, el);
                              else trackRefs.current.delete(segment.id);
                            });
                          }}
                        >
                          {visibleSlots.map((segment) => {
                            const displayedSegment: DutySlot = {
                              ...segment,
                              start_minute: Math.max(segment.start_minute, displayCoverageWindow.startMinute),
                              end_minute: Math.min(segment.end_minute, displayCoverageWindow.endMinute),
                            };
                            const routeLabel = segment.duty_kind === 'patrol'
                              ? (sitePatrols.find((p) => p.id === segment.patrol_id)?.name ?? null)
                              : segment.duty_kind === 'fixed_post'
                                ? (sitePosts.find((p) => p.id === segment.post_id)?.name ?? null)
                                : null;
                            return (
                              <DutyBlock
                                key={segment.id}
                                slot={displayedSegment}
                                coverageWindow={displayCoverageWindow}
                                interaction={row.isCarryover ? null : slotInteraction}
                                isMenuOpen={!row.isCarryover && inspectorSlotId === segment.id}
                                guardDragOver={!row.isCarryover && guardDrag?.activated === true && guardDrag.overSlotId === segment.id}
                                isLocked={!row.isCarryover && isPublishedRosterLocked}
                                routeLabel={routeLabel}
                                breakOverlays={buildDutyBreakOverlays(displayedSegment, breakSlots)}
                                ruleViolations={displayedViolationBySourceSlotId.get(segment.id) ?? []}
                                guardAvatarById={guardAvatarById}
                                pixelsPerMinute={pixelsPerMinute}
                                selectedBreakId={selectedBreakId}
                                guardDragBreakId={guardDrag?.activated === true ? guardDrag.overSlotId : null}
                                onBreakPointerDown={(breakSlotId, e) => {
                                  if (!row.isCarryover) handleBlockPointerDown(breakSlotId, 'move', e);
                                }}
                                onSelectBreak={(breakSlotId) => {
                                  if (row.isCarryover) return;
                                  if (!ensureEditableDay()) return;
                                  setInspectorSlotId(breakSlotId);
                                }}
                                onPointerDown={(mode, e) => {
                                  if (!row.isCarryover) handleBlockPointerDown(segment.id, mode, e);
                                }}
                                onOpenMenu={(e) => {
                                  if (row.isCarryover && row.sourceDate) {
                                    setSelectedDate(row.sourceDate);
                                    return;
                                  }
                                  if (!ensureEditableDay()) return;
                                  const blockRect = (e.currentTarget as HTMLElement).closest('.db-block')?.getBoundingClientRect()
                                    ?? (e.currentTarget as HTMLElement).getBoundingClientRect();
                                  handleOpenActionMenu(segment, blockRect);
                                }}
                                onSetRoute={(e) => {
                                  if (row.isCarryover) return;
                                  if (!ensureEditableDay()) return;
                                  const rect = (e.currentTarget as HTMLElement).closest('.db-block')?.getBoundingClientRect()
                                    ?? (e.currentTarget as HTMLElement).getBoundingClientRect();
                                  handleOpenRoutePicker(segment.id, rect);
                                }}
                              />
                            );
                          })}

                          {addBlockWindow && (
                            <button
                              type="button"
                              className="db-lane__add"
                              style={addBlockStyle}
                              onClick={(e) => { e.stopPropagation(); void handleAddRowBlock(row); }}
                              disabled={isBusy}
                              title={t('Add another block to {name} — cover a different time with a different guard', { name: slot.name })}
                              aria-label={t('Add another block to {name}', { name: slot.name })}
                            >
                              <FiPlus aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Break lanes — coverage view only; the guard view draws breaks on the guard's own row */}
                  {boardViewMode === 'coverage' && breakRows.length > 0 && (
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(180,195,216,0.06)' }}>
                      <div style={{ padding: '0 0 8px 0' }}>
                        <span className="db-lane__kind">{t('Breaks')}</span>
                      </div>
                      {breakRows.map((row) => {
                        const pb = row.primaryBreak;
                        const isBreakInspectorOpen = row.breaks.some((b) => b.id === inspectorSlotId);
                        const totalBreakAssigned = row.breaks.reduce((n, b) => n + b.assignments.length, 0);
                        const badgeTarget = row.breaks[0];
                        const rowViolations = uniqueViolations(row.breaks.flatMap((b) => displayedViolationBySourceSlotId.get(b.id) ?? []));
                        const visibleBreaks = row.breaks.filter((b) => overlapsWindow(
                          b.start_minute,
                          b.end_minute,
                          displayCoverageWindow.startMinute,
                          displayCoverageWindow.endMinute,
                        ));

                        return (
                          <div key={`break-row-${row.displayOrder}`} className={`db-lane${isBreakInspectorOpen ? ' db-lane--selected' : ''}`}>
                            <div className="db-lane__head">
                              <div className="db-lane__head-top">
                                <span className="db-lane__swatch" style={{ background: pb.is_paid ? 'var(--db-success)' : 'var(--db-warning)' }} />
                                <span className="db-lane__name" style={{ cursor: 'default' }}>
                                  {row.breaks.length > 1 ? `${row.breaks.length}x ` : ''}{pb.duration_minutes}min {pb.is_paid ? 'paid' : 'unpaid'}
                                </span>
                              </div>
                              <div className="db-lane__meta">
                                <button
                                  className="db-lane__coverage"
                                  onClick={() => { setInspectorSlotId(badgeTarget.id); }}
                                  disabled={!isDayEditable}
                                  style={{ cursor: 'pointer', border: 'none', background: 'none', fontFamily: 'inherit' }}
                                >
                                  {totalBreakAssigned === 0
                                    ? t('unassigned')
                                    : t('{count} assigned', { count: totalBreakAssigned })}
                                </button>
                                {rowViolations.length > 0 && (
                                  <span className="db-lane__warning" title={violationTitle(rowViolations)}>
                                    {rowViolations.length} warn
                                  </span>
                                )}
                                {isDayEditable && (
                                  <button
                                    className="db-btn-secondary"
                                    style={{ height: 18, fontSize: 9, padding: '0 5px', marginLeft: 'auto' }}
                                    onClick={() => { void handleDuplicateBreak(pb); }}
                                    disabled={isBusy}
                                    title={t('Add another break like this')}
                                  >+1</button>
                                )}
                                {isDayEditable && (
                                  <button
                                    className="db-btn-secondary"
                                    style={{ height: 18, fontSize: 9, padding: '0 5px' }}
                                    onClick={() => { void Promise.all(row.breaks.map((b) => handleDeleteBreak(b.id))); }}
                                    title={t('Delete all breaks in row')}
                                  >{t('Delete')}</button>
                                )}
                              </div>
                            </div>

                            <div
                              className={`db-lane__track${visibleBreaks.some((b) => guardDrag?.overSlotId === b.id) ? ' db-lane__track--drop-ok' : ''}`}
                              ref={(el) => {
                                visibleBreaks.forEach((b) => {
                                  if (el) trackRefs.current.set(b.id, el);
                                  else trackRefs.current.delete(b.id);
                                });
                              }}
                            >
                              {visibleBreaks.map((b) => (
                                <BreakBlock
                                  key={b.id}
                                  breakSlot={b}
                                  coverageWindow={displayCoverageWindow}
                                  interaction={slotInteraction}
                                  guardDragOver={guardDrag?.activated === true && guardDrag.overSlotId === b.id}
                                  ruleViolations={displayedViolationBySourceSlotId.get(b.id) ?? []}
                                  onPointerDown={(mode, e) => handleBlockPointerDown(b.id, mode, e)}
                                  onOpenSettings={() => {
                                    if (!ensureEditableDay()) return;
                                    setInspectorSlotId(b.id);
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Guard lanes — one row per rostered guard, breaks sit on the same row */}
                  {boardViewMode === 'guard' && boardGuardRows.length === 0 && (
                    <div className="db-empty">
                      <p>{t('No guards on the board yet.')}</p>
                      <span>{t('Assign a guard to a duty, or use + Guard below to put someone on the board.')}</span>
                    </div>
                  )}

                  {boardViewMode === 'guard' && boardGuardRows.map((row) => {
                    const { guard } = row;
                    const isRowSelected = row.segments.some(({ slot }) => slot.id === inspectorSlotId)
                      || row.looseBreaks.some((breakSlot) => breakSlot.id === inspectorSlotId);
                    const isReassignTarget = !!slotInteraction?.fromGuardId
                      && slotInteraction.overGuardId === guard.id
                      && slotInteraction.fromGuardId !== guard.id;
                    const isRailDropTarget = guardDrag?.activated === true && (
                      row.segments.some(({ slot }) => guardDrag.overSlotId === slot.id)
                      || row.looseBreaks.some((breakSlot) => guardDrag.overSlotId === breakSlot.id)
                    );
                    const avatarUrl = guardAvatarById.get(guard.id) ?? null;
                    const hoursLabel = row.assignedMinutes === 0
                      ? t('free')
                      : `${(row.assignedMinutes / 60).toFixed(row.assignedMinutes % 60 ? 1 : 0)}h`;
                    const isPickerOpen = openGuardRowPickerId === guard.id;
                    const dutyOptions = isPickerOpen
                      ? slots
                        .filter((slot) => !slot.assignments.some((a) => a.guard_id === guard.id))
                        .sort((a, b) => a.start_minute - b.start_minute)
                        .map((slot) => ({
                          slot,
                          conflictReason: getDutyAssignmentConflictReason(slot.id, guard.id),
                        }))
                      : [];

                    return (
                      <div
                        key={`guard-row-${guard.id}`}
                        className={`db-lane db-lane--guard${isRowSelected ? ' db-lane--selected' : ''}`}
                      >
                        <div className={`db-lane__head${isPickerOpen ? ' db-lane__head--raised' : ''}`}>
                          <div className="db-lane__head-top">
                            <span
                              className="db-lane__avatar"
                              style={avatarUrl ? undefined : getAvatarAccentStyle(guard.id || guard.full_name)}
                            >
                              {avatarUrl
                                ? <img src={avatarUrl} alt={guard.full_name} />
                                : initials(guard.full_name)}
                            </span>
                            <span className="db-lane__name" style={{ cursor: 'default' }} title={guard.full_name}>
                              {guard.full_name}
                            </span>
                          </div>

                          <div className="db-lane__meta">
                            <span className="db-lane__kind" title={row.licenceLabel}>{row.licenceLabel}</span>
                            <span
                              className={`db-lane__coverage db-lane__coverage--${row.assignedMinutes === 0 ? 'idle' : 'full'}`}
                            >
                              <span className="db-lane__coverage-dot" />
                              {hoursLabel}
                            </span>
                            {row.conflicts.length > 0 && (
                              <span
                                className="db-lane__warning"
                                title={row.conflicts.map((conflict) => conflict.title).join('\n')}
                              >{t('clash')}</span>
                            )}
                          </div>

                          {isDayEditable && (
                            <div className="db-lane__rowmenu-wrap">
                              <button
                                type="button"
                                className="db-lane__addduty"
                                onClick={() => setOpenGuardRowPickerId((prev) => (prev === guard.id ? null : guard.id))}
                                aria-expanded={isPickerOpen}
                              >+ {t('duty')}</button>

                              {isPickerOpen && (
                                <div className="db-rowmenu" role="menu">
                                  <span className="db-rowmenu__label">{t('Assign to duty')}</span>
                                  {dutyOptions.length === 0 ? (
                                    <span className="db-rowmenu__empty">{t('No other duties today.')}</span>
                                  ) : dutyOptions.map(({ slot, conflictReason }) => (
                                    <button
                                      key={slot.id}
                                      type="button"
                                      className="db-rowmenu__item"
                                      role="menuitem"
                                      disabled={isBusy || !!conflictReason}
                                      title={conflictReason ?? undefined}
                                      onClick={() => {
                                        setOpenGuardRowPickerId(null);
                                        void handleAssignGuard(slot.id, guard.id);
                                      }}
                                    >
                                      <span
                                        className="db-rowmenu__dot"
                                        style={{ background: COLOUR_META[slot.colour].dot }}
                                      />
                                      <span className="db-rowmenu__name">{slot.name}</span>
                                      <span className="db-rowmenu__time">
                                        {formatMinute(slot.start_minute, displayCoverageWindow)}
                                        –
                                        {formatMinute(slot.end_minute, displayCoverageWindow)}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div
                          className={`db-lane__track${isReassignTarget || isRailDropTarget ? ' db-lane__track--drop-ok' : ''}`}
                          data-guard-row-id={guard.id}
                          ref={(el) => {
                            if (!el) return;
                            laneTrackRef.current = el;
                            row.segments.forEach(({ slot, isCarryover }) => {
                              if (!isCarryover) trackRefs.current.set(slot.id, el);
                            });
                            row.looseBreaks.forEach((breakSlot) => trackRefs.current.set(breakSlot.id, el));
                          }}
                        >
                          {row.segments.length === 0 && row.looseBreaks.length === 0 && (
                            <span className="db-lane__prompt">
                              {t('Drag a duty here to assign {name}', { name: guard.full_name.split(' ')[0] })}
                            </span>
                          )}

                          {row.conflicts.map((conflict) => (
                            <div
                              key={conflict.id}
                              className="db-lane__clash"
                              style={{ left: `${conflict.leftPct}%`, width: `${conflict.widthPct}%` }}
                              title={conflict.title}
                            />
                          ))}

                          {row.looseBreaks.map((breakSlot) => (
                            <BreakBlock
                              key={`${guard.id}-${breakSlot.id}`}
                              breakSlot={breakSlot}
                              coverageWindow={displayCoverageWindow}
                              interaction={slotInteraction}
                              guardDragOver={guardDrag?.activated === true && guardDrag.overSlotId === breakSlot.id}
                              ruleViolations={displayedViolationBySourceSlotId.get(breakSlot.id) ?? []}
                              focusGuardId={guard.id}
                              onPointerDown={(mode, e) => handleBlockPointerDown(breakSlot.id, mode, e, guard.id)}
                              onOpenSettings={() => {
                                if (!ensureEditableDay()) return;
                                setInspectorSlotId(breakSlot.id);
                              }}
                            />
                          ))}

                          {row.segments.map(({ slot, isCarryover, sourceDate }) => {
                            const displayedSegment: DutySlot = {
                              ...slot,
                              start_minute: Math.max(slot.start_minute, displayCoverageWindow.startMinute),
                              end_minute: Math.min(slot.end_minute, displayCoverageWindow.endMinute),
                            };
                            const routeLabel = slot.duty_kind === 'patrol'
                              ? (sitePatrols.find((p) => p.id === slot.patrol_id)?.name ?? null)
                              : slot.duty_kind === 'fixed_post'
                                ? (sitePosts.find((p) => p.id === slot.post_id)?.name ?? null)
                                : null;
                            return (
                              <DutyBlock
                                key={`${guard.id}-${slot.id}`}
                                slot={displayedSegment}
                                coverageWindow={displayCoverageWindow}
                                interaction={isCarryover ? null : slotInteraction}
                                isMenuOpen={!isCarryover && inspectorSlotId === slot.id}
                                guardDragOver={!isCarryover && guardDrag?.activated === true && guardDrag.overSlotId === slot.id}
                                isLocked={!isCarryover && isPublishedRosterLocked}
                                routeLabel={routeLabel}
                                breakOverlays={buildDutyBreakOverlays(displayedSegment, breakSlots, guard.id)}
                                ruleViolations={displayedViolationBySourceSlotId.get(slot.id) ?? []}
                                guardAvatarById={guardAvatarById}
                                focusGuardId={guard.id}
                                pixelsPerMinute={pixelsPerMinute}
                                selectedBreakId={selectedBreakId}
                                guardDragBreakId={guardDrag?.activated === true ? guardDrag.overSlotId : null}
                                onBreakPointerDown={(breakSlotId, e) => {
                                  handleBlockPointerDown(breakSlotId, 'move', e, guard.id);
                                }}
                                onSelectBreak={(breakSlotId) => {
                                  if (!ensureEditableDay()) return;
                                  setInspectorSlotId(breakSlotId);
                                }}
                                onPointerDown={(mode, e) => {
                                  if (!isCarryover) handleBlockPointerDown(slot.id, mode, e, guard.id);
                                }}
                                onOpenMenu={() => {
                                  if (isCarryover) {
                                    if (sourceDate) setSelectedDate(sourceDate);
                                    return;
                                  }
                                  if (!ensureEditableDay()) return;
                                  handleOpenActionMenu(slot);
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* View switch — coverage rows are duties, guard rows are people */}
            <div className="db-viewswitch" role="group" aria-label={t('Board view')}>
              <span className="db-viewswitch__label">{t('View')}</span>
              <button
                type="button"
                className={`db-viewswitch__btn${boardViewMode === 'coverage' ? ' db-viewswitch__btn--on' : ''}`}
                aria-pressed={boardViewMode === 'coverage'}
                onClick={() => {
                  setBoardViewMode('coverage');
                  setOpenGuardRowPickerId(null);
                  setIsBoardGuardPickerOpen(false);
                }}
              >
                <span className="db-viewswitch__icon db-viewswitch__icon--coverage" aria-hidden="true" />
                {t('Coverage')}
              </button>
              <button
                type="button"
                className={`db-viewswitch__btn${boardViewMode === 'guard' ? ' db-viewswitch__btn--on' : ''}`}
                aria-pressed={boardViewMode === 'guard'}
                onClick={() => {
                  setBoardViewMode('guard');
                  setOpenGuardRowPickerId(null);
                }}
              >
                <span className="db-viewswitch__icon db-viewswitch__icon--guard" aria-hidden="true" />
                {t('Guard')}
              </button>
            </div>

            {/* The way out of the lock sits where the editing controls normally are */}
            {isPublishedRosterLocked && (
              <div className="db-actionbar db-actionbar--locked">
                <span className="db-actionbar__lock" aria-hidden="true"><FiLock /></span>
                <span className="db-actionbar__locktext">
                  <strong>{t('This roster is published')}</strong>
                  <small>{t('Guards can see these shifts. Unlock the day to change it.')}</small>
                </span>
                <button
                  type="button"
                  className="db-actionbar__btn db-actionbar__btn--primary"
                  onClick={handleStartPublishedEdit}
                  disabled={isBusy}
                >
                  <FiUnlock aria-hidden="true" /> {t('Unlock to edit')}
                </button>
              </div>
            )}

            {/* Floating action bar */}
            {isDayEditable && (
              <div className="db-actionbar">
                <button
                  className="db-actionbar__btn db-actionbar__btn--primary"
                  onClick={() => { void handleAddDuty(); }}
                  disabled={isBusy}
                >+ Duty</button>
                <button
                  className="db-actionbar__btn"
                  onClick={handleAddBreak}
                  disabled={isBusy}
                >+ Break</button>
                {boardViewMode === 'guard' && (
                  <>
                    <div className="db-actionbar__div" />
                    <div className="db-actionbar__pop">
                      <button
                        className="db-actionbar__btn"
                        onClick={() => {
                          setIsBoardGuardPickerOpen((prev) => !prev);
                          setOpenGuardRowPickerId(null);
                        }}
                        disabled={isBusy}
                        aria-expanded={isBoardGuardPickerOpen}
                      >+ Guard</button>

                      {isBoardGuardPickerOpen && (
                        <div className="db-guardpicker" role="menu">
                          <span className="db-guardpicker__label">{t('Add guard to board')}</span>
                          {guardsOffBoard.length === 0 ? (
                            <span className="db-guardpicker__empty">
                              {t('Every guard is already on the board.')}
                            </span>
                          ) : guardsOffBoard.map((guard) => {
                            const availability = boardGuardAvailabilityById.get(guard.id);
                            const isUnavailable = availability?.isAvailable === false;
                            const avatarUrl = guardAvatarById.get(guard.id) ?? null;
                            return (
                              <button
                                key={guard.id}
                                type="button"
                                role="menuitem"
                                className="db-guardpicker__item"
                                title={isUnavailable ? summarizeUnavailableReason(availability) : undefined}
                                onClick={() => {
                                  setManualBoardGuardIds((prev) => (
                                    prev.includes(guard.id) ? prev : [...prev, guard.id]
                                  ));
                                  setIsBoardGuardPickerOpen(false);
                                  showToast(t('{name} added to the board', { name: guard.full_name }));
                                }}
                              >
                                <span
                                  className="db-guardpicker__avatar"
                                  style={avatarUrl ? undefined : getAvatarAccentStyle(guard.id || guard.full_name)}
                                >
                                  {avatarUrl
                                    ? <img src={avatarUrl} alt={guard.full_name} />
                                    : initials(guard.full_name)}
                                </span>
                                <span className="db-guardpicker__text">
                                  <strong>{guard.full_name}</strong>
                                  <small className={isUnavailable ? 'db-guardpicker__warn' : undefined}>
                                    {isUnavailable
                                      ? t('Unavailable')
                                      : guardLicenceLabelById.get(guard.id) ?? t('Available')}
                                  </small>
                                </span>
                                <span className="db-guardpicker__cta">{t('Add')}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
                {slots.length > 0 && (
                  <>
                    <div className="db-actionbar__div" />
                    <button
                      className={`db-actionbar__btn${clipboard?.sourceDate === selectedDate ? ' db-actionbar__btn--primary' : ''}`}
                      onClick={() => handleCopyDay(selectedDate)}
                      disabled={isBusy}
                    >{clipboard?.sourceDate === selectedDate ? 'Copied' : 'Copy day'}</button>
                  </>
                )}
                {clipboard && clipboard.sourceDate !== selectedDate && !isPastDay && (
                  <button
                    className="db-actionbar__btn"
                    onClick={() => { void handlePasteDay([selectedDate]); }}
                    disabled={isBusy}
                    title={clipboard.includeAssignments
                      ? t('Paste the copied duties and their guards into this day')
                      : t('Paste the copied duties into this day, unassigned')}
                  >{clipboard.includeAssignments ? t('Paste + guards') : t('Paste')}</button>
                )}
              </div>
            )}
          </div>
        )}

        {/* RIGHT INSPECTOR — an overlay, so the timeline keeps one width whatever the
            panel does. It slides in over the board and back out on a click off the duty. */}
        {isWeek ? null : (
          <div
            className={`db-inspector db-inspector--drawer${inspectorBreakSlot ? ' db-inspector--break' : ' db-inspector--duty'}`}
            role="complementary"
            aria-label={inspectorBreakSlot ? t('Break details') : t('Duty details')}
            aria-hidden={!isInspectorPanelOpen}
            inert={!isInspectorPanelOpen}
          >
            {inspectorSlot && (() => {
              const assignedCount = inspectorSlot.assignments.length;
              const requiredCount = inspectorSlot.guards_required;
              const shortfall = Math.max(0, requiredCount - assignedCount);
              const staffingState = assignedCount >= requiredCount ? 'ok' : assignedCount === 0 ? 'err' : 'warn';
              const staffingLabel = assignedCount >= requiredCount
                ? 'Fully staffed'
                : assignedCount === 0
                  ? 'Unstaffed'
                  : 'Needs staff';
              const supportsLocation = inspectorSlot.duty_kind === 'fixed_post' || inspectorSlot.duty_kind === 'patrol';
              const isPatrolDuty = inspectorSlot.duty_kind === 'patrol';
              const locationItems = supportsLocation
                ? isPatrolDuty ? sitePatrols : sitePosts
                : [];
              const locationKindLabel = isPatrolDuty ? 'patrol route' : 'post';
              const selectedLocationId = isPatrolDuty
                ? inspectorSlot.patrol_id ?? ''
                : inspectorSlot.post_id ?? '';
              const selectedLocationMissing = Boolean(selectedLocationId)
                && !locationItems.some((item) => item.id === selectedLocationId);
              const requiredLicenceLabel = inspectorSlot.required_licence_type_id
                ? resolveLicenceName(inspectorSlot.required_licence_type_id, inspectorSlot.required_licence?.name)
                : 'No licence';
              const assignedGuardSubtitle = inspectorSlot.required_licence_type_id
                ? requiredLicenceLabel
                : 'Assigned guard';
              const assignableGuards = inspectorAvailableGuards.filter((guard) => (
                inspectorGuardAssignmentStateById.get(guard.id)?.canAssign !== false
              ));

              return (
                <>
                  <div className="db-inspector__head">
                    <div className="db-inspector__title-row">
                      <span
                        className="db-inspector__swatch"
                        style={{ background: COLOUR_META[inspectorSlot.colour].dot }}
                      />
                      <input
                        key={`${inspectorSlot.id}:${inspectorSlot.name}`}
                        className="db-inspector__title-input"
                        defaultValue={inspectorSlot.name}
                        disabled={!isDayEditable}
                        maxLength={60}
                        onBlur={(e) => {
                          const nextName = e.currentTarget.value.trim();
                          if (!nextName) {
                            e.currentTarget.value = inspectorSlot.name;
                            return;
                          }
                          if (nextName !== inspectorSlot.name) {
                            void handleRenameRow(inspectorSlot.display_order, nextName);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.blur();
                          if (e.key === 'Escape') {
                            e.currentTarget.value = inspectorSlot.name;
                            e.currentTarget.blur();
                          }
                        }}
                      />
                      <button
                        className="db-inspector__close"
                        onClick={() => setInspectorSlotId(null)}
                        aria-label={t('Close inspector')}
                      >
                        <FiX aria-hidden="true" />
                      </button>
                    </div>
                    <div className="db-inspector__tags">
                      <span className="db-tag">{t(KIND_LABEL[inspectorSlot.duty_kind])}</span>
                      <span className="db-tag">
                        {formatMinute(inspectorSlot.start_minute, coverageWindow)}-{formatMinute(inspectorSlot.end_minute, coverageWindow)}
                      </span>
                      <span className={`db-tag db-tag--${staffingState}`}>{t(staffingLabel)}</span>
                      <label className="db-chip-select">
                        <span>{t('Licence')}</span>
                        <select
                          className="form__select form__select--bare"
                          value={inspectorSlot.required_licence_type_id ?? ''}
                          onChange={(e) => { void handleRequiredLicenceChange(inspectorSlot.display_order, e.currentTarget.value || null); }}
                          disabled={!isDayEditable || !selectedSiteCountryCode}
                          aria-label={t('Licence required')}
                        >
                          {!selectedSiteCountryCode && <option value="">{t('Choose site country')}</option>}
                          {selectedSiteCountryCode && <option value="">{t('No licence')}</option>}
                          {siteLicenceTypes.map((licenceType) => (
                            <option key={licenceType.id} value={licenceType.id}>{licenceType.name}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>

                  <div className="db-inspector__body">
                    <section className="db-inspector__section" aria-labelledby="db-duty-type-heading">
                      <div className="db-inspector__section-head">
                        <h3 id="db-duty-type-heading">{t('Type')}</h3>
                      </div>
                      <div className="db-kind-pills">
                        {getInspectorKindOptions(inspectorSlot.duty_kind).map((kind) => (
                          <button
                            key={kind}
                            type="button"
                            className={`db-kind-pill${inspectorSlot.duty_kind === kind ? ' db-kind-pill--on' : ''}`}
                            disabled={!isDayEditable || isBusy}
                            onClick={() => { void handleKindChange(inspectorSlot.display_order, kind); }}
                          >
                            <span className="db-kind-pill__icon">{KIND_ICON[kind]}</span>
                            <span>{t(KIND_LABEL[kind])}</span>
                          </button>
                        ))}
                      </div>
                    </section>

                    <section className="db-inspector__section" aria-labelledby="db-duty-times-heading">
                      <div className="db-inspector__section-head">
                        <h3 id="db-duty-times-heading">{t('Times')}</h3>
                        {isDayEditable && (
                          <button
                            type="button"
                            className="db-inline-action"
                            disabled={isBusy}
                            onClick={() => { void handleSplitSlot(inspectorSlot); }}
                          >
                            <FiScissors aria-hidden="true" />
                            {t('Split')}
                          </button>
                        )}
                      </div>
                      <div className="db-time-fields">
                        <button
                          type="button"
                          className="db-time-field"
                          disabled={!isDayEditable}
                          onClick={() => handleOpenEditTimes(inspectorSlot)}
                        >
                          <span>{t('Start')}</span>
                          <strong>{formatMinute(inspectorSlot.start_minute, coverageWindow)}</strong>
                        </button>
                        <button
                          type="button"
                          className="db-time-field"
                          disabled={!isDayEditable}
                          onClick={() => handleOpenEditTimes(inspectorSlot)}
                        >
                          <span>{t('End')}</span>
                          <strong>{formatMinute(inspectorSlot.end_minute, coverageWindow)}</strong>
                        </button>
                      </div>
                    </section>

                    <section className="db-inspector__section" aria-labelledby="db-duty-colour-heading">
                      <div className="db-inspector__section-head">
                        <h3 id="db-duty-colour-heading">{t('Color')}</h3>
                      </div>
                      <div className="db-color-swatches" role="radiogroup" aria-label={t('Duty color')}>
                        {COLOURS.map((colour) => (
                          <button
                            key={colour}
                            type="button"
                            className={`db-color-swatch${inspectorSlot.colour === colour ? ' db-color-swatch--on' : ''}`}
                            style={{ background: COLOUR_META[colour].dot }}
                            disabled={!isDayEditable || isBusy}
                            aria-label={`Use ${colour} color`}
                            aria-checked={inspectorSlot.colour === colour}
                            role="radio"
                            onClick={() => { void handleColourChange(inspectorSlot.display_order, colour); }}
                          />
                        ))}
                      </div>
                    </section>

                    <section className="db-inspector__section" aria-labelledby="db-duty-location-heading">
                      <div className="db-inspector__section-head">
                        <h3 id="db-duty-location-heading">{t('Location')}</h3>
                      </div>
                      <div className="db-inspector-select-wrap">
                        <select
                          className="form__select db-inspector-select"
                          value={selectedLocationId}
                          disabled={!isDayEditable || isBusy || !supportsLocation || locationItems.length === 0}
                          onChange={(e) => {
                            const nextLocationId = e.currentTarget.value || null;
                            void handleSelectRoute(
                              inspectorSlot.id,
                              isPatrolDuty ? nextLocationId : null,
                              isPatrolDuty ? null : nextLocationId,
                            );
                          }}
                          aria-label={t('Duty location')}
                        >
                          {!supportsLocation && <option value="">{selectedSite?.name ?? 'Site-wide duty'}</option>}
                          {supportsLocation && <option value="">{t('Select {kind}', { kind: t(locationKindLabel) })}</option>}
                          {selectedLocationMissing && <option value={selectedLocationId}>{t('Selected {kind}', { kind: t(locationKindLabel) })}</option>}
                          {locationItems.map((item) => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                      </div>
                      {supportsLocation && locationItems.length === 0 && isDayEditable && (
                        <button
                          type="button"
                          className="db-inline-action db-inline-action--full"
                          onClick={() => {
                            setInspectorSlotId(null);
                            if (isPatrolDuty) onNavigateToCreatePatrol?.();
                            else onNavigateToCreatePost?.();
                          }}
                        >
                          <FiPlus aria-hidden="true" />
                          {t('Create {kind}', { kind: t(locationKindLabel) })}
                        </button>
                      )}
                    </section>

                    <div className="db-inspector__divider" />

                    <section className="db-inspector__section" aria-labelledby="db-duty-staffing-heading">
                      <div className="db-staffing-head">
                        <h3 id="db-duty-staffing-heading">{t('Staffing - {assigned}/{required}', { assigned: assignedCount, required: requiredCount })}</h3>
                        <span><FiCoffee aria-hidden="true" /> {t('per-guard breaks')}</span>
                      </div>

                      <div className="db-staff-list">
                        {inspectorSlot.assignments.map((assignment) => {
                          const guardBreak = breakSlots.find((b) => (
                            b.assignments.some((a) => a.guard_id === assignment.guard_id)
                            && overlapsWindow(
                              b.start_minute,
                              b.end_minute,
                              inspectorSlot.start_minute,
                              inspectorSlot.end_minute,
                            )
                          )) ?? null;
                          const isBreakMenuOpen = staffBreakMenuGuardId === assignment.guard_id;
                          const breakSummary = guardBreak
                            ? t('{duration} min {kind} · {start}–{end}', {
                              duration: guardBreak.duration_minutes,
                              kind: guardBreak.is_paid ? t('paid') : t('unpaid'),
                              start: formatMinute(guardBreak.start_minute, displayCoverageWindow),
                              end: formatMinute(guardBreak.end_minute, displayCoverageWindow),
                            })
                            : '';

                          return (
                          <div key={assignment.id} className="db-staff-card">
                            <span
                              className="db-staff-card__avatar"
                              style={assignment.avatar_url ? undefined : getAvatarAccentStyle(assignment.guard_id)}
                            >
                              {assignment.avatar_url
                                ? <img src={assignment.avatar_url} alt={assignment.guard_name} />
                                : initials(assignment.guard_name)}
                            </span>
                            <span className="db-staff-card__body">
                              <strong>{assignment.guard_name}</strong>
                              <small>{guardBreak ? breakSummary : t(assignedGuardSubtitle)}</small>
                            </span>
                            <div className="db-staff-card__break-wrap">
                              <button
                                type="button"
                                className={`db-staff-card__break${guardBreak ? ' db-staff-card__break--on' : ''}`}
                                disabled={isBusy || !isDayEditable}
                                aria-expanded={isBreakMenuOpen}
                                title={guardBreak ? breakSummary : t('Add a break for this guard')}
                                aria-label={guardBreak ? t('Edit break') : t('Add break')}
                                onClick={() => setStaffBreakMenuGuardId((prev) => (
                                  prev === assignment.guard_id ? null : assignment.guard_id
                                ))}
                              >
                                <FiCoffee aria-hidden="true" />
                              </button>

                              {isBreakMenuOpen && (
                                <div className="db-breakmenu" role="menu">
                                  {guardBreak ? (
                                    <>
                                      <span className="db-breakmenu__label">{t('Break')}</span>
                                      <span className="db-breakmenu__summary">{breakSummary}</span>
                                      <button
                                        type="button"
                                        role="menuitem"
                                        className="db-breakmenu__item"
                                        onClick={() => {
                                          setStaffBreakMenuGuardId(null);
                                          setInspectorSlotId(guardBreak.id);
                                        }}
                                      >{t('Edit break')}</button>
                                      <button
                                        type="button"
                                        role="menuitem"
                                        className="db-breakmenu__item db-breakmenu__item--danger"
                                        onClick={() => {
                                          setStaffBreakMenuGuardId(null);
                                          void handleRemoveGuardBreak(guardBreak, assignment.guard_id);
                                        }}
                                      >{t('Remove break')}</button>
                                    </>
                                  ) : (
                                    <>
                                      <span className="db-breakmenu__label">{t('Add break')}</span>
                                      <div className="db-breakmenu__durations">
                                        {[15, 30, 45, 60].map((minutes) => (
                                          <button
                                            key={minutes}
                                            type="button"
                                            role="menuitem"
                                            className="db-breakmenu__chip"
                                            onClick={() => {
                                              setStaffBreakMenuGuardId(null);
                                              void handleCreateGuardBreak(
                                                inspectorSlot,
                                                assignment.guard_id,
                                                minutes,
                                                newBreakIsPaid,
                                              );
                                            }}
                                          >{t('{count}m', { count: minutes })}</button>
                                        ))}
                                      </div>
                                      <label className="db-breakmenu__paid">
                                        <input
                                          type="checkbox"
                                          checked={newBreakIsPaid}
                                          onChange={(e) => setNewBreakIsPaid(e.currentTarget.checked)}
                                        />
                                        {t('Paid break')}
                                      </label>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              className="db-staff-card__remove"
                              disabled={isBusy || !isDayEditable}
                              onClick={() => { void handleUnassignGuard(inspectorSlot.id, assignment.id); }}
                              aria-label={`Remove ${assignment.guard_name}`}
                            >
                              <FiX aria-hidden="true" />
                            </button>
                          </div>
                          );
                        })}

                        {Array.from({ length: shortfall }).map((_, index) => (
                          <div key={`empty-${index}`} className="db-staff-card db-staff-card--empty">
                            <span className="db-staff-card__avatar db-staff-card__avatar--empty">
                              <FiUser aria-hidden="true" />
                            </span>
                            <span className="db-staff-card__body">
                              <strong>{t('Unfilled guard slot')}</strong>
                              <small>{t(requiredLicenceLabel)}</small>
                            </span>
                          </div>
                        ))}
                      </div>

                      {isDayEditable && shortfall > 0 && assignableGuards.length > 0 && (
                        <div className="db-inspector-select-wrap db-staff-add">
                          <select
                            className="form__select db-inspector-select"
                            value=""
                            disabled={isBusy}
                            onChange={(e) => {
                              const guardId = e.currentTarget.value;
                              if (guardId) void handleAssignGuard(inspectorSlot.id, guardId);
                            }}
                            aria-label={t('Assign guard')}
                          >
                            <option value="" disabled>{t('Assign guard')}</option>
                            {inspectorAvailableGuards.map((guard) => {
                              const assignmentState = inspectorGuardAssignmentStateById.get(guard.id);
                              const disabledReason = assignmentState && !assignmentState.canAssign
                                ? assignmentState.reasonText
                                : undefined;
                              return (
                                <option key={guard.id} value={guard.id} disabled={Boolean(disabledReason)}>
                                  {guard.full_name}{disabledReason ? ` - ${disabledReason}` : ''}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      )}

                      {isDayEditable && shortfall > 0 && inspectorAvailableGuards.length > 0 && assignableGuards.length === 0 && (
                        <p className="db-inspector__muted">{t('No eligible guards for this duty.')}</p>
                      )}
                    </section>

                    <div className="db-inspector__divider" />

                    <div className="db-inspector__actions">
                      <button
                        type="button"
                        className="db-danger-btn db-inspector__danger"
                        disabled={isBusy || !isDayEditable}
                        onClick={() => { void handleDeleteDutyRow(slots.filter((s) => s.display_order === inspectorSlot.display_order)); }}
                      >
                        <FiTrash2 aria-hidden="true" />
                        {t('Delete duty')}
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}

            {inspectorBreakSlot && (() => {
              const assignedIds = new Set(inspectorBreakSlot.assignments.map((assignment) => assignment.guard_id));
              const breakAvailableGuards = guards.filter((guard) => !assignedIds.has(guard.id));

              return (
                <>
                  <div className="db-inspector__head">
                    <div className="db-inspector__title-row">
                      <span
                        className="db-inspector__swatch"
                        style={{ background: inspectorBreakSlot.is_paid ? 'var(--db-success)' : 'var(--db-warning)' }}
                      />
                      <span className="db-inspector__title">
                        {inspectorBreakSlot.is_paid ? 'Paid' : 'Unpaid'} Break
                      </span>
                      <button
                        className="db-inspector__close"
                        onClick={() => setInspectorSlotId(null)}
                        aria-label={t('Close inspector')}
                      >
                        <FiX aria-hidden="true" />
                      </button>
                    </div>
                    <div className="db-inspector__tags">
                      <span className="db-tag">{inspectorBreakSlot.duration_minutes} min</span>
                      <span className="db-tag">
                        {formatMinute(inspectorBreakSlot.start_minute, coverageWindow)}-{formatMinute(inspectorBreakSlot.end_minute, coverageWindow)}
                      </span>
                      <span className={`db-tag${inspectorBreakSlot.assignments.length > 0 ? ' db-tag--ok' : ' db-tag--warn'}`}>
                        {t('{count} assigned', { count: inspectorBreakSlot.assignments.length })}
                      </span>
                    </div>
                  </div>

                  <div className="db-inspector__body">
                    <section className="db-inspector__section" aria-labelledby="db-break-time-heading">
                      <div className="db-inspector__section-head">
                        <h3 id="db-break-time-heading">{t('Time')}</h3>
                      </div>
                      <div className="db-time-fields">
                        <div className="db-time-field db-time-field--static">
                          <span>{t('Start')}</span>
                          <strong>{formatMinute(inspectorBreakSlot.start_minute, coverageWindow)}</strong>
                        </div>
                        <div className="db-time-field db-time-field--static">
                          <span>{t('End')}</span>
                          <strong>{formatMinute(inspectorBreakSlot.end_minute, coverageWindow)}</strong>
                        </div>
                      </div>
                    </section>

                    <section className="db-inspector__section" aria-labelledby="db-break-staffing-heading">
                      <div className="db-staffing-head">
                        <h3 id="db-break-staffing-heading">{t('Assigned guards')}</h3>
                        <span><FiCoffee aria-hidden="true" /> {inspectorBreakSlot.duration_minutes} min</span>
                      </div>

                      <div className="db-staff-list">
                        {inspectorBreakSlot.assignments.length === 0 && (
                          <div className="db-staff-card db-staff-card--empty">
                            <span className="db-staff-card__avatar db-staff-card__avatar--empty">
                              <FiUser aria-hidden="true" />
                            </span>
                            <span className="db-staff-card__body">
                              <strong>{t('No guard assigned')}</strong>
                              <small>{t('Assign guards who should take this break.')}</small>
                            </span>
                          </div>
                        )}

                        {inspectorBreakSlot.assignments.map((assignment) => (
                          <div key={assignment.id} className="db-staff-card">
                            <span
                              className="db-staff-card__avatar"
                              style={assignment.avatar_url ? undefined : getAvatarAccentStyle(assignment.guard_id)}
                            >
                              {assignment.avatar_url
                                ? <img src={assignment.avatar_url} alt={assignment.guard_name} />
                                : initials(assignment.guard_name)}
                            </span>
                            <span className="db-staff-card__body">
                              <strong>{assignment.guard_name}</strong>
                              <small>{inspectorBreakSlot.is_paid ? 'Paid break' : 'Unpaid break'}</small>
                            </span>
                            <button
                              type="button"
                              className="db-staff-card__remove"
                              disabled={isBusy || !isDayEditable}
                              onClick={() => { void handleUnassignGuardFromBreak(inspectorBreakSlot.id, assignment.id); }}
                              aria-label={`Remove ${assignment.guard_name}`}
                            >
                              <FiX aria-hidden="true" />
                            </button>
                          </div>
                        ))}
                      </div>

                      {isDayEditable && breakAvailableGuards.length > 0 && (
                        <div className="db-inspector-select-wrap db-staff-add">
                          <select
                            className="form__select db-inspector-select"
                            value=""
                            disabled={isBusy}
                            onChange={(e) => {
                              const guardId = e.currentTarget.value;
                              if (guardId) void handleAssignGuardToBreak(inspectorBreakSlot.id, guardId);
                            }}
                            aria-label={t('Assign guard to break')}
                          >
                            <option value="" disabled>{t('Assign guard')}</option>
                            {breakAvailableGuards.map((guard) => (
                              <option key={guard.id} value={guard.id}>{guard.full_name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </section>

                    <div className="db-inspector__divider" />

                    <div className="db-inspector__actions">
                      <button
                        type="button"
                        className="db-danger-btn db-inspector__danger"
                        disabled={isBusy || !isDayEditable}
                        onClick={() => { void handleDeleteBreak(inspectorBreakSlot.id); }}
                      >
                        <FiTrash2 aria-hidden="true" />
                        {t('Remove break')}
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

      </div>

      {/* Drag ghost */}
      {guardDrag?.activated && (
        <div
          className="db-drag-ghost"
          style={{
            left: guardDrag.clientX + 12,
            top: guardDrag.clientY - 16,
            pointerEvents: 'none',
          }}
          aria-hidden
        >
          {initials(guardDrag.guardName)} {guardDrag.guardName.split(' ')[0]}
        </div>
      )}

      {/* Guard view: dragging a block onto another guard's row reassigns it */}
      {reassignTargetGuardName && slotInteraction && (
        <div
          className="db-drag-ghost"
          style={{
            left: slotInteraction.pointerX + 14,
            top: slotInteraction.pointerY - 16,
            pointerEvents: 'none',
          }}
          aria-hidden
        >
          → {reassignTargetGuardName}
        </div>
      )}

      {editTimesState && editTimesSlot && (
        <EditTimesModal
          slot={editTimesSlot}
          startValue={editTimesState.startValue}
          endValue={editTimesState.endValue}
          error={editTimesState.error}
          isBusy={isBusy}
          onChangeStart={(value) => setEditTimesState((prev) => (prev ? { ...prev, startValue: value, error: '' } : prev))}
          onChangeEnd={(value) => setEditTimesState((prev) => (prev ? { ...prev, endValue: value, error: '' } : prev))}
          onClose={() => setEditTimesState(null)}
          onSave={() => { void handleSaveEditTimes(); }}
        />
      )}

      {copyPromptDate && copyPromptSummary && (
        <CopyDayModal
          dateLabel={formatDateKeyLabel(copyPromptDate)}
          dutyCount={copyPromptSummary.dutyCount}
          blockCount={copyPromptSummary.blockCount}
          assignmentCount={copyPromptSummary.assignmentCount}
          onCopy={handleConfirmCopyDay}
          onClose={() => setCopyPromptDate(null)}
        />
      )}

      {showAddBreakModal && (
        <AddBreakModal
          onSave={(dur, paid) => { void handleSaveNewBreak(dur, paid); }}
          onClose={() => setShowAddBreakModal(false)}
          isBusy={isBusy}
        />
      )}

      {showUnavailableModal && (
        <UnavailableGuardsModal
          guards={filteredUnavailableGuards}
          searchValue={unavailableGuardSearch}
          onSearchChange={setUnavailableGuardSearch}
          onClose={() => setShowUnavailableModal(false)}
        />
      )}

      {publishWarningState && !fixModalDateKey && (
        <RosterWarningsModal
          dateKey={publishWarningState.dateKey}
          violations={publishWarningState.violations}
          isBusy={isBusy}
          onClose={() => setPublishWarningState(null)}
          onOverride={handleOverridePublish}
          onSuggestFixes={() => setFixModalDateKey(publishWarningState.dateKey)}
        />
      )}

      {fixModalDateKey && selectedSite && (
        <ComplianceFixModal
          site={selectedSite}
          dateKey={fixModalDateKey}
          onClose={() => setFixModalDateKey(null)}
          onApplied={(dateKey, remaining) => { void handleFixesApplied(dateKey, remaining); }}
          onPublish={handlePublishAfterFixes}
        />
      )}

      {isAutoScheduleOpen && selectedSite && (
        <AutoScheduleModal
          sites={sites}
          initialSiteIds={[selectedSite.id]}
          anchorDate={selectedDate}
          initialPeriod={viewMode}
          onClose={() => setIsAutoScheduleOpen(false)}
          onApplied={(siteIds, range, result) => {
            void handleAutoScheduleApplied(siteIds, range, result.createdAssignments);
          }}
        />
      )}

      {toast && (
        <div className="db-toast" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </div>
  );
}
